import Konva from 'konva';
import { changeDpiDataUrl } from 'changedpi';
import fontService from '../services/fontService';
import HistoryManager from './HistoryManager';

class KonvaController {
  constructor(container) {
    if (!container) {
      throw new Error('Container element is required');
    }

    this.container = container;
    this.stage = null;
    this.layers = {};
    // History manager for manual Save/Undo/Redo (max 24 states enforced in HistoryManager)
    this.history = new HistoryManager(null);
    this._historyByView = {};
    this._desiredHintVisible = true;
    // Track selected nodes for region sync
    this._selectedNodes = new Set();
    try {
      if (typeof window !== 'undefined' && typeof window.__hintVisible === 'boolean') {
        this._desiredHintVisible = window.__hintVisible;
      }
    } catch (_) {}
  }

  _normalizeImageUrl(url) {
    try {
      if (!url) return url;
      const s = String(url);
      if (
        typeof window !== 'undefined' &&
        window.location &&
        window.location.protocol === 'https:' &&
        s.startsWith('http://')
      ) {
        return 'https://' + s.slice('http://'.length);
      }
      return s;
    } catch {
      return url;
    }
  }

  _debugDumpDesignGroup(tag = '') {
    try {
      if (typeof window === 'undefined' || !window.__debugExportNodes) return;
      const designLayer = this.layers && this.layers['Design'];
      const root = this.design_group;
      if (!designLayer || !root) {
        console.log('[debugDumpDesignGroup] missing designLayer/design_group', { tag, hasDesignLayer: !!designLayer, hasDesignGroup: !!root });
        return;
      }

      const items = [];
      const counts = {
        total: 0,
        byClass: {},
        byName: {},
      };

      const safe = (fn, fallback = undefined) => {
        try { return fn(); } catch (_) { return fallback; }
      };

      const walk = (node, depth) => {
        if (!node) return;
        counts.total += 1;

        const className = safe(() => node.className, 'unknown');
        const name = safe(() => (typeof node.name === 'function' ? node.name() : ''), '');
        const id = safe(() => (typeof node.id === 'function' ? node.id() : ''), '');
        const visible = safe(() => (typeof node.visible === 'function' ? node.visible() : undefined), undefined);
        const listening = safe(() => (typeof node.listening === 'function' ? node.listening() : undefined), undefined);
        const regionAttr = safe(() => node.getAttr && node.getAttr('region'), undefined);

        counts.byClass[className] = (counts.byClass[className] || 0) + 1;
        const nmKey = name || '(no-name)';
        counts.byName[nmKey] = (counts.byName[nmKey] || 0) + 1;

        items.push({
          depth,
          className,
          name,
          id,
          visible,
          listening,
          region: regionAttr,
          hasVisualNode: !!node.visualNode,
          visualNodeName: node.visualNode ? safe(() => (typeof node.visualNode.name === 'function' ? node.visualNode.name() : ''), '') : '',
          visualNodeId: node.visualNode ? safe(() => (typeof node.visualNode.id === 'function' ? node.visualNode.id() : ''), '') : '',
        });

        const children = safe(() => (typeof node.getChildren === 'function' ? node.getChildren() : []), []);
        if (Array.isArray(children) && children.length) {
          children.forEach(ch => walk(ch, depth + 1));
        }
      };

      walk(root, 0);

      const prefix = tag ? `[${tag}]` : '[debugDumpDesignGroup]';
      console.log(prefix, {
        tag: tag || '',
        selectedView: this.getViewId ? this.getViewId(this.selectedView) : this.selectedView,
        counts,
        items
      });
    } catch (e) {
      try { console.warn('[debugDumpDesignGroup] failed', e); } catch (_) {}
    }
  }

  getViewSnapPoints() {
    try {
      const viewId = this.getViewId ? this.getViewId(this.selectedView) : (this.selectedView?.id || this.selectedView || '');
      const views = this.configData?.SupportedModels?.CommonProperties?.Views;
      const viewCfg = Array.isArray(views)
        ? views.find(v => (typeof v === 'object' ? String(v.id || '').toUpperCase() === String(viewId || '').toUpperCase() : String(v || '').toUpperCase() === String(viewId || '').toUpperCase()))
        : null;
      const arr = viewCfg && Array.isArray(viewCfg.center) ? viewCfg.center : [];
      return arr
        .map((c, idx) => {
          const xRaw = c && (c.x != null ? c.x : c.X);
          const yRaw = c && (c.y != null ? c.y : c.Y);
          const x = xRaw != null ? Number(xRaw) : null;
          const y = yRaw != null ? Number(yRaw) : null;
          if (x == null || y == null || !isFinite(x) || !isFinite(y)) return null;
          return { index: idx, x, y };
        })
        .filter(Boolean);
    } catch (_) {
      return [];
    }
  }

  stagePointToProductPx(stageX, stageY) {
    try {
      if (!this._productImageRatio || !this._productImagePosition) return null;
      const px = (Number(stageX) - this._productImagePosition.x) / this._productImageRatio;
      const py = (Number(stageY) - this._productImagePosition.y) / this._productImageRatio;
      if (!isFinite(px) || !isFinite(py)) return null;
      return { x: px, y: py };
    } catch (_) {
      return null;
    }
  }

  productPxToStagePoint(px, py) {
    try {
      if (!this._productImageRatio || !this._productImagePosition) return null;
      const sx = this._productImagePosition.x + (Number(px) * this._productImageRatio);
      const sy = this._productImagePosition.y + (Number(py) * this._productImageRatio);
      if (!isFinite(sx) || !isFinite(sy)) return null;
      return { x: sx, y: sy };
    } catch (_) {
      return null;
    }
  }

  getNodeVisualCenter(node) {
    try {
      if (!node || typeof node.getClientRect !== 'function') return null;
      let r = null;
      try {
        if (this.stage) {
          r = node.getClientRect({ skipShadow: true, skipStroke: true, relativeTo: this.stage });
        }
      } catch (_) {
        r = null;
      }
      if (!r) {
        r = node.getClientRect({ skipShadow: true, skipStroke: true });
      }
      if (!r || !r.width || !r.height) return null;
      return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    } catch (_) {
      return null;
    }
  }

  getNearestSnapForStagePoint(stageX, stageY) {
    try {
      const snaps = this.getViewSnapPoints();
      if (!snaps.length) return null;
      const p = this.stagePointToProductPx(stageX, stageY);
      if (!p) return null;

      let best = null;
      let bestD2 = Infinity;
      for (const s of snaps) {
        const dx = p.x - s.x;
        const dy = p.y - s.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = { index: s.index, snapX: s.x, snapY: s.y, cx: dx, cy: dy };
        }
      }
      return best;
    } catch (_) {
      return null;
    }
  }

  applySnapToNodeIfPresent(node) {
    try {
      if (!node || !node.attrs) return;
      const snapIndex = node.attrs.snapIndex;
      const cx = node.attrs.cx;
      const cy = node.attrs.cy;
      if (snapIndex == null || cx == null || cy == null) return;
      if (!this._productImageRatio || !this._productImagePosition) return;

      const snaps = this.getViewSnapPoints();
      const s = snaps.find(x => x.index === Number(snapIndex));
      if (!s) return;

      const absProductX = Number(s.x) + Number(cx);
      const absProductY = Number(s.y) + Number(cy);
      if (!isFinite(absProductX) || !isFinite(absProductY)) return;
      const stagePt = this.productPxToStagePoint(absProductX, absProductY);
      if (!stagePt) return;

      if (node.className === 'Text') {
        try {
          this.normalizeTextAnchor(node);
        } catch (_) {}
        try {
          node.position({ x: stagePt.x, y: stagePt.y });
        } catch (_) {}
      } else if (node.className === 'Image') {
        // Restore by visual center (handles scale/rotation and avoids drift on view changes)
        try {
          let rect = null;
          try {
            if (this.stage && typeof node.getClientRect === 'function') {
              rect = node.getClientRect({ skipShadow: true, skipStroke: true, relativeTo: this.stage });
            }
          } catch (_) {
            rect = null;
          }
          if (!rect && typeof node.getClientRect === 'function') {
            try {
              rect = node.getClientRect({ skipShadow: true, skipStroke: true });
            } catch (_) {}
          }
          if (rect && rect.width && rect.height) {
            const vcx = rect.x + rect.width / 2;
            const vcy = rect.y + rect.height / 2;
            const dx = stagePt.x - vcx;
            const dy = stagePt.y - vcy;
            const abs = typeof node.getAbsolutePosition === 'function' ? node.getAbsolutePosition() : { x: node.x(), y: node.y() };
            if (typeof node.setAbsolutePosition === 'function') {
              node.setAbsolutePosition({ x: abs.x + dx, y: abs.y + dy });
            } else {
              node.position({ x: (abs.x + dx), y: (abs.y + dy) });
            }
          } else {
            // Fallback: center using unscaled width/height
            const w = typeof node.width === 'function' ? node.width() : (node.attrs.width || 0);
            const h = typeof node.height === 'function' ? node.height() : (node.attrs.height || 0);
            node.position({ x: stagePt.x - (w / 2), y: stagePt.y - (h / 2) });
          }
        } catch (_) {}
      }
    } catch (_) {}
  }

  updateNodeSnapAttrs(node) {
    try {
      if (!node) return;
      const c = this.getNodeVisualCenter(node);
      if (!c) return;
      const productPt = this.stagePointToProductPx(c.x, c.y);
      if (productPt) {
        if (typeof node.setAttrs === 'function') {
          node.setAttrs({ snapPx: productPt.x, snapPy: productPt.y });
        } else {
          node.attrs = node.attrs || {};
          node.attrs.snapPx = productPt.x;
          node.attrs.snapPy = productPt.y;
        }
      }
    } catch (_) {}
  }
  // Helper function to get view ID from view object or string
  getViewId(view) {
    if (!view) return '';
    if (typeof view === 'string') {
      return view; // Old format: simple string
    }
    if (typeof view === 'object' && view.id) {
      return view.id; // New format: object with id
    }
    return String(view); // Fallback: convert to string
  }

  // Helper to get the current design index for a view (defaults to 1)
  // Reads from globals that Header/UI may set: window.designIndexByView[viewId] or window.currentDesignIndex
  getDesignIndexForView(viewId) {
    try {
      if (!viewId) return 1;
      const dibv = (typeof window !== 'undefined' && window.designIndexByView) || null;
      if (dibv && typeof dibv === 'object' && dibv[viewId] != null) {
        const v = Number(dibv[viewId]);
        return isFinite(v) && v > 0 ? v : 1;
      }
      const cur = (typeof window !== 'undefined' && window.currentDesignIndex) || null;
      if (cur != null) {
        const v = Number(cur);
        return isFinite(v) && v > 0 ? v : 1;
      }
    } catch (_) {}
    return 1;
  }

  // Compute the target visual center for placing/centering design nodes
  // Prefer the visual bounds of the product image; fallback to stage center
  getTargetCenter() {
    try {
      if (!this.stage) return { x: 0, y: 0 };
      const designLayer = this.layers ? this.layers['Design'] : null;
      let centerX = this.stage.width() / 2;
      let centerY = this.stage.height() / 2;

      try {
        const viewId = this.getViewId ? this.getViewId(this.selectedView) : (this.selectedView?.id || this.selectedView || '');
        const views = this.configData?.SupportedModels?.CommonProperties?.Views;
        const viewCfg = Array.isArray(views)
          ? views.find(v => (typeof v === 'object' ? String(v.id || '').toUpperCase() === String(viewId || '').toUpperCase() : String(v || '').toUpperCase() === String(viewId || '').toUpperCase()))
          : null;
        const c0 = viewCfg && Array.isArray(viewCfg.center) ? viewCfg.center[0] : null;
        const cxRaw = c0 && (c0.x != null ? c0.x : c0.X);
        const cyRaw = c0 && (c0.y != null ? c0.y : c0.Y);
        const cx = cxRaw != null ? Number(cxRaw) : null;
        const cy = cyRaw != null ? Number(cyRaw) : null;
        if (cx != null && cy != null && isFinite(cx) && isFinite(cy) && this._productImageRatio && this._productImagePosition) {
          centerX = this._productImagePosition.x + (cx * this._productImageRatio);
          centerY = this._productImagePosition.y + (cy * this._productImageRatio);
          return { x: centerX, y: centerY };
        }
      } catch (_) {}

      if (designLayer) {
        const productImage = designLayer.findOne(n => typeof n.name === 'function' && n.name() === 'product-image');
        if (productImage && typeof productImage.getClientRect === 'function') {
          const r = productImage.getClientRect({ skipShadow: true, skipStroke: true });
          if (r && r.width && r.height) {
            centerX = r.x + r.width / 2;
            centerY = r.y + r.height / 2;
          }
        } else if (this._productImagePosition && this._productImageDimensions) {
          centerX = this._productImagePosition.x + (this._productImageDimensions.width / 2);
          centerY = this._productImagePosition.y + (this._productImageDimensions.height / 2);
        }
      }
      return { x: centerX, y: centerY };
    } catch (_) {
      return { x: this.stage ? this.stage.width() / 2 : 0, y: this.stage ? this.stage.height() / 2 : 0 };
    }
  }

  // Ensure a Text node's anchor is centered without changing its visual position
  normalizeTextAnchor(textNode) {
    try {
      if (!textNode || textNode.className !== 'Text') return;
      // Current visual center using current offset
      const width = textNode.width();
      const height = textNode.height();
      const currentX = textNode.x();
      const currentY = textNode.y();
      const currentOffsetX = typeof textNode.offsetX === 'function' ? textNode.offsetX() : 0;
      const currentOffsetY = typeof textNode.offsetY === 'function' ? textNode.offsetY() : 0;

      const visualCenterX = currentX - currentOffsetX + width / 2;
      const visualCenterY = currentY - currentOffsetY + height / 2;

      // Set centered offsets so x/y represent the center
      textNode.offsetX(width / 2);
      textNode.offsetY(height / 2);
      textNode.position({ x: visualCenterX, y: visualCenterY });
    } catch (e) {
      console.warn('normalizeTextAnchor failed:', e);
    }
  }

  /**
   * Resizes the canvas to a specific width while maintaining aspect ratio
   * @param {number} width - The desired width
   * @param {Object} options - Additional options
   * @param {Function} options.onDimensionsChange - Callback when dimensions change
   * @returns {Object} The new dimensions {width, height}
   */
  resizeCanvas(width, options = {}) {
    try {
      if (!this.stage) {
        console.warn('Stage not initialized');
        return null;
      }
      
      // Get current dimensions and calculate aspect ratio
      const currentWidth = this.stage.width();
      const currentHeight = this.stage.height();
      const aspectRatio = currentHeight / currentWidth;
      
      console.log('Current canvas dimensions:', { width: currentWidth, height: currentHeight, aspectRatio });
      
      // Calculate new height based on aspect ratio
      const newWidth = Math.round(width);
      const newHeight = Math.round(newWidth * aspectRatio);
      
      // Skip if size is unchanged
      if (Math.round(currentWidth) === newWidth && Math.round(currentHeight) === newHeight) {
        // No-op: dimensions unchanged
        return { width: currentWidth, height: currentHeight };
      }
      
      console.log('Resizing canvas to:', { width: newWidth, height: newHeight });
      
      // Update stage dimensions
      this.stage.width(newWidth);
      this.stage.height(newHeight);
      this.stage.batchDraw();
      
      // Call the callback if provided
      if (typeof options.onDimensionsChange === 'function') {
        options.onDimensionsChange({ width: newWidth, height: newHeight });
      }
      
      return { width: newWidth, height: newHeight };
    } catch (err) {
      console.error('Error resizing canvas:', err);
      return null;
    }
  }

  /**
   * Get all design objects from the design layer group
   * @returns {Array} Array of Konva objects from the design layer
   */
  getObjects() {
    try {
      if (!this.stage) {
        console.warn('Stage not initialized');
        return [];
      }
      
      // First try to find the design group directly
      let designGroup = this.stage.findOne('#designGroup') || 
                        this.stage.findOne('.designGroup');
      
      // If not found, look for design layer and then find design group within it
      if (!designGroup) {
        const designLayer = this.stage.findOne('.Design') || 
                          this.stage.findOne('#designLayer') || 
                          this.stage.getLayers()[0];
        
        if (designLayer) {
          designGroup = designLayer.findOne('#designGroup') || 
                       designLayer.findOne('.designGroup') || 
                       designLayer;
        }
      }
      
      // If still not found, use the first layer as fallback
      if (!designGroup) {
        console.warn('Design group not found, using first layer');
        designGroup = this.stage.getLayers()[0];
      }
      
      if (!designGroup) {
        console.warn('No design group or layers found');
        return [];
      }
      
      // Get all children from the design group
      const objects = designGroup.getChildren();
      console.log(`Found ${objects.length} objects in design group`);
      
      // Filter out any utility objects if needed
      return objects.filter(obj => {
        // Skip temporary objects, guides, or other utility objects
        if (!obj || !obj.attrs) return false;
        
        const name = obj.attrs.name || '';
        if (name.includes('guide') || name.includes('temp') || name.includes('_helper')) {
          return false;
        }
        
        return true;
      });
    } catch (error) {
      console.error('Error getting design objects:', error);
      return [];
    }
  }

  // --- Minimal snapshot-based history API for Save / Undo / Redo ---
  // Build a JSON snapshot of current design_group (excluding base/variant/hint nodes)
  _buildDesignSnapshot() {
    try {
      if (!this.design_group) return null;
      const designData = this.design_group.toObject();
      const childrenArr = Array.isArray(designData.children) ? designData.children : [];

      designData.children = childrenArr.filter((child) => {
        const attrs = child && child.attrs ? child.attrs : {};
        const id = attrs.id;
        const nm = attrs.name;
        return (
          id !== 'product-image' &&
          nm !== 'product-image' &&
          nm !== 'variant-design-image' &&
          nm !== 'hint-layer'
        );
      });

      const rawNodes = this.design_group.getChildren();
      const nodesArr = typeof rawNodes.toArray === 'function' ? rawNodes.toArray() : Array.from(rawNodes || []);
      const filteredNodes = nodesArr.filter((node) => {
        try {
          const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
          const id = typeof node.id === 'function' ? node.id() : (node.attrs && node.attrs.id);
          return (
            id !== 'product-image' &&
            nm !== 'product-image' &&
            nm !== 'variant-design-image' &&
            nm !== 'hint-layer'
          );
        } catch (_) {
          return true;
        }
      });

      if (Array.isArray(designData.children)) {
        designData.children.forEach((childObj, idx) => {
          const node = filteredNodes[idx];
          if (!node) return;
          childObj.attrs = childObj.attrs || {};
          try {
            childObj.attrs.zIndex = typeof node.zIndex === 'function' ? node.zIndex() : (typeof node.index === 'function' ? node.index() : idx);
          } catch (_) {
            childObj.attrs.zIndex = idx;
          }

          try {
            const c = this.getNodeVisualCenter(node);
            if (c) {
              const snap = this.getNearestSnapForStagePoint(c.x, c.y);
              if (snap) {
                childObj.attrs.snapIndex = snap.index;
                childObj.attrs.cx = snap.cx;
                childObj.attrs.cy = snap.cy;
              }
            }
          } catch (_) {}

          if (node.className === 'Image') {
            try {
              const img = typeof node.image === 'function' ? node.image() : null;
              const nodeImageUrl = node && node.attrs
                ? (node.attrs.imageUrl || node.attrs.src || '')
                : '';
              const src = (img && img.src) ? String(img.src) : '';
              if (src && !src.startsWith('blob:')) {
                childObj.attrs.imageUrl = src;
              } else if (nodeImageUrl && !String(nodeImageUrl).startsWith('blob:')) {
                childObj.attrs.imageUrl = String(nodeImageUrl);
              } else if (src) {
                // Best-effort fallback (may still be blob:)
                childObj.attrs.imageUrl = src;
              }
            } catch (_) {}
          }
        });
      }

      return JSON.stringify(designData);
    } catch (e) {
      console.warn('Failed to build design snapshot:', e);
      return null;
    }
  }

  async _applyDesignSnapshot(snapshotJson) {
    try {
      if (!this.stage) return;
      const designLayer = this.layers && this.layers['Design'];
      if (!designLayer) return;
      if (!this.design_group) {
        this.ensureDesignGroup(designLayer);
      }

      // Remove existing user nodes (preserve base/variant/hint)
      try {
        const children = this.design_group.getChildren().slice();
        children.forEach((child) => {
          try {
            const nm = typeof child.name === 'function' ? child.name() : (child.attrs && child.attrs.name);
            const id = typeof child.id === 'function' ? child.id() : (child.attrs && child.attrs.id);
            if (
              id === 'product-image' ||
              nm === 'product-image' ||
              nm === 'variant-design-image' ||
              nm === 'hint-layer'
            ) return;
            child.destroy();
          } catch (_) {}
        });
      } catch {}

      if (!snapshotJson) {
        designLayer.batchDraw();
        return;
      }

      let designData = null;
      try {
        designData = JSON.parse(snapshotJson);
      } catch (e) {
        try { designData = JSON.parse(JSON.parse(snapshotJson)); } catch (_) {}
      }
      if (!designData || !designData.children) {
        designLayer.batchDraw();
        return;
      }

      const loadPromises = [];
      const createdNodes = [];

      designData.children.forEach((child) => {
        let nodeInstance = null;
        if (child.className === 'Text') {
          const text = new Konva.Text(child.attrs);
          try { this.applySnapToNodeIfPresent(text); } catch (_) {}
          this.design_group.add(text);
          nodeInstance = text;
          try {
            text.on('click tap', (e) => {
              try { if (e) e.cancelBubble = true; } catch (_) {}
              try {
                if (typeof window !== 'undefined') window.selectedKonvaNode = text;
              } catch (_) {}
              try {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('textNodeSelected', { detail: { node: text, text: text.text && typeof text.text === 'function' ? text.text() : undefined } }));
                }
              } catch (_) {}
              try {
                if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'text' } }));
              } catch (_) {}
              try { this.addTransform(text, 'Design'); } catch (_) {}
            });
          } catch (_) {}
          createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
        } else if (child.className === 'Rect') {
          const rect = new Konva.Rect(child.attrs || {});
          this.design_group.add(rect);
          nodeInstance = rect;
          createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
        } else if (child.className === 'Image') {
          let imageUrl = child.attrs && child.attrs.imageUrl ? child.attrs.imageUrl : child.attrs.src;
          if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            const base = window.location.origin;
            imageUrl = new URL(imageUrl, base).href;
          }
          if (imageUrl) {
            const loadPromise = new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.onload = () => {
                const konvaImage = new Konva.Image({
                  ...child.attrs,
                  image: img
                });
                try { this.applySnapToNodeIfPresent(konvaImage); } catch (_) {}
                this.design_group.add(konvaImage);
                nodeInstance = konvaImage;
                try {
                  konvaImage.on('click tap', (e) => {
                    try { if (e) e.cancelBubble = true; } catch (_) {}
                    try {
                      if (typeof window !== 'undefined') window.selectedKonvaNode = konvaImage;
                    } catch (_) {}
                    try {
                      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'image' } }));
                    } catch (_) {}
                    try { this.addTransform(konvaImage, 'Design'); } catch (_) {}
                  });
                } catch (_) {}
                createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
                resolve();
              };
              img.onerror = () => resolve();
              img.src = imageUrl;
            });
            loadPromises.push(loadPromise);
          }
        }
      });

      await Promise.all(loadPromises);

      // Restore stacking order
      if (createdNodes.some(n => n.zIndex !== null && typeof n.zIndex !== 'undefined')) {
        createdNodes
          .filter(n => n.node && typeof n.zIndex === 'number')
          .sort((a, b) => a.zIndex - b.zIndex)
          .forEach((n) => {
            try { n.node.zIndex(n.zIndex); } catch (e) {
              try { n.node.moveTo({ index: n.zIndex }); } catch (_) {}
            }
          });
      }

      // Add transformers immediately, like in addText/addImage
      createdNodes.forEach(({ node }) => {
        if (node) {
          this.addTransform(node, 'Design');
        }
      });

      designLayer.batchDraw();
    } catch (e) {
      console.warn('Failed to apply design snapshot:', e);
    }
  }

  // Save button uses this to push a snapshot into HistoryManager
  setHistoryState(newState) {
    try {
      if (!this.history) {
        this.history = new HistoryManager(null);
      }
      console.log('[KonvaController] setHistoryState called');
      this.history.setState(newState);
    } catch (e) {
      console.warn('setHistoryState failed:', e);
    }
  }

  // Save button uses this to get current snapshot JSON
  getHistoryState() {
    const snap = this._buildDesignSnapshot();
    console.log('[KonvaController] getHistoryState -> snapshot length:', snap ? snap.length : 0);
    return snap;
  }

  async undo() {
    try {
      console.log('[KonvaController] undo() called');
      const snap = this.history ? this.history.undo() : null;
      await this._applyDesignSnapshot(snap);
      return snap;
    } catch (e) {
      console.warn('undo failed:', e);
      return null;
    }
  }

  async redo() {
    try {
      console.log('[KonvaController] redo() called');
      const snap = this.history ? this.history.redo() : null;
      await this._applyDesignSnapshot(snap);
      return snap;
    } catch (e) {
      console.warn('redo failed:', e);
      return null;
    }
  }
  
  /**
   * Wait until the controller has the expected view active and base product image is present
   * @param {string} expectedViewId
   * @param {number} timeoutMs
   */
  async waitForViewReady(expectedViewId, timeoutMs = 3000) {
    const start = Date.now();
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    while (Date.now() - start < timeoutMs) {
      try {
        const activeId = this.getViewId ? this.getViewId(this.selectedView) : (this.selectedView?.id || this.selectedView || '');
        const designLayer = this.layers ? this.layers['Design'] : null;
        const productImage = designLayer ? designLayer.findOne(n => n.name && n.name() === 'product-image') : null;
        const imgObj = productImage ? productImage.image && productImage.image() : null;
        const imgReady = imgObj && imgObj.width && imgObj.height;
        if (activeId === expectedViewId && imgReady) {
          return true;
        }
      } catch (_) {}
      await sleep(100);
    }
    console.warn('waitForViewReady timed out for view:', expectedViewId);
    return false;
  }
  /**
   * Update the z-index order of design objects based on the provided array
   * @param {Array} objects - Array of objects in the desired order (top to bottom)
   */
  updateLayerOrder(objects) {
    if (!this.stage || !objects || !Array.isArray(objects)) {
      console.error('Invalid stage or objects array');
      return;
    }
    
    try {
      const group = this.design_group;
      if (!group) return;
      const children = group.getChildren();
      const fixedNames = new Set(['product-image','background-color','background-image','mask-image','hint-layer']);
      children.forEach((child) => {
        const nm = typeof child.name === 'function' ? child.name() : '';
        if (nm === 'product-image') child.zIndex(0);
      });
      children.forEach((child) => {
        const nm = typeof child.name === 'function' ? child.name() : '';
        if (nm === 'background-color') child.zIndex(1);
      });
      children.forEach((child) => {
        const nm = typeof child.name === 'function' ? child.name() : '';
        if (nm === 'background-image') child.zIndex(2);
      });
      children.forEach((child) => {
        const nm = typeof child.name === 'function' ? child.name() : '';
        if (nm === 'mask-image') child.zIndex(3);
      });
      const orderIds = objects.map(o => String(o.id));
      for (let i = orderIds.length - 1; i >= 0; i--) {
        const id = orderIds[i];
        const child = children.find(c => String(c.id && c.id()) === id);
        if (!child) continue;
        const nm = typeof child.name === 'function' ? child.name() : '';
        if (fixedNames.has(nm)) continue;
        child.moveToTop();
      }

      
      // Force redraw of the stage
      this.stage.batchDraw();
      this.stage.draw();
      console.log('✅ Layer order updated successfully');
    } catch (error) {
      console.error('❌ Error updating layer order:', error);
    }
  }

  /**
   * Initialize the Konva stage with configurable options
   * @param {Object} config - Configuration object
   * @param {number} config.width - Stage width in pixels
   * @param {number} config.height - Stage height in pixels
   * @param {string} [config.backgroundColor='transparent'] - Background color (CSS color)
   * @param {boolean} [config.preserveAspectRatio=true] - Whether to preserve aspect ratio on resize
   * @param {Array<Object>} [config.layers] - Layer configurations
   * @returns {Konva.Stage} The initialized Konva stage
   */
  init(config) {
    try {
      // Validate config
      if (!config || typeof config !== 'object') {
        throw new Error('Configuration object is required');
      }

      const {
        width,
        height,
        backgroundColor = 'transparent',
        preserveAspectRatio = true,
        layers: customLayers
      } = config;

      // Validate dimensions
      if (typeof width !== 'number' || typeof height !== 'number' ||
        width <= 0 || height <= 0) {
        throw new Error('Invalid dimensions: width and height must be positive numbers');
      }

      // Validate container
      if (!(this.container instanceof HTMLElement)) {
        throw new Error('Container must be a valid DOM element');
      }

      // Clear existing stage
      if (this.stage) {
        this.stage.destroy();
        this.layers = {};
      }

      // Create new stage with exact pixel dimensions
      this.stage = new Konva.Stage({
        container: this.container,
        width: Math.floor(width),
        height: Math.floor(height),
      });

      // Set container styles
      Object.assign(this.container.style, {
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor,
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none'
      });

      // Create default layers if none provided
      const defaultLayers = [
        {
          name: 'Product',
          listening: false,
          opacity: 1,
          visible: true,
          priority: 0
        },
        {
          name: 'Design',
          listening: true,
          opacity: 1,
          visible: true,
          priority: 1
        },
        {
          name: 'Overlay',
          listening: false,
          opacity: 1,
          visible: true,
          priority: 2
        }
      ];

      // Use custom layers if provided, otherwise use defaults
      const layerConfigs = customLayers && customLayers.length > 0
        ? customLayers
        : defaultLayers;

      // Sort layers by priority
      layerConfigs.sort((a, b) => (a.priority || 0) - (b.priority || 0));

      // Reset layers object
      this.layers = {};

      // Create design group that will contain our design elements
      this.design_group = new Konva.Group({
        name: 'design-group',
        id: 'design-group',
        draggable: false,
        listening: true
      });

      // Create layers
      layerConfigs.forEach(layerDef => {
        const newLayer = new Konva.Layer({
          listening: layerDef.listening,
          perfectDrawEnabled: false,
          opacity: layerDef.opacity,
          visible: layerDef.visible !== false,
          clipX: 0,
          clipY: 0,
          clipWidth: width,
          clipHeight: height
        });

        // Store the layer configuration for later reference
        newLayer.config = {
          name: layerDef.name,
          ...layerDef
        };

        this.layers[layerDef.name] = newLayer;
        this.stage.add(newLayer);

        // Add design_group to the Design layer
        if (layerDef.name === 'Design' && this.design_group) {
          newLayer.add(this.design_group);
        }
      });

      // Setup event listeners
      this.setupEventListeners();

      // Setup resize handler if needed
      if (preserveAspectRatio) {
        this.setupResizeHandler(width / height);
      }

      // Force initial draw
      this.stage.batchDraw();
      return this.stage;

    } catch (error) {
      console.error('Failed to initialize Konva stage:', error);
      // Clean up partially created stage on error
      if (this.stage) {
        this.stage.destroy();
        this.stage = null;
      }
      this.layers = {};
      throw error;
    }
  }

  /**
   * Set up event listeners for the stage
   * @private
   */
  setupEventListeners() {
    if (!this.stage) return;

    // Handle stage clicks
    this.stage.on('click tap', (e) => {
      const clickedNode = e.target;

      // If clicking on stage background
      if (clickedNode === this.stage) {
        // Remove all transformers and delete buttons
        const designLayer = this.layers['Design'];
        if (designLayer) {
          designLayer.find('Transformer').forEach(t => t.destroy());
          designLayer.find('.delete-button-group').forEach(t => t.destroy());
          designLayer.batchDraw();
        }
        // Clear global selection and notify UI
        try {
          window.selectedKonvaNode = null;
          window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: null } }));
        } catch {}
        return;
      }

      // Log the clicked object for debugging
      console.debug('Clicked on:', {
        type: clickedNode.className,
        id: clickedNode.id(),
        name: clickedNode.name(),
        attrs: clickedNode.attrs,
        position: { x: clickedNode.x(), y: clickedNode.y() }
      });
    });

    // Listen for global centering events
    const onCenterX = () => this.centerSelected('x');
    const onCenterY = () => this.centerSelected('y');
    window.addEventListener('CENTER_X', onCenterX);
    window.addEventListener('CENTER_Y', onCenterY);

    // Store cleanup so we can remove listeners if needed later
    this._cleanupCenteringListeners = () => {
      window.removeEventListener('CENTER_X', onCenterX);
      window.removeEventListener('CENTER_Y', onCenterY);
    };
  }

  /**
   * Set up window resize handler to maintain aspect ratio
   * @param {number} targetAspect - Target width/height ratio
   * @private
   */
  setupResizeHandler(targetAspect) {
    if (!this.stage || !this.container) return;

    const updateStageSize = () => {
      if (!this.stage) return;

      const container = this.container.parentElement || this.container;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      let newWidth = containerWidth;
      let newHeight = containerHeight;

      // Calculate dimensions to maintain aspect ratio
      const containerAspect = containerWidth / containerHeight;

      if (containerAspect > targetAspect) {
        // Container is wider than target aspect ratio
        newWidth = containerHeight * targetAspect;
      } else {
        // Container is taller than target aspect ratio
        newHeight = containerWidth / targetAspect;
      }

      // Round for stable comparisons
      newWidth = Math.round(newWidth);
      newHeight = Math.round(newHeight);

      // Compute desired centered position
      const desiredX = (containerWidth - newWidth) / 2;
      const desiredY = (containerHeight - newHeight) / 2;

      const curW = Math.round(this.stage.width());
      const curH = Math.round(this.stage.height());
      const curPos = this.stage.position();

      // If nothing changes, skip updates
      if (curW === newWidth && curH === newHeight &&
          Math.round(curPos.x) === Math.round(desiredX) &&
          Math.round(curPos.y) === Math.round(desiredY)) {
        return;
      }

      // Update stage size
      this.stage.width(newWidth);
      this.stage.height(newHeight);
      this.stage.scale({ x: 1, y: 1 });

      // Center the stage
      this.stage.position({ x: desiredX, y: desiredY });

      // Update layer clipping
      Object.values(this.layers).forEach(layer => {
        layer.clipWidth(newWidth);
        layer.clipHeight(newHeight);
      });

      this.stage.batchDraw();

      // After resize, reposition user nodes relative to product snap points
      if (this.design_group) {
        this.design_group.getChildren().forEach(node => {
          try {
            const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
            if (nm !== 'product-image' && nm !== 'variant-design-image' && nm !== 'hint-layer' && nm !== 'background-color' && nm !== 'background-image') {
              this.applySnapToNodeIfPresent(node);
            }
          } catch (_) {}
        });
      }
    };

    // Initial update
    updateStageSize();

    // Add resize listener with debounce
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(updateStageSize, 100);
    };

    window.addEventListener('resize', handleResize);

    // Store cleanup function
    this._cleanupResizeHandler = () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }

  /**
   * Clean up resize handler when controller is destroyed
   * @private
   */
  _cleanupResizeHandler() {
    // Will be overridden by setupResizeHandler
  }

  // Add a text object with transformer
  async addText(text, x, y, styles = {}) {
    try {
      if (!this.stage) {
        console.error('Stage not initialized');
        return null;
      }

      const layer = this.layers['Design'];
      if (!layer) {
        console.error('Design layer not found');
        return null;
      }

      // Make sure design group is in the layer and has an ID
      if (!this.design_group.id()) {
        this.design_group.id('design-group');
      }
      if (!layer.find('#design-group')[0]) {
        layer.add(this.design_group);
      }
      
      // Get the font family from styles or use default
      const fontFamilyToUse = styles.fontFamily || 'Arial';
      
      // Check if the font is loaded and load it on demand if needed
      if (fontFamilyToUse !== 'Arial' && fontService) {
        try {
          
          const isFontLoaded = fontService.isFontLoaded(fontFamilyToUse);
          
          if (!isFontLoaded) {
            
            // Show a loading indicator or use a fallback font temporarily
            await fontService.loadFontOnDemand(fontFamilyToUse);
            
          } else {
            
          }
        } catch (fontError) {
          console.error(`Error loading font '${fontFamilyToUse}':`, fontError);
          // Continue with fallback font
        }
      }

      // Create text object with styles first to measure it
      const textObj = new Konva.Text({
        text: text,
        fontSize: styles.fontSize || 32,
        fontFamily: fontFamilyToUse,
        fontStyle: styles.fontWeight === 'bold' ? 'bold' : 'normal',
        align: 'center',
        padding: 0,
      });

      // Calculate text dimensions
      const textWidth = textObj.width();
      const textHeight = textObj.height();

      // IMPORTANT: center the anchor BEFORE positioning so x/y are visual center
      textObj.offsetX(textWidth / 2);
      textObj.offsetY(textHeight / 2);

      // Determine desired center position (treat x/y as center if provided)
      const target = this.getTargetCenter();
      const hasProductArea = !!(this._productImagePosition && this._productImageDimensions) ||
                             !!(this.layers && this.layers['Design'] && this.layers['Design'].findOne(n => typeof n.name === 'function' && n.name() === 'product-image'));
      const centerX = (x !== undefined) ? x : target.x;
      const centerY = (y !== undefined) ? y : target.y;

      // Update text object with position (center-based) and remaining styles
      textObj.setAttrs({
        x: centerX,
        y: centerY,
        fontSize: styles.fontSize || 32,
        fontFamily: fontFamilyToUse,
        fontStyle: styles.fontWeight === 'bold' ? 'bold' : 'normal',
        fill: styles.color || '#000000',
        draggable: true,
        listening: true,
        perfectDrawEnabled: true,
        name: 'text-node',
        id: 'text-' + Date.now()
      });

      // Recalculate size after attributes applied and correct anchor immediately
      try {
        const w2 = textObj.width();
        const h2 = textObj.height();
        textObj.offsetX(w2 / 2);
        textObj.offsetY(h2 / 2);
      } catch (_) {}

      // Add to design group
      this.design_group.add(textObj);

      // Move the text to top within the design group
      textObj.moveToTop();

      // Final visual centering using bounding box (absolute coords safe for transformed parents)
      try {
        const rect = textObj.getClientRect({ skipShadow: true, skipStroke: true });
        const visualCenterX = rect.x + rect.width / 2;
        const visualCenterY = rect.y + rect.height / 2;
        const dx = centerX - visualCenterX;
        const dy = centerY - visualCenterY;
        const abs = textObj.getAbsolutePosition();
        textObj.setAbsolutePosition({ x: abs.x + dx, y: abs.y + dy });
      } catch (_) {}

      // Post-render re-center: handle any metric changes after draw (e.g., font load/render)
      try {
        const layerRef = layer;
        requestAnimationFrame(() => {
          try {
            // Keep anchor centered and re-align by visual bounds
            this.normalizeTextAnchor(textObj);
            const rect2 = textObj.getClientRect({ skipShadow: true, skipStroke: true });
            const vcx2 = rect2.x + rect2.width / 2;
            const vcy2 = rect2.y + rect2.height / 2;
            const dx2 = centerX - vcx2;
            const dy2 = centerY - vcy2;
            const abs2 = textObj.getAbsolutePosition();
            textObj.setAbsolutePosition({ x: abs2.x + dx2, y: abs2.y + dy2 });
            if (layerRef) layerRef.batchDraw();
          } catch {}
        });
      } catch {}

      // If product image area was not ready at creation time, try to center again shortly after
      try {
        if (!hasProductArea) {
          setTimeout(() => {
            try {
              if (this._productImagePosition && this._productImageDimensions) {
                const px = this._productImagePosition.x + (this._productImageDimensions.width / 2);
                const py = this._productImagePosition.y + (this._productImageDimensions.height / 2);
                const rect3 = textObj.getClientRect({ skipShadow: true, skipStroke: true });
                const vcx3 = rect3.x + rect3.width / 2;
                const vcy3 = rect3.y + rect3.height / 2;
                const dx3 = px - vcx3;
                const dy3 = py - vcy3;
                const abs3 = textObj.getAbsolutePosition();
                textObj.setAbsolutePosition({ x: abs3.x + dx3, y: abs3.y + dy3 });
                layer.batchDraw();
              }
            } catch {}
          }, 200);
        }
      } catch {}

      // Generic safety re-center shortly after creation to catch any late metric changes
      try {
        setTimeout(() => {
          try {
            this.normalizeTextAnchor(textObj);
            const rect4 = textObj.getClientRect({ skipShadow: true, skipStroke: true });
            const vcx4 = rect4.x + rect4.width / 2;
            const vcy4 = rect4.y + rect4.height / 2;
            const dx4 = centerX - vcx4;
            const dy4 = centerY - vcy4;
            const abs4 = textObj.getAbsolutePosition();
            textObj.setAbsolutePosition({ x: abs4.x + dx4, y: abs4.y + dy4 });
            layer.batchDraw();
          } catch {}
        }, 300);
      } catch {}

      // Add click handler to select the text and show transformer
      textObj.on('click tap', (e) => {
        console.log('KonvaController: Text node clicked:', textObj.text());
        console.log('KonvaController: Text node type:', textObj.className);
        
        e.cancelBubble = true; // Prevent event from reaching stage
        
        // Set global reference directly here as well
        window.selectedKonvaNode = textObj;
        
        // If this is a text node, dispatch a custom event for TextTab to listen to
        if (textObj.className === 'Text') {
          console.log('KonvaController: Text node selected in addTransform, dispatching textNodeSelected event');
          console.log('KonvaController: Text node properties:', {
            text: textObj.text(),
            fontFamily: textObj.fontFamily(),
            fontSize: textObj.fontSize(),
            fill: textObj.fill(),
            id: textObj.id()
          });
          
          // Set the global reference explicitly
          window.selectedKonvaNode = textObj;
          console.log('KonvaController: Set window.selectedKonvaNode =', window.selectedKonvaNode);
          
          // Use setTimeout to ensure the event is dispatched after the current execution context
          setTimeout(() => {
            try {
              console.log('KonvaController: Dispatching textNodeSelected event with setTimeout');
              const event = new CustomEvent('textNodeSelected', { 
                detail: { node: textObj, text: textObj.text() } 
              });
              window.dispatchEvent(event);
              // Also broadcast generic selection type for toolbar logic
              window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'text' } }));
              console.log('KonvaController: Event dispatched successfully');
            } catch (error) {
              console.error('KonvaController: Error dispatching textNodeSelected event:', error);
            }
          }, 0);
        } else {
          console.log('KonvaController: Non-text node selected:', textObj.className);
          // Still notify selection type when applicable
          try { window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'text' } })); } catch {}
        }
        
        // Call addTransform to add the transformer
        this.addTransform(textObj, 'Design');
      });

      // Add transform controls immediately
      this.addTransform(textObj, 'Design');

      try { this.updateNodeSnapAttrs(textObj); } catch (_) {}

      // Force update and redraw
      layer.batchDraw();

      return textObj;
    } catch (error) {
      console.error('Error adding text:', error);
      return null;
    }
  }


  // Helper to ensure a composite group exists for a region (upper/middle/lower)
  _ensureRegionGroup(region) {
    if (!this.design_group) return null;
    const groupName = `region-group-${region}`;
    let group = this.design_group.findOne(`.${groupName}`);
    
    if (!group) {
      group = new Konva.Group({
        name: groupName,
        listening: false // Visual only group
      });
      
      // Set the region attribute so export can find it
      group.setAttr('region', [region]);
      
      this.design_group.add(group);
      
      // Get current view config to check for ClipRegions
      const viewId = this.getViewId(this.selectedView);
      const views = this.configData?.SupportedModels?.CommonProperties?.Views;
      const viewCfg = Array.isArray(views) 
          ? views.find(v => v.id === viewId) 
          : null;
      
      const clipRegions = viewCfg?.ClipRegions;
      
      // Compute product->stage transform from the current product-image node when available.
      // This avoids stale _productImageRatio/_productImagePosition values after exports/view switches.
      let ratio = this._productImageRatio || 1;
      let pos = this._productImagePosition || { x: 0, y: 0 };
      try {
        const designLayer = this.layers && this.layers['Design'];
        const pImg = designLayer ? designLayer.findOne(n => n.name && n.name() === 'product-image') : null;
        const imgEl = pImg && typeof pImg.image === 'function' ? pImg.image() : null;
        const naturalH = imgEl && imgEl.height ? imgEl.height : null;
        const renderedH = pImg && typeof pImg.height === 'function'
          ? (pImg.height() * (typeof pImg.scaleY === 'function' ? pImg.scaleY() : 1))
          : null;
        if (naturalH && renderedH) {
          ratio = renderedH / naturalH;
          pos = {
            x: (typeof pImg.x === 'function') ? pImg.x() : 0,
            y: (typeof pImg.y === 'function') ? pImg.y() : 0
          };
        }
      } catch (_) {}
      
      if (clipRegions && clipRegions[region]) {
          // Use configured product-space boundaries from view config
          // Format: object with range [start, end]
          const regionConfig = clipRegions[region];
          const range = Array.isArray(regionConfig) ? regionConfig : regionConfig.range;
          
          if (range && range.length === 2) {
            const [start, end] = range;
            
            const stageStart = (start * ratio) + pos.y;
            const stageEnd = (end * ratio) + pos.y;
            
            group.clipFunc((ctx) => {
                // Draw rectangle for clipping
                // Note: ctx.rect(x, y, w, h)
                // We use massive width (-10000 to 10000) to cover full width
                ctx.rect(-10000, stageStart, 20000, stageEnd - stageStart);
            });
          }
      }
    }
    return group;
  }

  // Helper to draw visual guidelines for regions
  // _ensureRegionGuidelines() {
     // Removed
  // }

  // Add an image to the design group with transform controls
  addImage(imageUrl, options = {}) {
    if (!this.stage || !imageUrl) {
      console.error('Stage not initialized or invalid image URL');
      return Promise.resolve(null);
    }

    const normalizedImageUrl = this._normalizeImageUrl(imageUrl);

    // Stage dimensions are managed elsewhere, no need to store them here

    
    const layer = this.layers['Design'];
    if (!layer) {
      console.error('Design layer not found');
      return Promise.resolve(null);
    }
    
    // Make sure design group is in the layer and has an ID
    if (!this.design_group.id()) {
      this.design_group.id('design-group');
    }
    if (!layer.find('#design-group')[0]) {
      layer.add(this.design_group);
    }

    // Create a new image object
    const img = new window.Image();
    try {
      const corsSetting = Object.prototype.hasOwnProperty.call(options || {}, 'imageCrossOrigin')
        ? options.imageCrossOrigin
        : 'Anonymous';
      if (corsSetting !== false && corsSetting !== null && corsSetting !== undefined) {
        img.crossOrigin = String(corsSetting);
      }
    } catch {}

    return new Promise((resolve) => {
      img.onerror = (e) => {
        console.error('Error loading image:', e);
        resolve(null);
      };
      img.onload = () => {
        try {
          if (typeof window !== 'undefined' && window.__debugAddImageNodes) {
            this._debugDumpDesignGroup('addImage(before)');
            console.log('[addImage] begin', { imageUrl, options });
          }

          // Calculate dimensions to fit the stage while maintaining aspect ratio
          const stageHeight = this.stage.height();
          const stageWidth = this.stage.width();

          // Calculate dimensions
          let width;
          let height;
          if (options && options.fitToStage) {
            const maxW = stageWidth * 0.9;
            const maxH = stageHeight * 0.9;
            const containScale = Math.min(maxW / img.width, maxH / img.height);
            width = img.width * containScale;
            height = img.height * containScale;
          } else {
            // Default behavior: apply provided scale or default to 0.8
            const scale = options.scaleX || 0.8;
            width = img.width * scale;
            height = img.height * scale;
          }

          // Calculate center position, adjusting for image dimensions
          let target = this.getTargetCenter ? this.getTargetCenter() : { x: stageWidth / 2, y: stageHeight / 2 };
          
          // Adjust target for regions if options.region is present AND valid for this view
          const viewId = this.getViewId(this.selectedView);
          const views = this.configData?.SupportedModels?.CommonProperties?.Views;
          const viewCfg = Array.isArray(views) ? views.find(v => v.id === viewId) : null;
          const clipRegions = viewCfg?.ClipRegions;

          // Compute product->stage transform from the current product-image node when available.
          // This avoids stale _productImageRatio/_productImagePosition values after exports/view switches.
          const computeStageTransform = () => {
            try {
              const designLayer = this.layers && this.layers['Design'];
              const pImg = designLayer ? designLayer.findOne(n => n.name && n.name() === 'product-image') : null;
              const imgEl = pImg && typeof pImg.image === 'function' ? pImg.image() : null;
              const naturalH = imgEl && imgEl.height ? imgEl.height : null;
              const renderedH = pImg && typeof pImg.height === 'function'
                ? (pImg.height() * (typeof pImg.scaleY === 'function' ? pImg.scaleY() : 1))
                : null;
              if (naturalH && renderedH) {
                return {
                  ratio: renderedH / naturalH,
                  pos: {
                    x: (typeof pImg.x === 'function') ? pImg.x() : 0,
                    y: (typeof pImg.y === 'function') ? pImg.y() : 0
                  }
                };
              }
            } catch (_) {}
            return {
              ratio: this._productImageRatio || 1,
              pos: this._productImagePosition || { x: 0, y: 0 }
            };
          };

          const { ratio: productToStageRatio, pos: productToStagePos } = computeStageTransform();

          // Normalize region: support multiple regions for clipping
          // If ClipRegions is missing (or region key invalid), do not apply any clipping.
          let normalizedRegions = [];
          if (typeof options.region === 'string') {
            normalizedRegions = [options.region];
          } else if (Array.isArray(options.region)) {
            normalizedRegions = options.region.filter(r => typeof r === 'string');
          }
          if (normalizedRegions.length === 0 && Array.isArray(this._defaultRegionsForNextAdd) && this._defaultRegionsForNextAdd.length > 0) {
            normalizedRegions = this._defaultRegionsForNextAdd.slice();
          }

          // Calculate position
          const posX = options.x !== undefined ? options.x : (target.x - (width / 2));
          const posY = options.y !== undefined ? options.y : (target.y - (height / 2));

          // Remove internal options from Konva.Image attrs
          const { scaleX, scaleY, region, fitToStage, imageCrossOrigin, ...restOptions } = options;

          // Create the image object
          const imageObj = new Konva.Image({
            image: img,
            x: posX,
            y: posY,
            width: width,
            height: height,
            draggable: true,
            listening: true,
            name: 'uploaded-image',
            id: 'img-' + Date.now(),
            // Ensure the image is selectable
            hitStrokeWidth: 10,
            hitStroke: 'transparent',
            ...restOptions
          });

          // Store regions on the node for sync later
          console.log('[addImage] Regions on upload:', { normalizedRegions });
          if (normalizedRegions.length > 0) {
            try { 
              imageObj.setAttr('region', normalizedRegions);
              console.log('[addImage] Set regions on upload:', {
                nodeId: imageObj.id && imageObj.id(),
                regions: normalizedRegions,
                actualRegions: imageObj.getAttr('region')
              });
            } catch (_) {}
          } else {
            console.log('[addImage] No regions selected, showing whole image');
          }

          // Check if we have valid clip regions
          const hasValidClipRegions = normalizedRegions.length > 0 && clipRegions && 
            normalizedRegions.some(region => clipRegions[region]);

          if (hasValidClipRegions) {
            console.log('[addImage] Creating visual nodes for regions:', normalizedRegions);
            // Create visual nodes for each valid region
            const createdPairs = [];
            normalizedRegions.forEach(regionKey => {
              console.log('[addImage] Processing region:', regionKey);
              if (!clipRegions[regionKey]) {
                console.log('[addImage] Region not found in clipRegions:', regionKey);
                return;
              }
              
              const regionConfig = clipRegions[regionKey];
              const range = Array.isArray(regionConfig) ? regionConfig : regionConfig.range;
              
              if (!range || range.length !== 2) return;
              
              let [start, end] = range;
              const ratio = productToStageRatio || 1;
              const pos = productToStagePos || { x: 0, y: 0 };

              // Convert product-space range to stage space
              const startY = start * ratio + pos.y;
              const endY = (end === -1 ? this.stage.height() : end * ratio) + pos.y;

              // 1. Create the Visual Node (Clipped)
              // Clone the imageObj but make it non-interactive
              const visualNode = imageObj.clone({
                  listening: false,
                  draggable: false,
                  name: 'sticker-visual',
                  id: imageObj.id && imageObj.id() ? `${imageObj.id()}-visual-${regionKey}` : `visual-${Date.now()}-${regionKey}`,
              });
              
              // Remove any sceneFunc from visual node to show the actual image
              visualNode.sceneFunc(null);
              delete visualNode.sceneFunc;
              visualNode.visible(true);
              
              // Add Visual Node to the Clipped Region Group
              const regionGroup = this._ensureRegionGroup(regionKey);
              if (regionGroup) {
                regionGroup.add(visualNode);
                regionGroup.visible(true);
                createdPairs.push({ visual: visualNode, group: regionGroup });
                console.log('[addImage] Added visual node to region group:', {
                  region: regionKey,
                  groupName: regionGroup.name && regionGroup.name(),
                  visualNodeId: visualNode.id && visualNode.id()
                });
              } else {
                console.log('[addImage] ERROR: Failed to create region group for', regionKey);
              }

              // 2. Configure the Control Node (imageObj)
              // It stays in design_group (unclipped) but is invisible (sceneFunc empty)
              // It handles all interaction (clicks, drags)

              // IMPORTANT: Use empty sceneFunc for visual invisibility but keep interaction
              imageObj.sceneFunc((ctx, shape) => {
                  // Draw nothing - visually invisible
              });
              
              // Explicit hit function for robust interaction
              imageObj.hitFunc((ctx, shape) => {
                  ctx.beginPath();
                  ctx.rect(0, 0, shape.width(), shape.height());
                  ctx.closePath();
                  ctx.fillStrokeShape(shape);
              });
              
              // Add Control Node to design_group
              this.design_group.add(imageObj);
              imageObj.moveToTop();
              
              try {
                if (typeof window !== 'undefined' && window.__debugAddImageNodes) {
                  console.log('[addImage] created(region)', {
                    region: regionKey,
                    control: { name: imageObj.name && imageObj.name(), id: imageObj.id && imageObj.id(), visible: imageObj.visible && imageObj.visible(), listening: imageObj.listening && imageObj.listening() },
                    visual: { name: visualNode.name && visualNode.name(), id: visualNode.id && visualNode.id(), visible: visualNode.visible && visualNode.visible(), listening: visualNode.listening && visualNode.listening() },
                    regionGroup: { name: regionGroup.name && regionGroup.name(), id: regionGroup.id && regionGroup.id(), visible: regionGroup.visible && regionGroup.visible(), listening: regionGroup.listening && regionGroup.listening() }
                  });
                }
              } catch (_) {}
            });
            
            imageObj.visualGroups = createdPairs;
            const syncVisual = () => {
              if (Array.isArray(imageObj.visualGroups)) {
                imageObj.visualGroups.forEach(({ visual }) => {
                  if (!visual || typeof visual.getLayer !== 'function' || !visual.getLayer()) return;
                  visual.setAttrs({
                    x: imageObj.x(),
                    y: imageObj.y(),
                    scaleX: imageObj.scaleX(),
                    scaleY: imageObj.scaleY(),
                    rotation: imageObj.rotation(),
                    opacity: imageObj.opacity(),
                    skewX: imageObj.skewX(),
                    skewY: imageObj.skewY()
                  });
                  const layer = visual.getLayer();
                  if (layer && typeof layer.batchDraw === 'function') layer.batchDraw();
                });
              }
            };
            imageObj.syncVisual = syncVisual;
            imageObj.on('dragmove transform', syncVisual);
            const originalOpacity = imageObj.opacity.bind(imageObj);
            imageObj.opacity = function(val) {
              if (val !== undefined) {
                if (Array.isArray(this.visualGroups)) {
                  this.visualGroups.forEach(({ visual }) => {
                    if (visual && typeof visual.opacity === 'function') visual.opacity(val);
                  });
                }
                return originalOpacity(val);
              }
              return originalOpacity();
            };
            syncVisual();
          } else {
            // No regions selected - show whole image
            this.design_group.add(imageObj);
            imageObj.moveToTop();
          }

          try {
            if (typeof window !== 'undefined' && window.__debugAddImageNodes) {
              console.log('[addImage] created', {
                regions: normalizedRegions,
                control: { name: imageObj.name && imageObj.name(), id: imageObj.id && imageObj.id(), visible: imageObj.visible && imageObj.visible(), listening: imageObj.listening && imageObj.listening() }
              });
            }
          } catch (_) {}

          imageObj.on('click tap', (e) => {
            e.cancelBubble = true;
            try {
              window.selectedKonvaNode = imageObj;
              window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'image' } }));
            } catch {}
            this.addTransform(imageObj, 'Design');
          });

          // Optional cursor feedback
          imageObj.on('mouseenter', () => { document.body.style.cursor = 'pointer'; });
          imageObj.on('mouseleave', () => { document.body.style.cursor = 'default'; });

          try { this.updateNodeSnapAttrs(imageObj); } catch (_) {}
          layer.batchDraw();

          try {
            if (typeof window !== 'undefined' && window.__debugAddImageNodes) {
              this._debugDumpDesignGroup('addImage(after)');
            }
          } catch (_) {}
          
          resolve(imageObj);
        } catch (error) {
          console.error('Error creating image object:', error);
          resolve(null);
        }
      };

      img.onerror = (error) => {
        console.error('Error loading image:', error);
        resolve(null);
      };

      // Set src after setting up event handlers
      img.src = normalizedImageUrl;
    });
  }

  setOnClick(node, layerName = 'Design') {
    const layer = this.layers[layerName];
    if (!layer) return;

    node.on('click', () => {
      //remove existing transformers
      layer.find('Transformer').forEach(t => t.destroy());
      layer.batchDraw();
      this.addTransform(node, layerName);
    });
  }
  // Create a delete button for a node
  createDeleteButton(node, layer, transformer) {
    // Create delete button group
    if (!layer) return;
    
    // Remove existing delete button if it exists
    if (node.deleteButtonGroup) {
      node.deleteButtonGroup.destroy();
    }
    
    const deleteButtonGroup = new Konva.Group({
      x: 0,
      y: 0,
      width: 24,
      height: 24,
      visible: true,
      draggable: false,
      name: 'delete-button-group'
    });
    
    // Store reference to delete button group on the node
    node.deleteButtonGroup = deleteButtonGroup;

    // Add red circle background
    const deleteButtonBg = new Konva.Circle({
      x: 12,
      y: 12,
      radius: 10,
      fill: '#ff4d4f',
      stroke: '#fff',
      strokeWidth: 2,
      shadowColor: 'black',
      shadowBlur: 4,
      shadowOffset: { x: 0, y: 1 },
      shadowOpacity: 0.3
    });

    // Add white X mark
    const deleteButtonX1 = new Konva.Line({
      points: [8, 8, 16, 16],
      stroke: '#fff',
      strokeWidth: 2,
      lineCap: 'round',
      lineJoin: 'round'
    });

    const deleteButtonX2 = new Konva.Line({
      points: [16, 8, 8, 16],
      stroke: '#fff',
      strokeWidth: 2,
      lineCap: 'round',
      lineJoin: 'round'
    });

    // Add elements to the delete button group
    deleteButtonGroup.add(deleteButtonBg);
    deleteButtonGroup.add(deleteButtonX1);
    deleteButtonGroup.add(deleteButtonX2);

    // Add hover effect with move cursor hint
    deleteButtonGroup.on('mouseenter', function () {
      document.body.style.cursor = 'move';
      deleteButtonBg.fill('#ff7875');
      layer.draw();
    });

    deleteButtonGroup.on('mouseleave', function () {
      document.body.style.cursor = 'default';
      deleteButtonBg.fill('#ff4d4f');
      layer.draw();
    });

    deleteButtonGroup.on('click tap', (e) => {
      e.cancelBubble = true;
      try {
        if (Array.isArray(node.visualGroups)) {
          node.visualGroups.forEach(({ visual }) => {
            try { visual.destroy(); } catch (_) {}
          });
          node.visualGroups = [];
        }
        if (node.syncVisual) {
          try { node.off('dragmove', node.syncVisual); } catch (_) {}
          try { node.off('transform', node.syncVisual); } catch (_) {}
        }
        if (this.design_group && typeof this.design_group.getChildren === 'function' && typeof node.id === 'function') {
          const nid = node.id();
          this.design_group.getChildren(g => {
            return g && typeof g.name === 'function' && typeof g.getChildren === 'function' && String(g.name()).startsWith('region-group-');
          }).forEach(g => {
            try {
              g.getChildren(c => {
                return c && typeof c.name === 'function' && c.name() === 'sticker-visual' && typeof c.id === 'function' && c.id().includes(nid);
              }).forEach(c => { try { c.destroy(); } catch (_) {} });
            } catch (_) {}
          });
        }
      } catch (_) {}
      try { node.destroy(); } catch (_) {}
      if (transformer && transformer.getLayer()) {
        transformer.destroy();
      }
      if (deleteButtonGroup.getLayer()) {
        deleteButtonGroup.destroy();
      }
      layer.batchDraw();
    });

    // Position the delete button initially
    this.updateDeleteButtonPosition(node, deleteButtonGroup, layer);
    
    return deleteButtonGroup;
  }
  
  /**
   * Update the position of a delete button for a node
   * @param {Konva.Node} node - The node the delete button is for
   * @param {Konva.Group} deleteButtonGroup - The delete button group to position
   * @param {Konva.Layer} layer - The layer containing the node
   */
  updateDeleteButtonPosition(node, deleteButtonGroup, layer) {
    if (!node || !deleteButtonGroup || !layer) return;
    
    try {
      const nodeRect = node.getClientRect({ relativeTo: node.getParent() });
      
      // Position delete button at top-right corner of the node
      deleteButtonGroup.x(nodeRect.x + nodeRect.width);
      deleteButtonGroup.y(nodeRect.y);
      
      // Redraw the layer to show the updated position
      layer.batchDraw();
    } catch (error) {
      console.error('Error updating delete button position:', error);
    }
  }

  // This method has been removed as we're now using the built-in transformer rotation handle with a custom icon


  // Add a transformer to a node (e.g., on click)
  addTransform(node, layerName = 'Design') {
    // Log the node being transformed
    console.log('Clicked on:', node);
    console.log('Using layer:', layerName);
    
    // Do NOT override the layer name - use the one passed in
    // The background image is in the Design group, not the Background layer
    try {
      console.log('Available layers:', Object.keys(this.layers));
      let layer = this.layers[layerName];
      if (!layer) {
        console.error(`Layer ${layerName} not found`);
        console.log('Attempting to use Design layer as fallback');
        layerName = 'Design';
        layer = this.layers[layerName];
        if (!layer) {
          console.error('Design layer also not found, cannot add transformer');
          return null;
        }
        console.log('Using fallback layer:', layerName);
      } else {
        console.log('Layer found:', layerName);
      }

      // Remove existing transformers and their delete buttons
      const existingTransformers = layer.find('Transformer');
      const existingDeleteButtons = layer.find('.delete-button-group');
      existingTransformers.forEach(t => t.destroy());
      existingDeleteButtons.forEach(btn => btn.destroy());

      // Make sure the node is in the layer
      if (!node.getLayer()) {
        console.error('Node is not in any layer');
        return null;
      }
      
      // Set global reference to the selected node
      window.selectedKonvaNode = node;
      console.log('KonvaController: Node selected and set to global reference', node);

      try {
        const nodeType = (node && node.className) ? String(node.className).toLowerCase() : 'unknown';
        try { if (typeof window !== 'undefined') window.selectedNodeType = nodeType; } catch {}
        window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType } }));
        setTimeout(() => {
          try {
            try { if (typeof window !== 'undefined') window.selectedNodeType = nodeType; } catch {}
            window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType } }));
          } catch {}
        }, 0);
      } catch {}
      
      // If this is a text node, dispatch a custom event for TextTab to listen to
      if (node && node.className && node.className === 'Text') {
        console.log('KonvaController: Text node selected in addTransform, dispatching textNodeSelected event');
        console.log('KonvaController: Text node properties:', {
          text: node.text(),
          fontFamily: node.fontFamily(),
          fontSize: node.fontSize(),
          fill: node.fill(),
          id: node.id()
        });
        
        // Set the global reference explicitly
        window.selectedKonvaNode = node;
        console.log('KonvaController: Set window.selectedKonvaNode =', window.selectedKonvaNode);
        
        // Use setTimeout to ensure the event is dispatched after the current execution context
        setTimeout(() => {
          try {
            console.log('KonvaController: Dispatching textNodeSelected event with setTimeout');
            const event = new CustomEvent('textNodeSelected', { 
              detail: { node: node, text: node.text() } 
            });
            window.dispatchEvent(event);
            console.log('KonvaController: Event dispatched successfully');
          } catch (error) {
            console.error('KonvaController: Error dispatching textNodeSelected event:', error);
          }
        }, 0);
      } else {
        console.log('KonvaController: Non-text node selected:', node?.className);
      }

      // We'll draw the rotation icon directly on the canvas

      // Create a new transformer
      const transformer = new Konva.Transformer({
        nodes: [node],
        enabledAnchors: [
          'top-left', 'top-right',
          'bottom-left', 'bottom-right'
        ], // Only show corner anchors
        rotateEnabled: true,
        rotateAnchorVisible: true, // Explicitly enable rotation anchor
        centeredScaling: true,
        keepRatio: true,
        borderEnabled: true,
        borderStroke: '#0099ff',
        borderStrokeWidth: 1,
        anchorStroke: '#0099ff',
        anchorFill: '#ffffff',
        anchorSize: 8,
        anchorCornerRadius: 4,
        rotateAnchorOffset: 40, // Increased distance from the object edge
        rotationSnaps: [], // No snapping
        boundBoxFunc: (oldBox, newBox) => {
          // Limit minimum size
          if (newBox.width < 10 || newBox.height < 10) {
            return oldBox;
          }
          return newBox;
        }
      });

      // Create delete button
      const deleteButtonGroup = this.createDeleteButton(node, layer, transformer);

      // Function to update delete button position
      const updateDeleteButtonPosition = () => {
        const nodeRect = node.getClientRect();
        deleteButtonGroup.x(nodeRect.x + nodeRect.width);
        deleteButtonGroup.y(nodeRect.y);
        layer.batchDraw();
      };

      // Update position on transform and drag
      node.on('transform', updateDeleteButtonPosition);
      node.on('dragmove', updateDeleteButtonPosition);

      const updateSnap = () => {
        try { this.updateNodeSnapAttrs(node); } catch (_) {}
      };
      node.on('dragend', updateSnap);
      node.on('transformend', updateSnap);

      // Function to customize the rotation handle with a drawn rotation icon
      const customizeRotationHandle = () => {
        // Find the rotation handle
        const rotationHandle = transformer.findOne('.rotater');
        if (!rotationHandle) return;

        // Get the current position and size of the rotation handle
        // eslint-disable-next-line no-unused-vars
        const handleWidth = rotationHandle.width();
        // eslint-disable-next-line no-unused-vars
        const handleHeight = rotationHandle.height();

        // Create an off-screen canvas - make it larger than the handle
        const iconCanvas = document.createElement('canvas');
        iconCanvas.width = 30; // Size of the rotation handle
        iconCanvas.height = 30;

        // Draw the icon directly onto the canvas
        const ctx = iconCanvas.getContext('2d');
        const centerX = 15;
        const centerY = 15;
        const radius = 12; // Slightly smaller radius to ensure it fits

        // Clear the canvas first
        ctx.clearRect(0, 0, iconCanvas.width, iconCanvas.height);

        // Draw blue background circle
        ctx.fillStyle = '#1890ff';
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.fill();

        // Draw white border for contrast
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw rotation arrow (circular arrow)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        // Draw circular arc (270 degrees)
        ctx.arc(centerX, centerY, radius * 0.6, Math.PI * 0.25, Math.PI * 2, false);
        ctx.stroke();

        // Draw arrowhead
        const arrowX = centerX + radius * 0.6 * Math.cos(Math.PI * 0.25);
        const arrowY = centerY + radius * 0.6 * Math.sin(Math.PI * 0.25);
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(arrowX + 4, arrowY - 2);
        ctx.lineTo(arrowX + 2, arrowY + 4);
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        // Apply the icon to the rotation handle
        rotationHandle.fill(null); // Remove default fill
        rotationHandle.stroke(null); // Remove stroke
        rotationHandle.strokeWidth(0);

        // Set the size of the rotation handle to match our icon
        rotationHandle.width(30);
        rotationHandle.height(30);
        rotationHandle.offsetX(15); // Center the handle on its position
        rotationHandle.offsetY(15);

        // Use the canvas directly as the fill pattern image
        rotationHandle.fillPriority('pattern'); // Ensure pattern is used
        rotationHandle.fillPatternImage(iconCanvas);
        rotationHandle.fillPatternRepeat('no-repeat'); // Prevent tiling
        rotationHandle.fillPatternScale({ x: 1, y: 1 }); // No scaling
        rotationHandle.fillPatternOffset({ x: 0, y: 0 }); // No offset needed with offsetX/Y

        // Update the layer
        layer.batchDraw();
      };

      // Override the transformer's update method to maintain custom rotation handle
      const originalUpdate = transformer.update.bind(transformer);
      transformer.update = function () {
        originalUpdate();
        customizeRotationHandle();
      };

      // Add transformer and delete button to the layer
      layer.add(transformer);
      layer.add(deleteButtonGroup);

      // Position the delete button initially
      updateDeleteButtonPosition();

      // Apply custom rotation handle
      customizeRotationHandle();

      // Move elements to top
      transformer.moveToTop();
      deleteButtonGroup.moveToTop();

      // Clean up function
      const cleanup = () => {
        // Remove event listeners
        node.off('transform', updateDeleteButtonPosition);
        node.off('dragmove', updateDeleteButtonPosition);
        node.off('dragend', updateSnap);
        node.off('transformend', updateSnap);

        // Remove delete button if it still exists
        if (deleteButtonGroup && deleteButtonGroup.getLayer()) {
          deleteButtonGroup.destroy();
        }

        // Force redraw
        layer.batchDraw();
      };

      // Set up cleanup when transformer is destroyed
      transformer.on('destroy', cleanup);

      // Also clean up when node is removed
      node.on('remove', cleanup);

      // Update delete button visibility based on transformer visibility
      const updateButtonVisibility = () => {
        if (deleteButtonGroup && deleteButtonGroup.getLayer()) {
          if (transformer.visible() && transformer.getLayer()) {
            deleteButtonGroup.show();
          } else {
            deleteButtonGroup.hide();
          }
        }
        layer.batchDraw();
      };

      // Initial visibility update
      updateButtonVisibility();

      // Update visibility when transformer visibility changes
      transformer.on('visibleChange', updateButtonVisibility);

      // Force update the transformer
      transformer.forceUpdate();

      // Redraw the layer
      layer.batchDraw();

      

      // Return the transformer and delete button for external management if needed
      return { transformer, deleteButtonGroup };
    } catch (error) {
      console.error('Error in addTransform:', error);
      return null;
    }
  }

  // Center the currently selected node along an axis ('x' or 'y')
  centerSelected(axis = 'x') {
    try {
      if (!this.stage) return;
      const node = (typeof window !== 'undefined') ? window.selectedKonvaNode : null;
      if (!node || !node.getLayer) return;

      // 1) get real-time node center in stage coords
      const c = this.getNodeVisualCenter(node);
      if (!c) return;
      const nodeProduct = this.stagePointToProductPx(c.x, c.y);
      if (!nodeProduct) return;

      // 2) choose closest configured center along the requested axis
      const snaps = this.getViewSnapPoints();
      let targetStage = null;
      let shouldUpdateSnapAttrs = false;

      if (snaps && snaps.length) {
        let chosen = null;
        let best = Infinity;
        for (const s of snaps) {
          const d = axis === 'y' ? Math.abs(nodeProduct.y - s.y) : Math.abs(nodeProduct.x - s.x);
          if (d < best) {
            best = d;
            chosen = s;
          }
        }
        if (!chosen) return;
        targetStage = this.productPxToStagePoint(chosen.x, chosen.y);
        shouldUpdateSnapAttrs = true;
      } else {
        // Fallback: no centers provided for this view -> use stage/product target center
        const t = this.getTargetCenter ? this.getTargetCenter() : { x: this.stage.width() / 2, y: this.stage.height() / 2 };
        targetStage = { x: t.x, y: t.y };
        shouldUpdateSnapAttrs = false;
      }

      // 3) move node so its visual center snaps to target along that axis
      if (!targetStage) return;
      const dx = axis === 'x' ? (targetStage.x - c.x) : 0;
      const dy = axis === 'y' ? (targetStage.y - c.y) : 0;
      const abs = node.getAbsolutePosition();
      node.setAbsolutePosition({ x: abs.x + dx, y: abs.y + dy });

      // Persist snap attrs after moving so save/load stays correct (only when centers exist)
      if (shouldUpdateSnapAttrs) {
        try { this.updateNodeSnapAttrs(node); } catch (_) {}
      }

      // Refresh transformer and delete button positions if present
      const layer = node.getLayer();
      if (layer) {
        const transformer = layer.findOne('Transformer');
        if (transformer && transformer.nodes && transformer.nodes().includes(node)) {
          transformer.forceUpdate();
        }
        if (node.deleteButtonGroup) {
          try { this.updateDeleteButtonPosition(node, node.deleteButtonGroup, layer); } catch (_) {}
        }
        layer.batchDraw();
      } else if (this.stage) {
        this.stage.batchDraw();
      }
    } catch (err) {
      console.error('Error centering selected node:', err);
    }
  }

  // Helper function to fit image to canvas
  fitImageToCanvas(img, type) {
    if (!img || !img.width || !img.height) {
      console.error('Invalid image dimensions for', type);
      return null;
    }

    const layer = this.layers[type];
    if (!layer) {
      console.error(`Layer ${type} not found`);
      return null;
    }

    // Clear existing images in this layer
    layer.destroyChildren();

    // Calculate dimensions to fit stage while maintaining aspect ratio
    const stageWidth = this.stage.width();
    const stageHeight = this.stage.height();
    const imgRatio = img.width / img.height;
    const stageRatio = stageWidth / stageHeight;

    let width, height, x = 0, y = 0;

    if (imgRatio > stageRatio) {
      // Image is wider than stage (relative to height)
      width = stageWidth;
      height = width / imgRatio;
      y = (stageHeight - height) / 2; // Center vertically
    } else {
      // Image is taller than stage (relative to width)
      height = stageHeight;
      width = height * imgRatio;
      x = (stageWidth - width) / 2; // Center horizontally
    }

    // Create and position the image
    const konvaImage = new Konva.Image({
      image: img,
      x: x,
      y: y,
      width: width,
      height: height,
      listening: false
    });

    // Add to layer and update
    layer.add(konvaImage);
    layer.batchDraw();
    return konvaImage;
  }

  /**
   * Load all product-related images into their respective layers and fit to canvas
   * @param {string} id - Product ID
   * @param {string} baseUrl - Base URL for product assets
   * @param {boolean} hasDesign - Whether a design overlay exists and should be loaded
   * @param {string} designUrl - Base URL for design assets (optional)
   * @returns {Promise<void>} Resolves when all images are loaded
   */
  async loadProduct(id, baseUrl, hasDesign = false, designUrl = null, selectedView = null, caseUrl = null) {
    // Debug log all parameters
    // Store the parameters for potential reuse

    this.productId = id;
    this.baseUrl = baseUrl;
    this.hasDesign = hasDesign;
    this.designUrl = designUrl;
    
    // Import global view utilities
    const { setGlobalSelectedView } = await import('./globalOptions.js');
    try {
      if (!id || !baseUrl) {
        throw new Error('Product ID and base URL are required');
      }

      // Check if stage is initialized
      if (!this.stage) {
        throw new Error('Stage is not initialized. Call init() first.');
      }

      // Extract view ID from selectedView parameter using getViewId helper
      // This handles both string views and object views with translations      
      // Store the selected view both locally and globally
      this.selectedView = selectedView;
      
      // Also store view globally for consistent access
      if (selectedView) {
        setGlobalSelectedView(selectedView);
      }
      
      // Store product ID and other parameters for reuse in setSelectedView
      this.productId = id;
      this.baseUrl = baseUrl;
      this.hasDesign = hasDesign;
      this.designUrl = designUrl;
      this.caseUrl = caseUrl;
      
      // Preserve user design elements before clearing layers
      let userDesignElements = [];
      if (this.design_group) {
        // Find all user-added design elements (Text and Image nodes) excluding product, mask, and design images
        userDesignElements = this.design_group.find('Text, Image').filter(node => 
          !node.name().includes('product') && 
          !node.name().includes('mask') && 
          !node.name().includes('design-image')
        );
        
        
        
        // Remove them temporarily from the design group
        userDesignElements.forEach(node => node.remove());
      }
      
      // Clear existing content
      this.clearProductLayers();

      // Store whether this product has a design overlay
      this._hasDesign = hasDesign;
      

      // Process product ID: remove 'case' suffix and encode for URL
      let processedId = id;
      if (processedId && processedId.toLowerCase().endsWith(' case')) {
        processedId = processedId.substring(0, processedId.length - 5);
        
      }

      
      // Process options to ensure all values are strings
      const processOptionValue = (option) => {
        if (!option) return '';
        // If it's an object with value property, use that
        if (typeof option === 'object' && option !== null) {
          // Handle both { value: 'X' } and { en: 'X', cn: 'Y' } formats
          return option.value || option.en || option.cn || '';
        }
        // If it's already a string, return as is
        if (typeof option === 'string') {
          return option;
        }
        // For any other type, convert to string
        return String(option);
      };

      // Always get options from global storage - no options parameter needed
      const globalOptions = window.selectedOptions || {};
      console.log('🌍 loadProduct using global options:', globalOptions);
      
      const processedOptions = {};
      if (globalOptions && Object.keys(globalOptions).length > 0) {
        Object.keys(globalOptions).forEach(key => {
          processedOptions[key] = processOptionValue(globalOptions[key]);
        });
        console.log('✅ Processed global options:', processedOptions);
      } else {
        console.log('⚠️ No global options available, using empty options');
      }
      //check which option is isVisualOption ,and use the visual option to build the url
      const optionsSchema = (this.configData && this.configData.SupportedModels && this.configData.SupportedModels.Options) || {};
      const visualKeys = Object.keys(optionsSchema).filter(k => (optionsSchema[k] && optionsSchema[k].isVisualOption === true));

      
      
      // Build URL path based visual options , no hardcode options by 1 2 3 , u need check the 
      const optionsPath = visualKeys.map(k => processOptionValue(globalOptions?.[k]))
        .filter(Boolean) // Remove empty values
        .map(encodeURIComponent) // URL encode each part
        .join('/');

      // Add leading slash if we have any options
      const fullOptionsPath = optionsPath ? `${optionsPath}` : '';
      // Get view ID using helper function to handle both string and object views
      const viewId = this.getViewId(selectedView);

      // Build base path with optional options segment (no double slashes)
      const baseWithOptions = fullOptionsPath ? `${baseUrl}/${fullOptionsPath}` : `${baseUrl}`;
      const ProductPath = `${baseWithOptions}/${viewId}/Product.png`;
      const MaskPath = `${baseWithOptions}/${viewId}/Mask.png`;
      const imagesToLoad = [
        this.loadImageWithRetry(MaskPath).catch(error => {
          console.error('Failed to load mask image:', error);
        }),
        this.loadImageWithRetry(ProductPath).catch(error => {
          console.error('Failed to load product image:', error);
        })
      ];

      // Only load Design.png if hasDesign is true
      let designImagePromise = null;
      let designImageIndex = null;
      if (hasDesign) {
        // Use designUrl if provided, otherwise fall back to baseUrl
        const urlToUse = designUrl || baseUrl;
        const designBaseWithOptions = fullOptionsPath ? `${urlToUse}/${fullOptionsPath}` : `${urlToUse}`;
        // Construct design URL with the same format as case URL, avoiding double slashes
        const designImageUrl = `${designBaseWithOptions}/${viewId}/Design.png`;
        
        
        designImageIndex = imagesToLoad.length;
        designImagePromise = this.loadImageWithRetry(designImageUrl).catch(error => {
          console.error('Failed to load design image from primary path:', error);

          // No need for fallback with same URL structure as it would be identical
          console.error('Design image load failed:', error);

          // If designUrl was provided and failed, try with baseUrl as last resort
          if (designUrl && designUrl !== baseUrl) {
            
            const fallbackBaseWithOptions = fullOptionsPath ? `${baseUrl}/${fullOptionsPath}` : `${baseUrl}`;
            const fallbackUrl = `${fallbackBaseWithOptions}/${viewId}/Design.png`;
            
            return this.loadImageWithRetry(fallbackUrl).catch(error3 => {
              console.error('All design image load attempts failed:', error3);
              return null;
            });
          }
          return null;
        });
        imagesToLoad.push(designImagePromise);
      }

      // Load variant-specific DesignProduct.<VIEW> image if configured (e.g., CARD with {index}.jpg)
      let variantDesignImageIndex = null;
      try {
        const variants = this.configData && Array.isArray(this.configData.variants) ? this.configData.variants : null;
        if (variants && variants.length > 0) {
          // Schema-driven variant match using visual options, with backward compatibility (no hardcode)
          const getSelectedByVariantKey = (key) => {
            // Only use direct key lookup. No positional or legacy fallbacks.
            if (globalOptions && Object.prototype.hasOwnProperty.call(globalOptions, key)) {
              return processOptionValue(globalOptions[key]);
            }
            return null;
          };

          const match = variants.find(v => {
            for (const k of Object.keys(v)) {
              if (k === 'DesignProduct') continue;
              const expected = v[k];
              if (expected == null) continue; // null/undefined treated as wildcard
              const selected = getSelectedByVariantKey(k);
              if (!selected) continue; // if we don't have a selection, treat as wildcard
              if (String(expected).toLowerCase() !== String(selected).toLowerCase()) {
                return false;
              }
            }
            return true;
          });
          const viewKey = viewId; // e.g., 'CARD', 'BOX'
          const dp = match && match.DesignProduct ? match.DesignProduct : null;
          const template = dp && typeof dp[viewKey] === 'string' ? dp[viewKey] : null;
          if (template) {
            const index = this.getDesignIndexForView(viewId);
            const filledUrl = template.replace('{index}', String(index));
            console.log('🎨 Loading variant DesignProduct image:', { viewId, index, filledUrl });
            variantDesignImageIndex = imagesToLoad.length;
            imagesToLoad.push(
              this.loadImageWithRetry(filledUrl).catch(err => {
                console.warn('Variant DesignProduct image failed to load:', err);
                return null;
              })
            );
          }
        }
      } catch (e) {
        console.warn('Error while resolving variant DesignProduct image. Continuing without it.', e);
      }

      // Load all required images in parallel with retry logic
      const loadedImages = await Promise.all(imagesToLoad);
      const maskImage = loadedImages[0];
      const productImage = loadedImages[1];
      const designImage = designImageIndex != null ? loadedImages[designImageIndex] : null;

      const variantDesignImage = variantDesignImageIndex != null ? loadedImages[variantDesignImageIndex] : null;
      if (!maskImage || !productImage) {
        throw new Error('Failed to load required product images');
      }

      // Process product image first to set the ratio
      if (productImage) {
        await this.processProductImage(productImage);
      } else {
        throw new Error('Failed to process product image');
      }

      // Ensure region guidelines are added after product image (so they are visible)
      // We explicitly call this AFTER the product image and case image (if any) are processed
      // if (this.layers['Design']) {
      //   console.log('🔄 loadProduct: scheduling _ensureRegionGuidelines');
      //   setTimeout(() => {
      //       this._ensureRegionGuidelines();
      //   }, 100);
      // }

      // Then process design image(s) (above product, below user designs)
      // 1) Base Design.png if present
      if (hasDesign && designImage) {
        await this.processDesignImage(designImage);
      } else if (hasDesign) {
        console.warn('Design image not found or failed to load - continuing without it');
      }


      // Finally process mask image with the ratio from product image
      if (maskImage) {
        await this.processMaskImage(maskImage);
      } else {
        throw new Error('Failed to process mask image');
      }

      // Directly load the initial design once to avoid timing issues with external listeners
      if (this.productId && this.selectedView) {
        this.loadDesign(this.selectedView);
      }
      
      // Load and process case image based on haveCase flag and BaseProductUrl
      try {
        const hasCase = !!(this.configData && this.configData.haveCase);
        const baseProductUrl =
          (this.configData && this.configData.BaseProductUrl) || '';

        if (hasCase && baseProductUrl && this.selectedView) {
          const viewId =
            typeof this.selectedView === 'string'
              ? this.selectedView
              : this.selectedView.id;

          const finalCaseUrl = `${baseProductUrl}/${optionsPath}/${viewId}/Case.png`;

          const caseImage = await this.loadImageWithRetry(finalCaseUrl).catch(error => {
            console.error('Failed to load case image:', error);
            return null;
          });

          // Process the case image if loaded successfully
          if (caseImage) {
            await this.processCaseImage(caseImage);
          } else {
            console.warn('Case image not found or failed to load from constructed URL');
          }
        } else {
          // Remove any existing case images when haveCase is false
          const designLayer = this.layers['Design'];
          if (designLayer) {
            designLayer.find('.case-image').forEach(node => node.destroy());
            designLayer.batchDraw();
          }
        }
      } catch (error) {
        console.error('Error loading case image:', error);
        // Continue without case image, don't fail the whole product loading
      }


      // Ensure product image is visible after loading
      const designLayer = this.layers['Design'];
      if (designLayer) {
        const productImage = designLayer.findOne(node => node.name() === 'product-image');
        if (productImage) {
          // Explicitly ensure product image is visible
          productImage.visible(true);
          
        } else {
          console.warn('Product image not found in design layer after loading');
        }
        
        // Restore user design elements that were preserved before clearing layers
        if (userDesignElements && userDesignElements.length > 0) {
          
          
          // Add back the user design elements to the design group
          userDesignElements.forEach(node => {
            this.design_group.add(node);
            
            // Explicitly restore interactive properties
            node.draggable(true);
            node.listening(true);
            
            // Re-attach event handlers
            node.on('mouseenter', () => { document.body.style.cursor = 'pointer'; });
            node.on('mouseleave', () => { document.body.style.cursor = 'default'; });
            node.on('click tap', (e) => {
              e && (e.cancelBubble = true);
              try {
                window.selectedKonvaNode = node;
                window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'text' } }));
              } catch {}
              this.addTransform(node, 'Design');
            });
            
            
          });
          
          // Move them to the top to ensure they're above product/design images
          userDesignElements.forEach(node => node.moveToTop());
          // Reassert case image to stay above all user elements
          try {
            const caseImageTop = designLayer.findOne(n => n.name && typeof n.name === 'function' ? n.name() === 'case-image' : (n.getAttr && n.getAttr('name') === 'case-image'));
            if (caseImageTop) {
              caseImageTop.moveToTop();
              try { caseImageTop.zIndex(999); } catch (e) {}
            }
            let hintTop = designLayer.findOne(n => n.name && typeof n.name === 'function' ? n.name() === 'hint-layer' : (n.getAttr && n.getAttr('name') === 'hint-layer'));
            if (hintTop) {
              hintTop.moveToTop();
              try { hintTop.zIndex(1000); } catch (_) {}
            }
          } catch (e) {
            // no-op
          }
        }
      }
      // Final update - only if stage is still available
      if (this.stage) {
        this.stage.batchDraw();
        
      } else {
        console.warn('Stage was destroyed before batchDraw could be called');
      }

    } catch (error) {
      console.error('Error loading product:', error);
      // Only re-throw if the stage is still available
      if (this.stage) {
        throw error; // Re-throw to allow callers to handle the error
      } else {
        console.warn('Error occurred after stage was destroyed:', error.message);
      }
    }
  }
  /**
   * Ensures that the design_group is properly initialized and added to the given layer
   * @param {Konva.Layer} layer - The layer to add the design_group to
   * @returns {Konva.Group} - The design_group
   */
  ensureDesignGroup(layer) {
    if (!layer) {
      console.error('Cannot ensure design_group: layer is null or undefined');
      return null;
    }
    
    // Create design_group if it doesn't exist
    if (!this.design_group) {
      
      this.design_group = new Konva.Group({
        name: 'design-group',
        draggable: false
      });
    }
    
    // Check if design_group is already in the layer
    const existingGroup = layer.findOne(node => node === this.design_group);
    if (!existingGroup) {
      
      layer.add(this.design_group);
    } else {
      
    }
    
    return this.design_group;
  }
  
  /**
   * Process and position the case image
   * @param {HTMLImageElement} caseImage - The case image element to process
   * @returns {Konva.Image|null} - The Konva image object or null if failed
   * @private
   */
  async processCaseImage(caseImage) {
    // Use the Design layer instead of creating a new layer
    const overlayLayer = this.layers['Overlay'] || this.layers['Design'];
    if (!overlayLayer) {
      console.error('Design layer not found');
      return null;
    }
    
    try {
      // Clear any existing case images
      overlayLayer.find('.case-image').forEach(node => node.destroy());
      
      // Use the same ratio and position as the product image
      if (!this._productImageRatio || !this._productImagePosition || !this._productImageDimensions) {
        console.error('Product image data not available for case image processing');
        return null;
      }
      
      // Get product image data
      const ratio = this._productImageRatio;
      const { x, y } = this._productImagePosition;
      const { width: productWidth, height: productHeight } = this._productImageDimensions;
      
      // Calculate case image dimensions using the same ratio as product image
      const newWidth = caseImage.width * ratio;
      const newHeight = caseImage.height * ratio;
      
      // Calculate centered position relative to product image
      const centerX = x + (productWidth - newWidth) / 2;
      const centerY = y + (productHeight - newHeight) / 2;
      
      
      
      // Create and configure case image
      const konvaCaseImage = new Konva.Image({
        image: caseImage,
        x: centerX,
        y: centerY, 
        width: newWidth,
        height: newHeight,
        name: 'case-image',
        listening: false,
        draggable: false,
        visible: true
      });
      
      // No clipping needed - just display the case image on top
      
      
      // Always add case image directly to the layer so it stays above group contents
      overlayLayer.add(konvaCaseImage);
      // Make sure case image is always on top
      konvaCaseImage.moveToTop();
      // Ensure case image has a very high z-index within the layer
      try { konvaCaseImage.zIndex(999); } catch (e) {}
      this.ensureDesignGroup(this.layers['Design']);
      
      try {
        const dl = this.layers['Design'];
        let hintTop = null;
        if (dl) hintTop = dl.findOne(n => n.name && n.name() === 'hint-layer');
        if (!hintTop) hintTop = overlayLayer.findOne(n => n.name && n.name() === 'hint-layer');
        if (hintTop) {
          hintTop.moveToTop();
          try { hintTop.zIndex(1000); } catch (_) {}
        }
      } catch (_) {}
      
      // Force redraw
      overlayLayer.batchDraw();
      
      
      return konvaCaseImage;
    } catch (error) {
      console.error('Error processing case image:', error);
      return null;
    }
  }
  
  /**
   * Set the selected view and reload the product with the new view
   * @param {Object|string} view - The view to select
   * @returns {Promise<void>}
   */
  async setSelectedView(view) {
    const { setGlobalSelectedView } = await import('./globalOptions.js');

    // Persist history for current view before switching
    try {
      const prevViewId = this.getViewId(this.selectedView);
      if (prevViewId) {
        this._historyByView = this._historyByView || {};
        const h = this.history;
        if (h) {
          this._historyByView[String(prevViewId)] = {
            undo: Array.isArray(h._undo_stack) ? h._undo_stack.slice() : [],
            redo: Array.isArray(h._redo_stack) ? h._redo_stack.slice() : [],
            current: h._current_state
          };
        }
      }
    } catch (_) {}

    // Persist current view's index before switching
    try {
      const prevViewId = this.getViewId(this.selectedView);
      if (prevViewId && typeof window !== 'undefined') {
        window.designIndexByView = window.designIndexByView || {};
        const prevIdx = this.getDesignIndexForView(prevViewId);
        window.designIndexByView[prevViewId] = prevIdx;
        console.log('💾 Saved design index before view change', { prevViewId, index: prevIdx });
      }
    } catch (e) {
      console.warn('Could not persist design index before view change:', e);
    }

    this.selectedView = view;
    setGlobalSelectedView(view);
    const viewId = this.getViewId(view);

    // Restore history for target view (redo should work again when you come back)
    try {
      const key = String(viewId);
      const saved = this._historyByView && this._historyByView[key];
      if (saved) {
        if (!this.history) this.history = new HistoryManager(null);
        this.history._undo_stack = Array.isArray(saved.undo) ? saved.undo.slice() : [];
        this.history._redo_stack = Array.isArray(saved.redo) ? saved.redo.slice() : [];
        this.history._current_state = saved.current;
      } else {
        // New view = new timeline; don't allow redo from previous view
        if (!this.history) this.history = new HistoryManager(null);
        try { this.history.clear(); } catch (_) {
          this.history._undo_stack = [];
          this.history._redo_stack = [];
          this.history._current_state = null;
        }
      }
    } catch (_) {}
    
    // Clear the variant state cache to ensure fresh image loading
    if (this._variantState) {
      console.log('🔄 Clearing variant state cache to ensure fresh image loading');
      this._variantState = {};
    }
    
    // Set current index from global per-view store (do not overwrite if already set)
    if (typeof window !== 'undefined') {
      window.designIndexByView = window.designIndexByView || {};
      const existing = window.designIndexByView[viewId];
      const idxNum = Number(existing);
      const safeIdx = Number.isFinite(idxNum) && idxNum > 0 ? idxNum : 1;
      window.currentDesignIndex = safeIdx;
      if (existing == null) {
        window.designIndexByView[viewId] = safeIdx;
      }
      console.log(`🔄 Using persisted design index for view`, { viewId, index: safeIdx });
    }
    
    // Reload product assets for the new view so the canvas updates visually
    try {
      if (this.productId && this.baseUrl) {
        console.log('🔁 setSelectedView: reloading product for view', viewId);
        await this.loadProduct(
          this.productId,
          this.baseUrl,
          this.hasDesign,
          this.designUrl,
          view,
          this.caseUrl
        );
        // Best-effort: refresh variant-specific overlay for this view
        try {
          await this.updateVariantDesignLayer(viewId);
        } catch (_) {}
        if (this.stage) this.stage.batchDraw();
      } else {
        console.warn('setSelectedView called before product context initialized; skipping reload');
      }
    } catch (err) {
      console.error('setSelectedView reload failed:', err);
    }
    
  }
  loadImageWithTimeout(url, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';

      const normalizedUrl = this._normalizeImageUrl(url);

      const timer = setTimeout(() => {
        img.onload = img.onerror = null;
        reject(new Error(`Image load timeout: ${url}`));
      }, timeout);

      img.onload = () => {
        clearTimeout(timer);
        resolve(img);
      };

      img.onerror = (err) => {
        clearTimeout(timer);
        reject(err);
      };

      // Request URL exactly as constructed (no cache-busting query params)
      img.src = normalizedUrl;
    });
  }

  /**
   * Load an image with retry functionality
   * @param {string} url - URL of the image to load
   * @param {number} maxRetries - Maximum number of retry attempts
   * @param {number} timeout - Timeout for each attempt in milliseconds
   * @returns {Promise<Image>} - Promise resolving to the loaded image
   */
  async loadImageWithRetry(url, maxRetries = 3, timeout = 10000) {
    let lastError = null;
    
    // Handle data URLs (iOS device uploads) - no retries needed for data URLs
    if (url.startsWith('data:image/')) {
      try {
        console.log(` Loading data URL image: ${url.substring(0, 50)}...`);
        return await this.loadImageWithTimeout(url, timeout);
      } catch (error) {
        console.error('Failed to load data URL image:', error);
        throw error;
      }
    }
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(` Loading image (attempt ${attempt}/${maxRetries}): ${url}`);
        return await this.loadImageWithTimeout(url, timeout);
      } catch (error) {
        lastError = error;
        console.warn(`Image load attempt ${attempt}/${maxRetries} failed for ${url}:`, error.message);
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed
    throw lastError || new Error(`Failed to load image after ${maxRetries} attempts: ${url}`);
  }

  /**
   * Clear product-related layers
   * @private
   */
  clearProductLayers() {
    const designLayer = this.layers['Design'];
    if (!designLayer) return;

    // Clear existing content from all relevant layers
    ['Product'].forEach(layerName => {
      const layer = this.layers[layerName];
      if (layer) layer.destroyChildren();
    });

    // Clear design group but preserve any user-added elements
    if (this.design_group) {
      // Find and remove only product-related images
      const productImages = this.design_group.find(node =>
        node.name() === 'product-image' ||
        (node.name() && node.name().includes('product'))
      );

      // Remove existing design image if present
      const designImage = this.design_group.findOne(node => node.name() === 'design-image');
      if (designImage) {
        designImage.destroy();
        
      }

      // Remove product images
      productImages.forEach(node => node.destroy());
    }

    // Clear mask
    const existingMask = designLayer.findOne(node => node.name() === 'mask-image');
    if (existingMask) {
      existingMask.destroy();
    }
    const existingHint = designLayer.findOne(node => node.name() === 'hint-layer');
    if (existingHint) {
      existingHint.destroy();
    }
    try {
      if (this.design_group) {
        const hintInGroup = this.design_group.findOne(node => node.name && node.name() === 'hint-layer');
        if (hintInGroup) hintInGroup.destroy();
      }
    } catch (_) {}
  }

  /**
   * Process and position the mask image
   * @private
   */
  async processMaskImage(maskImage) {
    const designLayer = this.layers['Design'];
    if (!designLayer) {
      console.error('Design layer not found');
      return;
    }

    // Ensure design group exists and is in the layer
    this.ensureDesignGroup(designLayer);

    // Use the same ratio and position as the product image
    if (!this._productImageRatio || !this._productImagePosition || !this._productImageDimensions) {
      console.error('Product image data not available');
      return;
    }

    // Get product image data
    const ratio = this._productImageRatio;
    const { x, y } = this._productImagePosition;
    const { width: productWidth, height: productHeight } = this._productImageDimensions;

    // Calculate mask dimensions using the same ratio as product image
    const newWidth = maskImage.width * ratio;
    const newHeight = maskImage.height * ratio;

    // Calculate centered position relative to product image
    const centerX = x + (productWidth - newWidth) / 2;
    const centerY = y + (productHeight - newHeight) / 2;

    // Create and configure mask image
    const konvaMaskImage = new Konva.Image({
      image: maskImage,
      x: centerX,
      y: centerY,
      width: newWidth,
      height: newHeight,
      globalCompositeOperation: 'destination-in',
      listening: false,
      name: 'mask-image',
      visible: true
    });

    // Add mask to layer
    designLayer.add(konvaMaskImage);

    // Apply clip to design group using product dimensions
    this.design_group.clipFunc((ctx) => {
      ctx.rect(x, y, productWidth, productHeight);
    });

    // Bring mask to top
    konvaMaskImage.moveToTop();
    designLayer.batchDraw();

    try {
      const cfg = (this.configData && this.configData.HintLayer) || {};
      const color = typeof cfg.color === 'string' && cfg.color ? cfg.color : '#0066FF';
      const opacity = typeof cfg.opacity === 'number' ? cfg.opacity : 0.5;
      const pattern = typeof cfg.pattern === 'string' ? cfg.pattern : '';
      // Defaults:
      // - borderDotLine: do NOT fill by default (so it doesn't block view)
      // - other patterns: preserve existing behavior (fill enabled unless explicitly disabled)
      const fillEnabled = pattern === 'borderDotLine'
        ? cfg.fillEnabled === true
        : cfg.fillEnabled === false ? false : true;
      const patternColor =
        (typeof cfg.patternColor === 'string' && cfg.patternColor) || color;
      const borderColor =
        (typeof cfg.borderColor === 'string' && cfg.borderColor) || patternColor;
      const borderWidth =
        (typeof cfg.borderWidth === 'number' && isFinite(cfg.borderWidth) && cfg.borderWidth > 0)
          ? cfg.borderWidth
          : 2;
      const borderDotStep =
        (typeof cfg.borderDotStep === 'number' && isFinite(cfg.borderDotStep) && cfg.borderDotStep > 0)
          ? Math.floor(cfg.borderDotStep)
          : 10;
      const borderDotRadius =
        (typeof cfg.borderDotRadius === 'number' && isFinite(cfg.borderDotRadius) && cfg.borderDotRadius > 0)
          ? cfg.borderDotRadius
          : borderWidth;
      const borderDotRectLong =
        (typeof cfg.borderDotRectLong === 'number' && isFinite(cfg.borderDotRectLong) && cfg.borderDotRectLong > 0)
          ? Math.floor(cfg.borderDotRectLong)
          : 4;
      const borderDotRectShort =
        (typeof cfg.borderDotRectShort === 'number' && isFinite(cfg.borderDotRectShort) && cfg.borderDotRectShort > 0)
          ? Math.floor(cfg.borderDotRectShort)
          : 1;
      const borderDotGap =
        (typeof cfg.borderDotGap === 'number' && isFinite(cfg.borderDotGap) && cfg.borderDotGap >= 0)
          ? Math.floor(cfg.borderDotGap)
          : 4;
      const borderDotsSpan =
        (typeof cfg.borderDotsSpan === 'number' && isFinite(cfg.borderDotsSpan) && cfg.borderDotsSpan > 0 && cfg.borderDotsSpan <= 1)
          ? cfg.borderDotsSpan
          : (pattern === 'borderDotLine' ? 1 : 0.5);
      const borderDotsPerSide =
        (typeof cfg.borderDotsPerSide === 'number' && isFinite(cfg.borderDotsPerSide) && cfg.borderDotsPerSide > 0)
          ? Math.floor(cfg.borderDotsPerSide)
          : null;
      // For borderDotLine, default is border-only unless explicitly enabled.
      // For dotLine45, default stays enabled.
      const innerPatternEnabled = pattern === 'borderDotLine'
        ? cfg.innerPatternEnabled === true
        : cfg.innerPatternEnabled === false ? false : true;

      const hintGroup = new Konva.Group({
        x: centerX,
        y: centerY,
        listening: false,
        draggable: false,
        visible: !!this._desiredHintVisible,
        name: 'hint-layer',
        opacity: opacity
      });

      const hintFill = fillEnabled ? new Konva.Rect({
        x: 0,
        y: 0,
        width: newWidth,
        height: newHeight,
        fill: color,
        listening: false,
        name: 'hint-fill'
      }) : null;

      // Optional 45-degree dot/line overlay
      let hintPatternShape = null;
      if (pattern === 'dotLine45' || (pattern === 'borderDotLine' && innerPatternEnabled)) {
        hintPatternShape = new Konva.Shape({
          x: 0,
          y: 0,
          width: newWidth,
          height: newHeight,
          listening: false,
          name: 'hint-pattern',
          sceneFunc: (ctx, shape) => {
            try {
              const step = 16; // distance between diagonal lines
              const lineWidth = 1.2;

              ctx.save();
              ctx.strokeStyle = patternColor;
              ctx.lineWidth = lineWidth;

              // Draw 45-degree diagonal lines across the rect
              for (let offset = -newHeight; offset < newWidth; offset += step) {
                ctx.beginPath();
                // line runs roughly from left to bottom-right across the rect
                let startX = offset;
                let startY = 0;
                let endX = offset + newHeight;
                let endY = newHeight;

                // Clip to bounds manually to avoid drawing outside rect
                if (endX < 0 || startY > newHeight) continue;
                if (startX > newWidth) continue;

                // Clamp to rect
                if (startX < 0) {
                  const diff = -startX;
                  startX = 0;
                  startY += diff;
                }
                if (endX > newWidth) {
                  const diff = endX - newWidth;
                  endX = newWidth;
                  endY -= diff;
                }

                ctx.moveTo(startX, startY);
                ctx.lineTo(endX, endY);
                ctx.stroke();
              }

              ctx.restore();
            } catch (e) {
              console.error('Error drawing hint pattern', e);
            }
            ctx.fillStrokeShape(shape);
          }
        });
      }

      // Optional border outline around the mask area
      let hintBorderShape = null;
      if (pattern === 'borderDotLine') {
        let outlineCanvas = null;
        try {
          const buildOutlineCanvas = () => {
            const c = document.createElement('canvas');
            c.width = Math.max(1, Math.floor(newWidth));
            c.height = Math.max(1, Math.floor(newHeight));
            const cctx = c.getContext('2d');
            if (!cctx) return null;

            // Draw mask image scaled to export size
            cctx.clearRect(0, 0, c.width, c.height);
            cctx.drawImage(maskImage, 0, 0, c.width, c.height);

            const imgData = cctx.getImageData(0, 0, c.width, c.height);
            const data = imgData.data;

            // Create outline pixels: alpha>0 and any 4-neighbor alpha==0
            const out = cctx.createImageData(c.width, c.height);
            const od = out.data;

            const bc = String(borderColor || '#ff0000');
            // Parse border color to rgb (basic #rgb/#rrggbb support)
            let r = 255, g = 0, b = 0;
            try {
              let hex = bc.trim();
              if (hex.startsWith('#')) hex = hex.slice(1);
              if (hex.length === 3) {
                r = parseInt(hex[0] + hex[0], 16);
                g = parseInt(hex[1] + hex[1], 16);
                b = parseInt(hex[2] + hex[2], 16);
              } else if (hex.length >= 6) {
                r = parseInt(hex.slice(0, 2), 16);
                g = parseInt(hex.slice(2, 4), 16);
                b = parseInt(hex.slice(4, 6), 16);
              }
              if (!isFinite(r)) r = 255;
              if (!isFinite(g)) g = 0;
              if (!isFinite(b)) b = 0;
            } catch (_) {}

            const w = c.width;
            const h = c.height;
            const aAt = (x, y) => {
              if (x < 0 || y < 0 || x >= w || y >= h) return 0;
              return data[(y * w + x) * 4 + 3] || 0;
            };

            const isEdge = (x, y) => {
              const a = aAt(x, y);
              if (a === 0) return false;
              return aAt(x - 1, y) === 0 || aAt(x + 1, y) === 0 || aAt(x, y - 1) === 0 || aAt(x, y + 1) === 0;
            };

            const setRectDot = (cx, cy, ww, hh) => {
              const dw = Math.max(1, Math.min(80, Math.floor(ww)));
              const dh = Math.max(1, Math.min(80, Math.floor(hh)));
              const startX = Math.round(cx - dw / 2);
              const startY = Math.round(cy - dh / 2);
              for (let yy = startY; yy < startY + dh; yy++) {
                for (let xx = startX; xx < startX + dw; xx++) {
                  if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
                  const idx = (yy * w + xx) * 4;
                  od[idx] = r;
                  od[idx + 1] = g;
                  od[idx + 2] = b;
                  od[idx + 3] = 255;
                }
              }
            };

            const placeDotsOnLine = (x1, y1, x2, y2, count, dotW, dotH) => {
              const n = Math.max(1, Math.floor(count));
              if (n === 1) {
                setRectDot(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2), dotW, dotH);
                return;
              }
              for (let i = 0; i < n; i++) {
                const t = i / (n - 1);
                const x = Math.round(x1 + (x2 - x1) * t);
                const y = Math.round(y1 + (y2 - y1) * t);
                setRectDot(x, y, dotW, dotH);
              }
            };

            const computeCountForSpan = (spanLenPx, dotLenPx) => {
              const span = Math.max(0, Math.floor(spanLenPx));
              const unit = Math.max(1, Math.floor(dotLenPx) + borderDotGap);
              // ensure we always have at least 2 dots if there is space
              const raw = Math.floor(span / unit) + 1;
              return Math.max(2, raw);
            };

            if ((borderDotsPerSide != null && borderDotsPerSide > 0) || borderDotsSpan) {
              // Fixed-dot mode: compute bounding box of mask alpha and place N dots per side.
              let minX = w, minY = h, maxX = -1, maxY = -1;
              for (let yy = 0; yy < h; yy++) {
                for (let xx = 0; xx < w; xx++) {
                  if (aAt(xx, yy) === 0) continue;
                  if (xx < minX) minX = xx;
                  if (yy < minY) minY = yy;
                  if (xx > maxX) maxX = xx;
                  if (yy > maxY) maxY = yy;
                }
              }
              if (maxX >= 0 && maxY >= 0) {
                const pad = Math.max(2, Math.floor(Math.max(borderDotRectLong, borderDotRectShort) / 2) + 1);
                const xL = Math.max(0, minX + pad);
                const xR = Math.min(w - 1, maxX - pad);
                const yT = Math.max(0, minY + pad);
                const yB = Math.min(h - 1, maxY - pad);

                // Constrain dots to the centered middle span of each side
                const spanX = Math.max(0, xR - xL);
                const spanY = Math.max(0, yB - yT);
                const a = Math.max(0, (1 - borderDotsSpan) / 2);
                const b = 1 - a;
                const midX1 = Math.round(xL + spanX * a);
                const midX2 = Math.round(xL + spanX * b);
                const midY1 = Math.round(yT + spanY * a);
                const midY2 = Math.round(yT + spanY * b);

                // Default: top/bottom are horizontal 4x1; left/right are vertical 1x4
                const dotWH = { w: borderDotRectLong, h: borderDotRectShort };
                const dotWV = { w: borderDotRectShort, h: borderDotRectLong };

                const topSpan = Math.max(0, midX2 - midX1);
                const sideSpan = Math.max(0, midY2 - midY1);
                const nTop = borderDotsPerSide != null ? borderDotsPerSide : computeCountForSpan(topSpan, dotWH.w);
                const nSide = borderDotsPerSide != null ? borderDotsPerSide : computeCountForSpan(sideSpan, dotWV.h);

                placeDotsOnLine(midX1, yT, midX2, yT, nTop, dotWH.w, dotWH.h);
                placeDotsOnLine(midX1, yB, midX2, yB, nTop, dotWH.w, dotWH.h);
                placeDotsOnLine(xL, midY1, xL, midY2, nSide, dotWV.w, dotWV.h);
                placeDotsOnLine(xR, midY1, xR, midY2, nSide, dotWV.w, dotWV.h);
              }
            } else {

              // Dotted border: sample edge pixels sparsely and draw square dot blocks
              // Use hash-based sampling to avoid dashed/striped artifacts.
              const step = Math.max(2, Math.min(80, borderDotStep));
              const maxRadius = Math.max(1, Math.floor(step / 4));
              const radius = Math.max(1, Math.min(maxRadius, Math.floor(borderDotRadius)));
              for (let y = 0; y < h; y++) {
                for (let x = 0; x < w; x++) {
                  if (!isEdge(x, y)) continue;
                  // Deterministic sparse sampling on edge pixels
                  const hh = (((x * 73856093) ^ (y * 19349663)) >>> 0);
                  const modBase = Math.max(8, step * step * 3);
                  if ((hh % modBase) !== 0) continue;

                  setRectDot(x, y, Math.max(1, Math.floor(borderDotRectLong)), Math.max(1, Math.floor(borderDotRectShort)));
                }
              }
            }

            // Put outline onto canvas
            const oc = document.createElement('canvas');
            oc.width = c.width;
            oc.height = c.height;
            const occtx = oc.getContext('2d');
            if (!occtx) return null;
            occtx.putImageData(out, 0, 0);
            return oc;
          };
          outlineCanvas = buildOutlineCanvas();
        } catch (e) {
          outlineCanvas = null;
        }

        if (outlineCanvas) {
          hintBorderShape = new Konva.Shape({
            x: 0,
            y: 0,
            width: newWidth,
            height: newHeight,
            listening: false,
            name: 'hint-border',
            sceneFunc: (ctx, shape) => {
              try {
                ctx.save();
                ctx.drawImage(outlineCanvas, 0, 0, newWidth, newHeight);
                ctx.restore();
              } catch (e) {
                console.error('Error drawing hint border', e);
              }
              ctx.fillStrokeShape(shape);
            }
          });
        }
      }

      const hintMask = new Konva.Image({
        image: maskImage,
        x: 0,
        y: 0,
        width: newWidth,
        height: newHeight,
        globalCompositeOperation: 'destination-in',
        listening: false,
        name: 'hint-mask'
      });

      if (hintFill) hintGroup.add(hintFill);
      if (hintBorderShape) {
        hintGroup.add(hintBorderShape);
      }
      if (hintPatternShape) {
        hintGroup.add(hintPatternShape);
      }

      // Draw optional center markers (+) for view center[] points on top of guideline
      try {
        const centers = this.getViewSnapPoints ? this.getViewSnapPoints() : [];
        if (centers && centers.length && this._productImageRatio && this._productImagePosition) {
          const g2 = new Konva.Group({ name: 'hint-centers', listening: false });
          const size = 18;
          const strokeWidth = 2;
          const stroke = (typeof borderColor === 'string' && borderColor)
            ? borderColor
            : ((typeof patternColor === 'string' && patternColor) ? patternColor : color);
          for (const c of centers) {
            const pt = this.productPxToStagePoint(c.x, c.y);
            if (!pt) continue;
            const lx = pt.x - centerX;
            const ly = pt.y - centerY;
            if (!isFinite(lx) || !isFinite(ly)) continue;
            g2.add(new Konva.Line({
              points: [lx - size / 2, ly, lx + size / 2, ly],
              stroke,
              strokeWidth,
              listening: false
            }));
            g2.add(new Konva.Line({
              points: [lx, ly - size / 2, lx, ly + size / 2],
              stroke,
              strokeWidth,
              listening: false
            }));
          }
          if (g2.children && g2.children.length) hintGroup.add(g2);
        }
      } catch (_) {}
      hintGroup.add(hintMask);

      this.ensureDesignGroup(designLayer);
      this.design_group.add(hintGroup);
      hintGroup.moveToTop();
      designLayer.batchDraw();

      try {
        if (typeof window !== 'undefined') window.__hintVisible = !!this._desiredHintVisible;
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('HINT_VISIBILITY_CHANGED', { detail: { visible: !!this._desiredHintVisible } }));
      } catch (_) {}
    } catch (_) {}

    return konvaMaskImage;
  }

  /**
   * Process and position the product image
   * @private
   */
  async processProductImage(productImage) {
    const productLayer = this.layers['Product'];
    const designLayer = this.layers['Design'];
    if (!productLayer || !designLayer) return;

    try {
      // Ensure design group exists
      this.ensureDesignGroup(designLayer);

      // Get stage dimensions
      const stageWidth = this.stage.width();
      const stageHeight = this.stage.height();

      // Detect if we're on mobile (using screen width as a heuristic)
      const isMobile = window.innerWidth <= 768;

      let ratio, newWidth, newHeight, x, y;

      if (isMobile) {
        // Mobile: contain and center
        const imgRatioMobile = productImage.width / productImage.height;
        const stageRatioMobile = stageWidth / stageHeight;
        if (imgRatioMobile > stageRatioMobile) {
          // Fit to width, center vertically
          ratio = stageWidth / productImage.width;
          newWidth = stageWidth;
          newHeight = productImage.height * ratio;
          x = 0;
          y = (stageHeight - newHeight) / 2;
        } else {
          // Fit to height, center horizontally
          ratio = stageHeight / productImage.height;
          newWidth = productImage.width * ratio;
          newHeight = stageHeight;
          x = (stageWidth - newWidth) / 2;
          y = 0;
        }
      } else {
        // Desktop: contain image within stage (fit width OR height based on aspect ratio)
        const stageRatio = stageWidth / stageHeight;
        const imgRatio = productImage.width / productImage.height;
        if (imgRatio > stageRatio) {
          // Image is wider than stage -> fit to width
          ratio = stageWidth / productImage.width;
          newWidth = stageWidth;
          newHeight = productImage.height * ratio;
          x = 0;
          y = (stageHeight - newHeight) / 2; // Center vertically
        } else {
          // Image is taller (relative) -> fit to height
          ratio = stageHeight / productImage.height;
          newWidth = productImage.width * ratio;
          newHeight = stageHeight;
          x = (stageWidth - newWidth) / 2; // Center horizontally
          y = 0; // Align to top
        }
      }


      // Store the ratio and position for mask image
      this._productImageRatio = ratio;
      this._productImagePosition = { x, y };
      this._productImageDimensions = { width: newWidth, height: newHeight };

      // Add background version to product layer (non-interactive)
      const productKonvaImage = new Konva.Image({
        image: productImage,
        x,
        y,
        width: newWidth,
        height: newHeight,
        listening: false,
        name: 'product-background'
      });

      productLayer.add(productKonvaImage);

      // Add transformable version to design group
      const transformableProductImg = new Konva.Image({
        image: productImage,
        x,
        y,
        width: newWidth,
        height: newHeight,
        name: 'product-image',
        draggable: false,
        listening: false,
        visible: true ,// Explicitly set visibility to true
        id: 'product-image'
      });
      
      

      this.design_group.add(transformableProductImg);
      transformableProductImg.moveToBottom();

      // Force update the layer
      designLayer.batchDraw();
      productLayer.batchDraw();

      // After product repositioning, reposition user nodes relative to snap points
      if (this.design_group) {
        this.design_group.getChildren().forEach(node => {
          try {
            const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
            if (nm !== 'product-image' && nm !== 'variant-design-image' && nm !== 'hint-layer' && nm !== 'background-color' && nm !== 'background-image' && nm !== 'design-image') {
              this.applySnapToNodeIfPresent(node);
            }
          } catch (_) {}
        });
      }

      return true;
    } catch (error) {
      console.error('Error processing product image:', error);
    }
  }
  async processDesignImage(designImage) {
    const designLayer = this.layers['Design'];
    if (!designLayer) {
      console.error('Design layer not found');
      return null;
    }

    // Ensure design group exists and is in the layer
    this.ensureDesignGroup(designLayer);

    try {
      // Use the same ratio and position as the product image
      if (!this._productImageRatio || !this._productImagePosition || !this._productImageDimensions) {
        console.error('Product image data not available');
        return null;
      }

      // Get product image data
      const ratio = this._productImageRatio;
      const { x, y } = this._productImagePosition;
      const { width: productWidth, height: productHeight } = this._productImageDimensions;

      // Calculate design dimensions using the same ratio as product image
      const newWidth = designImage.width * ratio;
      const newHeight = designImage.height * ratio;

      // Calculate centered position relative to product image
      const centerX = x + (productWidth - newWidth) / 2;
      const centerY = y + (productHeight - newHeight) / 2;


      // Create and configure design image
      const konvaDesignImage = new Konva.Image({
        image: designImage,
        x: centerX,
        y: centerY,
        width: newWidth,
        height: newHeight,
        listening: false, // Not draggable
        name: 'design-image',
        draggable: false, // Explicitly set not draggable
        visible: true // Explicitly set visible
      });

      // Add design image to design group
      this.design_group.add(konvaDesignImage);

      // Position the design image between product image and user elements
      const productImage = this.design_group.findOne(node => node.name() === 'product-image');
      if (productImage) {
        // Make sure product image is at the bottom
        productImage.moveToBottom();

        // Place design image just above product image in z-index
        konvaDesignImage.moveUp();
        
      } else {
        // If no product image, move design image to bottom
        konvaDesignImage.moveToBottom();
        
      }

      // Force redraw
      designLayer.batchDraw();
      

      return konvaDesignImage;
    } catch (error) {
      console.error('Error processing design image:', error);
      return null;
    }
  }

  // Processes a variant-specific design image (e.g., DesignProduct.CARD with {index})
  // Places it above the base Design.png but below user-added elements
  async processVariantDesignImage(variantImage) {
    const designLayer = this.layers['Design'];
    if (!designLayer) {
      console.error('Design layer not found');
      return null;
    }

    this.ensureDesignGroup(designLayer);

    try {
      if (!this._productImageRatio || !this._productImagePosition || !this._productImageDimensions) {
        console.error('Product image data not available');
        return null;
      }

      const ratio = this._productImageRatio;
      const { x, y } = this._productImagePosition;
      const { width: productWidth, height: productHeight } = this._productImageDimensions;

      const newWidth = variantImage.width * ratio;
      const newHeight = variantImage.height * ratio;
      const centerX = x + (productWidth - newWidth) / 2;
      const centerY = y + (productHeight - newHeight) / 2;

      const konvaVariantImage = new Konva.Image({
        image: variantImage,
        x: centerX,
        y: centerY,
        width: newWidth,
        height: newHeight,
        listening: false,
        name: 'variant-design-image',
        draggable: false,
        visible: true
      });

      this.design_group.add(konvaVariantImage);

      // Layering: product-image (bottom), design-image (base), variant-design-image (above base)
      const productImg = this.design_group.findOne(n => n.name && n.name() === 'product-image');
      const baseDesign = this.design_group.findOne(n => n.name && n.name() === 'design-image');
      if (productImg) productImg.moveToBottom();
      if (baseDesign) {
        baseDesign.moveUp();
        konvaVariantImage.moveToTop();
      } else {
        if (productImg) {
          konvaVariantImage.moveToTop();
        } else {
          konvaVariantImage.moveToBottom();
        }
      }

      designLayer.batchDraw();

      return konvaVariantImage;
    } catch (err) {
      console.error('Error processing variant design image:', err);
      return null;
    }
  }

  // Update the variant-specific design layer when design index or options change
  async updateVariantDesignLayer(viewId) {
    try {
      const designLayer = this.layers && this.layers['Design'];
      if (!designLayer) return;

      this.ensureDesignGroup(designLayer);
      

      // Resolve current variant based on global options
      const processOptionValue = (option) => {
        if (!option) return '';
        if (typeof option === 'object' && option !== null) return option.value || option.en || option.cn || '';
        if (typeof option === 'string') return option;
        return String(option);
      };

      const globalOptions = (typeof window !== 'undefined' && window.selectedOptions) || {};
      // Determine non-visual options from product config
      const optionsSchema = (this.configData && this.configData.SupportedModels && this.configData.SupportedModels.Options) || {};
      const nonVisualKeys = Object.keys(optionsSchema).filter(k => !(optionsSchema[k] && optionsSchema[k].isVisualOption === true));
      const selectedNonVisual = {};
      nonVisualKeys.forEach(k => { selectedNonVisual[k] = processOptionValue(globalOptions?.[k]); });

      const variants = this.configData && Array.isArray(this.configData.variants) ? this.configData.variants : null;
      if (!variants || variants.length === 0) return;
      const keysUsed = nonVisualKeys.slice().sort();
      const normalize = (v) => (v == null ? '' : String(v).toLowerCase());
      const variantMatches = (variant) => {
        for (const k of keysUsed) {
          const target = normalize(selectedNonVisual[k]);
          const cand = normalize(variant[k]);
          // if variant does not specify key, treat as wildcard
          if (cand === '') continue;
          if (target === '') return false;
          if (cand !== target) return false;
        }
        return true;
      };
      const match = variants.find(variantMatches);
      if (!match) {
        return;
      } else {
      }
      const dp = match && match.DesignProduct ? match.DesignProduct : null;
      const dpVal = dp ? dp[viewId] : null;
      if (!dpVal) return;

      // Support both string template and object with url/maxDesignCount/allowDesign_index
      let template = null;
      let maxDesignCount = null;
      let allowSpecs = null;
      if (typeof dpVal === 'string') {
        template = dpVal;
      } else if (typeof dpVal === 'object') {
        template = dpVal.url;
        maxDesignCount = dpVal.maxDesignCount;
        allowSpecs = Array.isArray(dpVal.allowDesign_index) ? dpVal.allowDesign_index : null;
      }
      if (!template) return;

      // Expand allowDesign_index specs like ["1-54", "55"] to a Set of allowed indexes
      const expandAllowed = (specs, max) => {
        if (!specs || specs.length === 0) {
          if (typeof max === 'number' && max > 0) {
            const s = new Set();
            for (let i = 1; i <= max; i++) s.add(i);
            return s;
          }
          return null; // no restriction
        }
        const s = new Set();
        specs.forEach(item => {
          const str = String(item).trim();
          if (!str) return;
          const m = str.match(/^(\d+)\s*-\s*(\d+)$/);
          if (m) {
            let a = parseInt(m[1], 10), b = parseInt(m[2], 10);
            if (Number.isFinite(a) && Number.isFinite(b)) {
              const start = Math.min(a, b), end = Math.max(a, b);
              for (let i = start; i <= end; i++) s.add(i);
            }
          } else if (/^\d+$/.test(str)) {
            s.add(parseInt(str, 10));
          }
        });
        return s;
      };

      const allowedSet = expandAllowed(allowSpecs, maxDesignCount);

      // Determine index. Behavior differs for UI vs export:
      // - During export (window.__isExporting === true): ignore allowDesign_index, use exact index and only clamp by maxDesignCount if provided.
      // - During UI interactions: respect allowDesign_index by coercing into allowedSet when necessary.
      let index = this.getDesignIndexForView(viewId);
      const isExporting = (typeof window !== 'undefined' && window.__isExporting) === true;
      if (isExporting) {
        // Only clamp to 1..maxDesignCount if available; otherwise keep as-is
        if (typeof maxDesignCount === 'number' && maxDesignCount > 0) {
          index = Math.max(1, Math.min(index, maxDesignCount));
        } else if (!Number.isFinite(index) || index <= 0) {
          index = 1;
        }
      } else {
        // UI path: enforce allowed set if present
        const isAllowed = (idx) => {
          if (allowedSet) return allowedSet.has(idx);
          if (typeof maxDesignCount === 'number' && maxDesignCount > 0) return idx >= 1 && idx <= maxDesignCount;
          return true;
        };
        if (!isAllowed(index)) {
          if (allowedSet && allowedSet.size > 0) {
            index = Math.min(...allowedSet); // pick the smallest allowed
          } else if (typeof maxDesignCount === 'number' && maxDesignCount > 0) {
            index = Math.max(1, Math.min(index, maxDesignCount));
          } else if (!Number.isFinite(index) || index <= 0) {
            index = 1;
          }
        }
      }
      
      const filledUrl = template.replace('{index}', String(index));
      console.log('🎨 updateVariantDesignLayer ->', { viewId, index, filledUrl, maxDesignCount, allowSpecs });

      // Canonical URL (strip cache-busting/query) and simple per-view dedupe
      this._variantState = this._variantState || {};
      const viewKey = String(viewId);
      const state = this._variantState[viewKey] || (this._variantState[viewKey] = {});

      const img = await this.loadImageWithRetry(filledUrl).catch(err => {
        console.warn('Variant DesignProduct image failed to load in updateVariantDesignLayer:', err);
        return null;
      });
      if (img) {
        await this.processVariantDesignImage(img);
        state.currentUrl = filledUrl;
      }

      // Ensure product-image is aligned to mask-image after updates
      try {
        await this.alignProductToMask();
      } catch (_) {}

      designLayer.batchDraw();
    } catch (e) {
      console.warn('updateVariantDesignLayer error:', e);
    }
  }

  // Force-align product-image node to the mask-image node (position and size)
  // This prevents any drift introduced by view/variant updates or transforms.
  async alignProductToMask() {
    try {
      const designLayer = this.layers && this.layers['Design'];
      if (!designLayer) return;

      const maskImage = designLayer.findOne(node => node.name && node.name() === 'mask-image');
      const productImage = designLayer.findOne(node => node.name && node.name() === 'product-image');

      if (!maskImage || !productImage) return;

      // Copy geometry from mask to product
      const w = typeof maskImage.width === 'function' ? maskImage.width() : (maskImage.attrs && maskImage.attrs.width) || 0;
      const h = typeof maskImage.height === 'function' ? maskImage.height() : (maskImage.attrs && maskImage.attrs.height) || 0;
      const x = typeof maskImage.x === 'function' ? maskImage.x() : (maskImage.attrs && maskImage.attrs.x) || 0;
      const y = typeof maskImage.y === 'function' ? maskImage.y() : (maskImage.attrs && maskImage.attrs.y) || 0;

      // Normalize product transforms first to avoid compounding
      try { if (typeof productImage.rotation === 'function') productImage.rotation(0); } catch {}
      try { if (typeof productImage.scale === 'function') productImage.scale({ x: 1, y: 1 }); } catch {}

      if (typeof productImage.position === 'function') productImage.position({ x, y });
      if (typeof productImage.width === 'function') productImage.width(w);
      if (typeof productImage.height === 'function') productImage.height(h);

      if (designLayer) designLayer.batchDraw();
    } catch (_) {}
  }

  // Sets a background image behind user design elements
  setBackgroundImage(imageUrl) {
    if (!this.stage || !imageUrl) {
      console.error('Stage not initialized or invalid image URL');
      return;
    }

    // Ensure design group exists
    const designLayer = this.layers && this.layers['Design'];
    if (!designLayer) {
      console.error('Design layer not found');
      return;
    }
    this.ensureDesignGroup(designLayer);

    console.log('Setting background image:', imageUrl);

    // Remove any existing background image and background color
    this.removeBackgroundImage();
    this.removeBackgroundColor && this.removeBackgroundColor();

    // Create a new image object
    const image = new window.Image();
    image.crossOrigin = 'Anonymous'; // Enable CORS

    image.onload = () => {
      // Determine target rect: product bounds if available, else stage bounds
      const hasProductRect = !!(this._productImagePosition && this._productImageDimensions);
      const rectX = hasProductRect ? this._productImagePosition.x : 0;
      const rectY = hasProductRect ? this._productImagePosition.y : 0;
      const rectW = hasProductRect ? this._productImageDimensions.width : this.stage.width();
      const rectH = hasProductRect ? this._productImageDimensions.height : this.stage.height();

      // Create Konva image once the image is loaded - similar to addImage method
      const backgroundImage = new Konva.Image({
        x: rectX,
        y: rectY,
        image: image,
        width: rectW,
        height: rectH,
        name: 'background-image',
        id: 'background-' + Date.now(),
        visible: true,
        opacity: 1,
        listening: true,
        draggable: true,
        hitStrokeWidth: 10,
        hitStroke: 'transparent'
      });

      // Add to design group
      this.design_group.add(backgroundImage);

      // Ensure design group is clipped to product bounds if available
      if (hasProductRect) {
        this.design_group.clipFunc((ctx) => {
          ctx.rect(rectX, rectY, rectW, rectH);
        });
      }

      // Place just above the product image so it acts as a design background
      const productImage = this.design_group.findOne(node => node.name && node.name() === 'product-image');
      if (productImage) {
        productImage.moveToBottom();
        backgroundImage.moveToBottom();
        backgroundImage.moveUp();
      } else {
        backgroundImage.moveToBottom();
      }

      // Click handler to select and transform
      backgroundImage.on('click tap', (e) => {
        e.cancelBubble = true;
        this.addTransform(backgroundImage, 'Design');
      });

      // Add transform controls immediately
      this.addTransform(backgroundImage, 'Design');

      this.stage.batchDraw();
      console.log('Background image set successfully');
    };

    image.onerror = (error) => {
      console.error('Error loading background image:', error);
    };

    // Start loading the image
    image.src = imageUrl;
  }

  /**
  2199→   * Sets a solid background color behind user design elements (instead of an image)
  2200→   * - Removes any existing background image
  2201→   * - Creates/updates a Konva.Rect named 'background-color' in the design_group
   * - Positions it just above the product image (bottom-most among design elements)
   * @param {string} color - CSS color string (e.g. '#ff0000', 'rgba(...)')
   */
  setBackground(color) {
    if (!this.stage || !this.design_group) {
      console.error('Stage or design_group not initialized');
      return;
    }

    try {
      // Remove any existing background image
      this.removeBackgroundImage();

      // Find existing background color rect
      let bgRect = this.design_group.findOne(node => node.name && node.name() === 'background-color');

      if (!bgRect) {
        // Create a new rect the size of the stage
        bgRect = new Konva.Rect({
          x: (this._productImagePosition && this._productImageDimensions) ? this._productImagePosition.x : 0,
          y: (this._productImagePosition && this._productImageDimensions) ? this._productImagePosition.y : 0,
          width: (this._productImageDimensions && this._productImageDimensions.width) ? this._productImageDimensions.width : this.stage.width(),
          height: (this._productImageDimensions && this._productImageDimensions.height) ? this._productImageDimensions.height : this.stage.height(),
          name: 'background-color',
          listening: false,
          draggable: false,
          visible: true,
          fill: color || 'transparent'
        });

        // Add to design group
        this.design_group.add(bgRect);

        // Place just above the product image so it acts as a design background
        const productImage = this.design_group.findOne(node => node.name && node.name() === 'product-image');
        if (productImage) {
          // Ensure product image is the lowest
          productImage.moveToBottom();
          // Put bg rect at bottom then move it one step up (just above product image)
          bgRect.moveToBottom();
          bgRect.moveUp();
        } else {
          // If no product image, keep it at bottom
          bgRect.moveToBottom();
        }

        // Keep size in sync if stage is resized
        if (!this._onResizeBgHandler) {
          this._onResizeBgHandler = () => {
            const rect = this.design_group && this.design_group.findOne(n => n.name && n.name() === 'background-color');
            if (rect) {
              if (this._productImagePosition && this._productImageDimensions) {
                rect.position({ x: this._productImagePosition.x, y: this._productImagePosition.y });
                rect.width(this._productImageDimensions.width);
                rect.height(this._productImageDimensions.height);
              } else {
                rect.position({ x: 0, y: 0 });
                rect.width(this.stage.width());
                rect.height(this.stage.height());
              }
            }
          };
          this.stage.on('resize.backgroundColor', this._onResizeBgHandler);
        }
      }

      // Update color
      bgRect.fill(color || 'transparent');

      this.stage.batchDraw();
    } catch (e) {
      console.error('Failed to set background color:', e);
    }
  }

  /**
   * Removes the solid background color rect if present
   */
  removeBackgroundColor() {
    if (!this.design_group) return;
    const bgRect = this.design_group.findOne(node => node.name && node.name() === 'background-color');
    if (bgRect) {
      bgRect.destroy();
      this.stage && this.stage.batchDraw();
    }
  }

  removeBackgroundImage() {
    if (!this.design_group) return;
    
    // Find and remove any existing background image
    const existingBgImage = this.design_group.findOne(node => node.name() === 'background-image');
    if (existingBgImage) {
      existingBgImage.destroy();
      this.stage.batchDraw();
    }
  }

  setHintVisible(isVisible) {
    try {
      this._desiredHintVisible = !!isVisible;
      const designLayer = this.layers && this.layers['Design'];
      const group = this.design_group;
      let hint = null;
      if (designLayer) {
        hint = designLayer.findOne(node => node.name && node.name() === 'hint-layer');
      }
      if (!hint && group) {
        hint = group.findOne(node => node.name && node.name() === 'hint-layer');
      }
      if (hint) {
        hint.visible(!!isVisible);
        if (designLayer) designLayer.batchDraw();
        if (this.stage) this.stage.batchDraw();
      } else {
        try {
          const expected = !!isVisible;
          let tries = 0;
          const tick = () => {
            tries += 1;
            try {
              if (this._desiredHintVisible !== expected) return;
              const dl = this.layers && this.layers['Design'];
              const g = this.design_group;
              let h = null;
              if (dl) h = dl.findOne(n => n.name && n.name() === 'hint-layer');
              if (!h && g) h = g.findOne(n => n.name && n.name() === 'hint-layer');
              if (h) {
                h.visible(expected);
                try { if (dl) dl.batchDraw(); } catch (_) {}
                try { if (this.stage) this.stage.batchDraw(); } catch (_) {}
                return;
              }
              if (tries < 12) setTimeout(tick, 80);
            } catch (_) {}
          };
          setTimeout(tick, 0);
        } catch (_) {}
      }
      try {
        if (typeof window !== 'undefined') window.__hintVisible = !!isVisible;
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('HINT_VISIBILITY_CHANGED', { detail: { visible: !!isVisible } }));
      } catch (_) {}
    } catch (_) {}
  }

  setHintOpacity(opacity) {
    try {
      const designLayer = this.layers && this.layers['Design'];
      const group = this.design_group;
      let hint = null;
      if (designLayer) {
        hint = designLayer.findOne(node => node.name && node.name() === 'hint-layer');
      }
      if (!hint && group) {
        hint = group.findOne(node => node.name && node.name() === 'hint-layer');
      }
      if (!hint || typeof opacity !== 'number') return;
      const clamped = Math.max(0, Math.min(1, opacity));
      try { hint.opacity(clamped); } catch (_) {}
      try {
        const filters = hint.filters && hint.filters();
        if (!filters || !filters.includes(Konva.Filters.RGBA)) {
          hint.filters([Konva.Filters.RGBA]);
        }
        hint.alpha(clamped);
      } catch (_) {}
      if (designLayer) designLayer.batchDraw();
      if (this.stage) this.stage.batchDraw();
    } catch (_) {}
  }

  setHintColor(color) {
    try {
      if (typeof color !== 'string' || !color) return;
      const designLayer = this.layers && this.layers['Design'];
      const group = this.design_group;
      let hint = null;
      if (designLayer) hint = designLayer.findOne(node => node.name && node.name() === 'hint-layer');
      if (!hint && group) hint = group.findOne(node => node.name && node.name() === 'hint-layer');
      if (!hint) return;
      const fillRect = hint.findOne(n => n.name && n.name() === 'hint-fill');
      if (fillRect && typeof fillRect.fill === 'function') {
        fillRect.fill(color);
      }
      if (designLayer) designLayer.batchDraw();
      if (this.stage) this.stage.batchDraw();
    } catch (_) {}
  }

  /**
   * Check if a background image is currently present in the design group
   * @returns {boolean}
   */
  hasBackgroundImage() {
    if (!this.design_group) return false;
    const existingBgImage = this.design_group.findOne(node => node.name && node.name() === 'background-image');
    return !!existingBgImage;
  }

  /**
   * Check if a background color rect is currently present in the design group
   * @returns {boolean}
   */
  hasBackgroundColor() {
    if (!this.design_group) return false;
    const bgRect = this.design_group.findOne(node => node.name && node.name() === 'background-color');
    return !!bgRect;
  }

  // ensureDesignGroup method is defined elsewhere in the class

  /**
   * Get the current product options
   * @returns {Object} The current product options in simple value format
   */
  getCurrentOptions() {
    // Always get options from global storage only
    const options = window.selectedOptions;
    if (!options) {
      return {};
    }
    
    // Extract option values from the options object
    const result = {};
    
    if (options.Option1) {
      // Extract just the value if it's in {value: 'something'} format
      result.Option1 = options.Option1.value || options.Option1;
    }
    
    if (options.Option2) {
      // Extract just the value if it's in {value: 'something'} format
      result.Option2 = options.Option2.value || options.Option2;
    }
    
    
    return result;
  }
  
  /**
   * Reload the current product using global options
   * This is the preferred method for reloading after options change
   * @returns {Promise<void>} Resolves when product is reloaded
   */
  /**
      }
    });
    if (!this.productId || !this.baseUrl) {
      console.warn('Cannot reload: missing productId or baseUrl');
      return;
    }
    
    // Call loadProduct without options parameter - it will use global options
    await this.loadProduct(
      this.productId,
      this.baseUrl,
      this.hasDesign,
      this.designUrl,
      this.selectedView,
      this.caseUrl
    );
  }
  
  // getViewId and setSelectedView methods are defined elsewhere in the class

  /**
   * Calculate image dimensions to fit canvas while maintaining aspect ratio
   * @private
   */
  calculateImageDimensions(img) {
    // Validate input image
    if (!img || !img.width || !img.height) {
      console.error('Invalid image provided to calculateImageDimensions');
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    // Get stage dimensions with fallbacks
    let stageWidth = 0;
    let stageHeight = 0;

    try {
      stageWidth = this.stage?.width() || 0;
      stageHeight = this.stage?.height() || 0;

      // If stage dimensions are zero, try to get container dimensions
      if (stageWidth === 0 || stageHeight === 0) {
        const container = this.stage?.container();
        if (container) {
          const rect = container.getBoundingClientRect();
          stageWidth = rect.width || 0;
          stageHeight = rect.height || 0;
        }
      }

      // If still no dimensions, use a default size
      if (stageWidth === 0 || stageHeight === 0) {
        console.warn('Using fallback dimensions for stage');
        stageWidth = window.innerWidth > 0 ? Math.min(window.innerWidth, 500) : 500;
        stageHeight = 500;
      }
    } catch (error) {
      console.error('Error getting stage dimensions:', error);
      stageWidth = window.innerWidth > 0 ? Math.min(window.innerWidth, 500) : 500;
      stageHeight = 500;
    }

    const imgRatio = img.width / img.height;
    const stageRatio = stageWidth / stageHeight;

    let width, height, x = 0, y = 0;

    if (imgRatio > stageRatio) {
      // Image is wider than stage (relative to height)
      width = stageWidth;
      height = width / imgRatio;
      y = (stageHeight - height) / 2; // Center vertically
    } else {
      // Image is taller than stage (relative to width)
      height = stageHeight;
      width = height * imgRatio;
      x = (stageWidth - width) / 2; // Center horizontally
    }
    
    return { x, y, width, height };
  }

  /**
   * Export a preview image with product image unmasked and design elements masked
   * @returns {Promise<string>} Promise that resolves with the data URL of the preview image
   */
  async exportPreview(options = {}) {
    
    
    const designLayer = this.layers['Design'];
    if (!designLayer) throw new Error('Design layer not found');

    const hideUi = !(options && options.hideUi === false);

    const selectedBefore = (typeof window !== 'undefined') ? window.selectedKonvaNode : null;
    const transformers = hideUi ? designLayer.find('Transformer') : [];
    const deleteButtons = hideUi ? designLayer.find('.delete-button-group') : [];
    const transformerStates = hideUi ? transformers.map(t => ({ node: t, wasVisible: t.visible() })) : [];
    const deleteBtnStates = hideUi ? deleteButtons.map(b => ({ node: b, wasVisible: b.visible() })) : [];
    if (hideUi) {
      transformerStates.forEach(({ node }) => node.visible(false));
      deleteBtnStates.forEach(({ node }) => node.visible(false));
    }
    
    // Store all product-related nodes and their visibility states
    const productNodes = [];
    
    // Find product image
    const productImage = designLayer.findOne(node => node.name() === 'product-image');
    if (productImage) {
      productNodes.push({ node: productImage, wasVisible: productImage.visible() });
      productImage.visible(true);
      
    }
    
    // Find case image
    const overlayLayer = this.layers['Overlay'] || designLayer;
    const caseImage = overlayLayer.findOne(node => node.name && node.name() === 'case-image') || designLayer.findOne(node => node.name && node.name() === 'case-image');
    if (caseImage) {
      productNodes.push({ node: caseImage, wasVisible: caseImage.visible() });
      caseImage.visible(true);
      
    }
    
    // Get mask image for export dimensions
    const maskImage = designLayer.findOne(node => node.name() === 'mask-image');
    if (!maskImage) {
      // Restore visibility if we changed it
      productNodes.forEach(item => {
        if (item.node) item.node.visible(item.wasVisible);
      });
      throw new Error('Mask image not found');
    }
    
    const maskElement = maskImage.image();
    if (!maskElement) throw new Error('Mask image element not found');
    
    // Use original mask dimensions for export
    const exportWidth = maskElement.width;
    const exportHeight = maskElement.height;
    
    
    // Calculate scale factors
    const scaleX = exportWidth / maskImage.width();
    const scaleY = exportHeight / maskImage.height();
    
    try {
      // STEP 1: Create a stage for the product image (bottom layer, no mask)
      const productStage = new Konva.Stage({
        width: exportWidth,
        height: exportHeight,
        container: document.createElement('div')
      });
      
      const productLayer = new Konva.Layer();
      productStage.add(productLayer);
      
      // Clone product image
      if (productImage) {
        const productClone = productImage.clone({
          visible: true
        });
        
        // Position and scale the product image
        productClone.position({
          x: -maskImage.x() * scaleX + productImage.x() * scaleX,
          y: -maskImage.y() * scaleY + productImage.y() * scaleY
        });
        
        productClone.scale({
          x: productClone.scaleX() * scaleX,
          y: productClone.scaleY() * scaleY
        });
        
        productLayer.add(productClone);
        
      }
      // Case image will be drawn on a separate overlay stage after masked design to ensure top order
      
      // STEP 2: Create a stage for design elements with mask
      const designStage = new Konva.Stage({
        width: exportWidth,
        height: exportHeight,
        container: document.createElement('div')
      });
      
      const designLayer = new Konva.Layer();
      designStage.add(designLayer);
      
      // Clone design elements (excluding product image)
      const designGroup = new Konva.Group({
        x: -maskImage.x() * scaleX,
        y: -maskImage.y() * scaleY,
        scaleX: scaleX,
        scaleY: scaleY
      });
      
      // Get clip regions for node processing
      const viewId = this.getViewId(this.selectedView);
      const views = this.configData?.SupportedModels?.CommonProperties?.Views;
      const viewCfg = Array.isArray(views) ? views.find(v => v.id === viewId) : null;
      const clipRegions = viewCfg?.ClipRegions;
      
      this.design_group.getChildren().forEach(node => {
        // Skip product image for design layer
        if (node.name() === 'product-image' || (node.name() && node.name().includes('product'))) {
          return;
        }
        if (node.name() === 'hint-layer') {
          return;
        }
        
        // If this is a region group, clone the entire group with its clipping
        if (node.name() && node.name().startsWith('region-group-')) {
          const groupRegion = node.getAttr('region') || [];
          
          // Clone the entire region group to preserve its clipping
          const regionGroupClone = node.clone();
          regionGroupClone.visible(true);
          
          // Apply JSON region clipping to the cloned group
          if (groupRegion.length > 0 && clipRegions) {
            /*
            regionGroupClone.clipFunc((ctx) => {
              
              // Apply each region from JSON (union of all regions)
              groupRegion.forEach(regionKey => {
                const regionConfig = clipRegions[regionKey];
                if (!regionConfig) {
                  return;
                }
                
                const range = Array.isArray(regionConfig) ? regionConfig : regionConfig?.range;
                if (!range || range.length !== 2) {
                  return;
                }
                
                const [start, end] = range;
                
                ctx.rect(-10000, start, 20000, end - start);
              });
            });
            */
          }
          
          designGroup.add(regionGroupClone);
          return; // Skip processing this node as individual node
        }
        
        // Process regular nodes (non-region groups)
        const clone = node.clone();
        
        // Skip control nodes that have visual nodes (handled inside region groups).
        if (node.visualNode) {
          return;
        }
        // For uploaded-image controls: only skip if they have a corresponding visual/regions.
        if (node.name() === 'uploaded-image') {
          const hasVisual = !!(node.visualGroup || node.visualNode);
          let hasRegions = false;
          try {
            const r1 = node.getAttr && node.getAttr('region');
            const r2 = node.visualGroup && node.visualGroup.getAttr && node.visualGroup.getAttr('region');
            const rr = Array.isArray(r1) && r1.length > 0 ? r1 : (Array.isArray(r2) ? r2 : []);
            hasRegions = Array.isArray(rr) && rr.length > 0;
          } catch (_) {}
          if (hasVisual || hasRegions) {
            return;
          }
          try { clone.visible(true); } catch (_) {}
        }
        
        // Make design images visible
        if (node.name() === 'design-image') {
          clone.visible(true);
        }
        
        // Check what region this node is assigned to
        let nodeRegions = node.getAttr('region') || [];
        
        // If this is a control node, check its visual group for regions
        if (nodeRegions.length === 0 && node.visualGroup) {
          nodeRegions = node.visualGroup.getAttr('region') || [];
        }
        
        // If still no regions, check if this is a visual node itself
        if (nodeRegions.length === 0 && (node.name() === 'sticker-visual' || node.name() === 'text-visual')) {
          // Visual nodes are inside region groups, check parent
          const parent = node.getParent();
          if (parent && parent.name() && parent.name().includes('visual-group') && typeof parent.getAttr === 'function') {
            nodeRegions = parent.getAttr('region') || [];
          }
        }
        
        // Apply region-specific clipping if node has regions assigned
        if (nodeRegions.length > 0 && clipRegions) {
          // Wrap in group for clipping
          const wrapperGroup = new Konva.Group();
          wrapperGroup.add(clone);
          
          wrapperGroup.clipFunc((ctx) => {
            
            const productHeight = this._productImageDimensions.height;
            const scaleY = exportHeight / productHeight;
            
            // Apply only the regions this node is assigned to
            nodeRegions.forEach(regionKey => {
              const regionConfig = clipRegions[regionKey];
              if (!regionConfig) {
                return;
              }
              
              const range = Array.isArray(regionConfig) ? regionConfig : regionConfig?.range;
              if (!range || range.length !== 2) {
                return;
              }
              
              const [start, end] = range;
              const exportY = start * scaleY;
              const exportHeight = (end - start) * scaleY;
              ctx.rect(-10000, exportY, 20000, exportHeight);
            });
          });
          
          designGroup.add(wrapperGroup);
        } else {
          // No region assignment, add clone without clipping
          designGroup.add(clone);
        }
      });
      
      designLayer.add(designGroup);
      
      // Add mask to design layer
      const maskClone = maskImage.clone({
        width: exportWidth,
        height: exportHeight,
        x: 0,
        y: 0,
        globalCompositeOperation: 'destination-in'
      });
      designLayer.add(maskClone);
      
      // STEP 3: Export both layers and combine them
      const productDataUrl = productStage.toDataURL({
        mimeType: 'image/png',
        quality: 1.0,
        pixelRatio: 1.0,
        width: exportWidth,
        height: exportHeight
      });
      
      const designDataUrl = designStage.toDataURL({
        mimeType: 'image/png',
        quality: 1.0,
        pixelRatio: 1.0,
        width: exportWidth,
        height: exportHeight
      });
      
      // Prepare case overlay data URL (draw last to ensure case is on top)
      let caseDataUrl = null;
      if (caseImage) {
        const caseStage = new Konva.Stage({
          width: exportWidth,
          height: exportHeight,
          container: document.createElement('div')
        });
        const caseLayer = new Konva.Layer();
        caseStage.add(caseLayer);
        const caseClone = caseImage.clone({ visible: true });
        caseClone.position({
          x: -maskImage.x() * scaleX + caseImage.x() * scaleX,
          y: -maskImage.y() * scaleY + caseImage.y() * scaleY
        });
        caseClone.scale({
          x: (typeof caseClone.scaleX === 'function' ? caseClone.scaleX() : (caseClone.attrs && caseClone.attrs.scaleX) || 1) * scaleX,
          y: (typeof caseClone.scaleY === 'function' ? caseClone.scaleY() : (caseClone.attrs && caseClone.attrs.scaleY) || 1) * scaleY
        });
        caseLayer.add(caseClone);
        caseDataUrl = caseStage.toDataURL({
          mimeType: 'image/png',
          quality: 1.0,
          pixelRatio: 1.0,
          width: exportWidth,
          height: exportHeight
        });
        try { caseStage.destroy(); } catch (_) {}
      }
      
      // STEP 4: Combine the layers using a canvas
      const canvas = document.createElement('canvas');
      canvas.width = exportWidth;
      canvas.height = exportHeight;
      const ctx = canvas.getContext('2d');
      
      // Load and draw the product image first (bottom layer)
      const productImg = new Image();
      await new Promise(resolve => {
        productImg.onload = resolve;
        productImg.src = productDataUrl;
      });
      ctx.drawImage(productImg, 0, 0);
      
      // Load and draw the masked design elements on top
      const designImg = new Image();
      await new Promise(resolve => {
        designImg.onload = resolve;
        designImg.src = designDataUrl;
      });
      ctx.drawImage(designImg, 0, 0);
      
      // Draw case overlay last (always on top)
      if (caseDataUrl) {
        const caseImg = new Image();
        await new Promise(resolve => {
          caseImg.onload = resolve;
          caseImg.src = caseDataUrl;
        });
        ctx.drawImage(caseImg, 0, 0);
      }
      
      // Get the combined image
      const combinedDataUrl = canvas.toDataURL('image/png');
      
      // Add DPI metadata
      const highDpiUrl = changeDpiDataUrl(combinedDataUrl, 300);
      
      // Clean up
      productStage.destroy();
      designStage.destroy();
      
      
      return highDpiUrl;
    } catch (error) {
      console.error('Error exporting preview:', error);
      throw error;
    } finally {
      try {
        if (hideUi) {
          transformerStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
          deleteBtnStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
        }
      } catch (_) {}

      try {
        if (hideUi && selectedBefore && selectedBefore.getLayer && selectedBefore.getLayer()) {
          const hasTransformer = designLayer.find('Transformer').length > 0;
          if (!hasTransformer) {
            this.addTransform(selectedBefore, 'Design');
          }
        }
      } catch (_) {}

      // Restore visibility
      productNodes.forEach(item => {
        if (item.node) item.node.visible(item.wasVisible);
      });
      
    }
  }

  /**
   * Export product design, preview, and texture images
   * @param {Object|boolean|function} options - Either an options object, a legacy boolean for fixDesign, or an onProgress function
   *   - options.fixDesign {boolean}: include product image in design export (false = transparent background)
   *   - options.onProgress {function(percent:number, message?:string)}: progress callback
   * @returns {Promise<{design:string, preview:string, texture:string}>}
   */
  async exportProduct(options = {}) {
    let onProgress = null;
    let fixDesign = false; // false = hide product image in design export

    if (typeof options === 'function') {
      onProgress = options;
    } else if (typeof options === 'boolean') {
      fixDesign = options;
    } else if (options && typeof options === 'object') {
      onProgress = options.onProgress || null;
      fixDesign = !!options.fixDesign;
    }

    try {
      try { this._debugDumpDesignGroup('beforeExport'); } catch (_) {}
      if (onProgress) onProgress(10, 'status.preparingExport');

      const designLayer = this.layers && this.layers['Design'];
      if (!designLayer || !this.design_group) {
        throw new Error('Design layer or group not found');
      }

      // Ensure variant design image for the current view/index is up-to-date before exporting
      try {
        const viewIdForExport = this.getViewId ? this.getViewId(this.selectedView || window.selectedView) : (typeof this.selectedView === 'object' ? this.selectedView?.id : this.selectedView);
        if (viewIdForExport) {
          await this.updateVariantDesignLayer(viewIdForExport);
        }
      } catch (e) {
        console.warn('exportProduct: failed to refresh variant design layer before export', e);
      }

      if (onProgress) onProgress(40, 'status.exportingPreview');
      const previewUrl = await this.exportPreview();

      // Resolve whether to apply mask during design export based on variant config and current view
      let applyMask = true; // default preserves existing behavior
      let includeCaseInDesign = true; // default: include case image in design export
      try {
        const cfg = this.configData;
        const selected = this.currentOptions || window.selectedOptions || {};
        const supported = cfg?.SupportedModels?.Options;
        const variants = Array.isArray(cfg?.variants) ? cfg.variants : null;
        if (supported && variants) {
          const resolveSelectedForKey = (key) => {
            const byKey = selected?.[key];
            if (byKey != null) return (typeof byKey === 'object' && 'value' in byKey) ? byKey.value : byKey;
            const metaName = supported?.[key]?.name;
            if (metaName) {
              const byName = selected?.[metaName];
              if (byName != null) return (typeof byName === 'object' && 'value' in byName) ? byName.value : byName;
            }
            return undefined;
          };

          const variant = variants.find(v => {
            for (const k of Object.keys(v)) {
              if (k === 'DesignProduct' || k === 'ExportIncludeCase' || k === 'price' || k === 'id') continue;
              const expected = v[k];
              if (expected == null || String(expected).trim() === '') continue;
              const sel = resolveSelectedForKey(k);
              if (sel == null) continue; // treat missing selection as wildcard
              if (String(sel).toLowerCase() !== String(expected).toLowerCase()) return false;
            }
            return true;
          });

          const viewId = this.getViewId ? this.getViewId(this.selectedView || window.selectedView) : (typeof this.selectedView === 'object' ? this.selectedView?.id : this.selectedView);
          const maskFlag = variant?.ExportMasked && viewId ? variant.ExportMasked[viewId] : undefined;
          if (typeof maskFlag === 'boolean') {
            applyMask = maskFlag;
          }
          let caseFlag;
          const getCaseFlag = (map, vid) => {
            if (!map || vid == null) return undefined;
            if (typeof map === 'boolean') return map;
            if (typeof map !== 'object') return undefined;
            const direct = map[vid];
            if (typeof direct === 'boolean') return direct;
            const vidLower = String(vid).toLowerCase();
            for (const k of Object.keys(map)) {
              if (String(k).toLowerCase() === vidLower && typeof map[k] === 'boolean') {
                return map[k];
              }
            }
            return undefined;
          };

          caseFlag = getCaseFlag(variant?.ExportIncludeCase, viewId);
          if (caseFlag === undefined) {
            caseFlag = getCaseFlag(cfg?.ExportIncludeCase, viewId);
          }
          if (typeof caseFlag === 'boolean') includeCaseInDesign = caseFlag;
        }
      } catch (e) {
        console.warn('exportProduct: failed to resolve applyMask from config; defaulting to true', e);
      }

      // Allow URL param override: includeCase=true/false
      try {
        const qp = new URLSearchParams(window.location.search);
        const p = qp.get('includeCase');
        if (p != null) {
          includeCaseInDesign = /^(1|true)$/i.test(p);
        }
      } catch {}

      if (onProgress) onProgress(70, 'status.exportingDesign');
      const designUrl = await this.exportDesignLayer(
        designLayer,
        this.design_group,
        true,           // showDesignOverlay (include design index overlay)
        fixDesign,      // showProductImage (true = include product image)
        applyMask,      // apply mask based on variant + view
        includeCaseInDesign // include case image toggle
      );

      // Keep texture equal to preview for now to simplify
      const textureUrl = previewUrl;

      if (onProgress) onProgress(100, 'status.exportComplete');
      try { this._debugDumpDesignGroup('afterExport'); } catch (_) {}
      return { design: designUrl, preview: previewUrl, texture: textureUrl };
    } catch (error) {
      console.error('Error in exportProduct:', error);
      try { this._debugDumpDesignGroup('afterExport'); } catch (_) {}
      if (onProgress) onProgress(100, 'error.exportFailed');
      throw error;
    }
  }

  /**
   * Export a texture layer for 3D model application
   * @param {Object} designLayer - The design layer to export
   * @param {Object} designGroup - The design group containing the design elements
   * @returns {Promise<string>} Promise that resolves with the data URL of the texture image
   */
  async exportTextureLayer(designLayer, designGroup) {
    if (!designLayer) throw new Error('Design layer not found');
    
    
    
    // Resolve inclusion flags from config/variant/url
    let includeProductInTexture = true;
    let includeCaseInTexture = true;
    try {
      const cfg = this.configData;
      const selected = this.currentOptions || window.selectedOptions || {};
      const supported = cfg?.SupportedModels?.Options;
      const variants = Array.isArray(cfg?.variants) ? cfg.variants : null;
      const resolveSelectedForKey = (key) => {
        const byKey = selected?.[key];
        if (byKey != null) return (typeof byKey === 'object' && 'value' in byKey) ? byKey.value : byKey;
        const metaName = supported?.[key]?.name;
        if (metaName) {
          const byName = selected?.[metaName];
          if (byName != null) return (typeof byName === 'object' && 'value' in byName) ? byName.value : byName;
        }
        return undefined;
      };
      const variant = variants?.find(v => {
        for (const k of Object.keys(v)) {
          if (k === 'DesignProduct' || k === 'ExportIncludeCase' || k === 'ExportTextureIncludeCase' || k === 'ExportTextureIncludeProduct' || k === 'price' || k === 'id') continue;
          const expected = v[k];
          if (expected == null || String(expected).trim() === '') continue;
          const sel = resolveSelectedForKey(k);
          if (sel == null) continue;
          if (String(sel).toLowerCase() !== String(expected).toLowerCase()) return false;
        }
        return true;
      }) || null;
      const viewId = this.getViewId ? this.getViewId(this.selectedView || window.selectedView) : (typeof this.selectedView === 'object' ? this.selectedView?.id : this.selectedView);
      const readBoolMap = (map, vid) => {
        if (!map || vid == null) return undefined;
        if (typeof map === 'boolean') return map;
        if (typeof map !== 'object') return undefined;
        const direct = map[vid];
        if (typeof direct === 'boolean') return direct;
        const vidLower = String(vid).toLowerCase();
        for (const k of Object.keys(map)) {
          if (String(k).toLowerCase() === vidLower && typeof map[k] === 'boolean') return map[k];
        }
        return undefined;
      };
      let prodFlag = readBoolMap(variant?.ExportTextureIncludeProduct, viewId);
      if (prodFlag === undefined) prodFlag = readBoolMap(cfg?.ExportTextureIncludeProduct, viewId);
      if (typeof prodFlag === 'boolean') includeProductInTexture = prodFlag;
      let caseFlag = readBoolMap(variant?.ExportTextureIncludeCase, viewId);
      if (caseFlag === undefined) caseFlag = readBoolMap(cfg?.ExportTextureIncludeCase, viewId);
      if (typeof caseFlag === 'boolean') includeCaseInTexture = caseFlag;
      try {
        const qp = new URLSearchParams(window.location.search);
        const p1 = qp.get('includeProductTexture');
        const p2 = qp.get('includeCaseTexture');
        if (p1 != null) includeProductInTexture = /^(1|true)$/i.test(p1);
        if (p2 != null) includeCaseInTexture = /^(1|true)$/i.test(p2);
      } catch {}
    } catch {}
    // Temporarily hide transformers and delete buttons before export (do not destroy)
    const selectedBefore = (typeof window !== 'undefined') ? window.selectedKonvaNode : null;
    const transformers = designLayer.find('Transformer');
    const deleteButtons = designLayer.find('.delete-button-group');
    const transformerStates = transformers.map(t => ({ node: t, wasVisible: t.visible() }));
    const deleteBtnStates = deleteButtons.map(b => ({ node: b, wasVisible: b.visible() }));
    transformerStates.forEach(({ node }) => node.visible(false));
    deleteBtnStates.forEach(({ node }) => node.visible(false));

    // Store all product-related nodes and their visibility states
    const productNodes = [];
    
    // Find and store product image visibility state
    const productImage = designLayer.findOne(node => node.name() === 'product-image');
    if (productImage) {
      const wasVisible = productImage.visible();
      productNodes.push({ node: productImage, wasVisible: wasVisible });
      
      // Control product image visibility based on config
      productImage.visible(!!includeProductInTexture);
      
    }
    
    // Handle product images in the design group
    designGroup.find('Image').forEach(node => {
      if (node.name() === 'product-image' || node.name().includes('product')) {
        // Record current state for restoration later
        productNodes.push({ node, wasVisible: node.visible() });
        node.visible(!!includeProductInTexture);
      }
    });
    
    // Find case image if it exists
    const overlayLayer = this.layers['Overlay'] || designLayer;
    const caseImage = overlayLayer.findOne(node => node.name && node.name() === 'case-image') || designLayer.findOne(node => node.name && node.name() === 'case-image');
    if (caseImage && includeCaseInTexture) {
      // For texture export, we always want the case image visible and on top
      const wasCaseVisible = caseImage.visible();
      caseImage.visible(true);
      caseImage.moveToTop();
      
      
      // Restore visibility after export if needed
      setTimeout(() => {
        caseImage.visible(wasCaseVisible);
      }, 100);
    }
    
    // Get mask image for export dimensions
    const maskImage = designLayer.findOne(node => node.name() === 'mask-image');
    if (!maskImage) {
      // Restore product image visibility if we changed it
      productNodes.forEach(item => {
        if (item.node) {
          item.node.visible(item.wasVisible);
        }
      });
      throw new Error('Mask image not found');
    }
    
    const maskElement = maskImage.image();
    if (!maskElement) throw new Error('Mask image element not found');
    
    // Use original mask dimensions for export
    const exportWidth = maskElement.width;
    const exportHeight = maskElement.height;
    
    // Create a temporary stage with exact export dimensions
    const tempStage = new Konva.Stage({
      width: exportWidth,
      height: exportHeight,
      container: document.createElement('div') // Off-screen
    });
    
    const tempLayer = new Konva.Layer();
    tempStage.add(tempLayer);
    
    // Clone the design group
    const clonedGroup = designGroup.clone();
    clonedGroup.getChildren().forEach(n => {
      try { if (typeof n.name === 'function' && n.name() === 'hint-layer') n.destroy(); } catch (_) {}
    });

    // Ensure uploaded-image nodes are kept (they are the actual uploads)
    clonedGroup.find('Image').forEach(node => {
      const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
      if (nm === 'uploaded-image') {
        node.visible(true);
      }
      // Ensure case image is visible and on top
      if (nm === 'case-image') {
        node.visible(true);
        node.moveToTop();
      }
    });

    // Include region-group visual nodes (sticker-visual) so stickers appear in 3D
    clonedGroup.getChildren().forEach(child => {
      const childName = typeof child.name === 'function' ? child.name() : (child.attrs && child.attrs.name);
      if (childName && childName.startsWith('region-group-')) {
        child.visible(true);
        // Ensure visual nodes inside are visible
        child.getChildren().forEach(vn => {
          const vnName = typeof vn.name === 'function' ? vn.name() : (vn.attrs && vn.attrs.name);
          if (vnName === 'sticker-visual') {
            vn.visible(true);
          }
        });
      }
    });
    
    // Position and scale the design group to match the mask
    clonedGroup.position({
      x: -maskImage.x() * (exportWidth / maskImage.width()),
      y: -maskImage.y() * (exportHeight / maskImage.height())
    });
    
    clonedGroup.scale({
      x: exportWidth / maskImage.width(),
      y: exportHeight / maskImage.height()
    });
    
    tempLayer.add(clonedGroup);
    
    // If case image exists on overlay/design layer and should be included, clone it onto tempLayer above group
    if (includeCaseInTexture && caseImage) {
      const caseClone = caseImage.clone({ visible: true });
      const scaleX = exportWidth / maskImage.width();
      const scaleY = exportHeight / maskImage.height();
      caseClone.position({
        x: -maskImage.x() * scaleX + caseImage.x() * scaleX,
        y: -maskImage.y() * scaleY + caseImage.y() * scaleY
      });
      caseClone.scale({
        x: (typeof caseClone.scaleX === 'function' ? caseClone.scaleX() : (caseClone.attrs && caseClone.attrs.scaleX) || 1) * scaleX,
        y: (typeof caseClone.scaleY === 'function' ? caseClone.scaleY() : (caseClone.attrs && caseClone.attrs.scaleY) || 1) * scaleY
      });
      tempLayer.add(caseClone);
      try { caseClone.moveToTop(); } catch (_) {}
    }
    
    try {
      // Export with high quality settings
      const dataUrl = await new Promise((resolve) => {
        tempLayer.toDataURL({
          mimeType: 'image/png',
          quality: 1.0,
          width: exportWidth,
          height: exportHeight,
          callback: (url) => {
            tempStage.destroy();
            resolve(url);
          }
        });
      });
      return dataUrl;
    } finally {
      // Restore transformer / delete button visibility
      try {
        transformerStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
        deleteBtnStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
      } catch (_) {}
      // Best-effort: re-attach transformer to selected node if it was lost
      try {
        if (selectedBefore && selectedBefore.getLayer && selectedBefore.getLayer()) {
          const hasTransformer = designLayer.find('Transformer').length > 0;
          if (!hasTransformer) {
            this.addTransform(selectedBefore, 'Design');
          }
        }
      } catch (_) {}

      // Always restore all product images visibility
      productNodes.forEach(item => {
        if (item.node) {
          item.node.visible(item.wasVisible);
        }
      });
      
    }
  }
  
  /**
   * Export the design layer as a data URL
   * @param {Object} designLayer - The design layer to export
   * @param {Object} designGroup - The design group containing the design elements
   * @param {boolean} showDesignOverlay - Whether to show the Design.png overlay in the export (default: false for transparent background)
   * @param {boolean} showProductImage - Whether to show the product image in the export
   * @returns {Promise<string>} Promise that resolves with the data URL of the exported image
   */
  async exportDesignLayer(designLayer, designGroup, showDesignOverlay = false, showProductImage = false, applyMask = true, includeCaseInDesign = true) {
    if (!designLayer) throw new Error('Design layer not found');

    

    // Temporarily hide transformers and delete buttons before export (do not destroy)
    const selectedBefore = (typeof window !== 'undefined') ? window.selectedKonvaNode : null;
    const transformers = designLayer.find('Transformer');
    const deleteButtons = designLayer.find('.delete-button-group');
    const transformerStates = transformers.map(t => ({ node: t, wasVisible: t.visible() }));
    const deleteBtnStates = deleteButtons.map(b => ({ node: b, wasVisible: b.visible() }));
    transformerStates.forEach(({ node }) => node.visible(false));
    deleteBtnStates.forEach(({ node }) => node.visible(false));

    // Store all product-related nodes and their visibility states
    const productNodes = [];

    const productImage = designLayer.findOne(node => node.name() === 'product-image');
    if (productImage) {
      // Store the current visibility state
      const wasVisible = productImage.visible();
      productNodes.push({ node: productImage, wasVisible: wasVisible });
      
      // Control product image visibility based on showProductImage parameter
      productImage.visible(!!showProductImage);
      
    } else {
      console.warn('Product image not found in design layer during export');
    }

    // Ensure case image is tracked and visible for export
    const overlayLayer = this.layers['Overlay'] || designLayer;
    const caseImage = overlayLayer.findOne(node => node.name && node.name() === 'case-image') || designLayer.findOne(node => node.name && node.name() === 'case-image');
    if (caseImage) {
      const wasCaseVisible = caseImage.visible();
      productNodes.push({ node: caseImage, wasVisible: wasCaseVisible });
    }

    const designImage = designGroup.findOne(node => node.name() === 'design-image');
    if (designImage) {
      // Only show design image if showDesignOverlay is true
      
      designImage.visible(showDesignOverlay);
    } else if (showDesignOverlay) {
      
    }

    // Handle product images in the design group
    designGroup.find('Image').forEach(node => {
      if (node.name() === 'product-image' || node.name().includes('product')) {
        productNodes.push({ node, wasVisible: node.visible() });
      }
    });

    // Get mask image for export dimensions
    const maskImage = designLayer.findOne(node => node.name() === 'mask-image');
    if (!maskImage) {
      // Restore product image visibility if we changed it
      productNodes.forEach(item => {
        if (item.node) {
          item.node.visible(item.wasVisible);
        }
      });
      throw new Error('Mask image not found');
    }

    const maskElement = maskImage.image();
    if (!maskElement) throw new Error('Mask image element not found');

    // Use original mask dimensions for export
    const exportWidth = maskElement.width;
    const exportHeight = maskElement.height;

    // Calculate scale factors
    const scaleX = exportWidth / maskImage.width();
    const scaleY = exportHeight / maskImage.height();

    // Create a temporary stage with exact export dimensions
    const tempStage = new Konva.Stage({
      width: exportWidth,
      height: exportHeight,
      container: document.createElement('div') // Off-screen
    });

    const tempLayer = new Konva.Layer();
    tempStage.add(tempLayer);

    // Sync all visual nodes before cloning
    console.log('[exportDesignLayer] Syncing all visual nodes before export');
    this.design_group.find('Image').forEach(node => {
      if (node.name && node.name() === 'uploaded-image' && node.syncVisual && typeof node.syncVisual === 'function') {
        console.log('[exportDesignLayer] Syncing control node:', {
          nodeId: node.id && node.id(),
          position: { x: node.x(), y: node.y() },
          scale: { x: node.scaleX(), y: node.scaleY() }
        });
        node.syncVisual();
      }
    });

    // Clone the design group
    const clonedGroup = designGroup.clone();
    clonedGroup.getChildren().forEach(n => {
      try { if (typeof n.name === 'function' && n.name() === 'hint-layer') n.destroy(); } catch (_) {}
    });

    // Debug: Check what's in the cloned group
    const clonedChildren = clonedGroup.getChildren().map(n => ({
      name: n.name && n.name(),
      type: n.className,
      visible: n.visible && n.visible(),
      children: n.getChildren ? n.getChildren().length : 0
    }));
    console.log('[exportDesignLayer] Cloned group children:', clonedChildren);
    
    // Check if region-group-center exists
    const centerGroup = clonedGroup.getChildren().find(child => 
      child.name && child.name() === 'region-group-center'
    );
    console.log('[exportDesignLayer] region-group-center exists:', !!centerGroup);
    if (centerGroup) {
      console.log('[exportDesignLayer] center group details:', {
        name: centerGroup.name && centerGroup.name(),
        visible: centerGroup.visible && centerGroup.visible(),
        children: centerGroup.getChildren ? centerGroup.getChildren().length : 0
      });
    }

    // Debug: Check region groups and their visual nodes
    clonedGroup.getChildren().forEach(child => {
      const childName = child.name && child.name();
      if (childName && childName.startsWith('region-group-')) {
        const visualNodes = child.getChildren ? child.getChildren() : [];
        console.log('[exportDesignLayer] Region group in clone:', {
          groupName: childName,
          visible: child.visible && child.visible(),
          visualNodeCount: visualNodes.length,
          visualNodes: visualNodes.map(vn => ({
            id: vn.id && vn.id(),
            name: vn.name && vn.name(),
            visible: vn.visible && vn.visible(),
            position: { x: vn.x(), y: vn.y() }
          }))
        });
      }
    });

    // Check what regions are assigned to the control node
    const controlNode = this.design_group.findOne('#img-1768863679415');
    if (controlNode) {
      console.log('[exportDesignLayer] Control node regions:', {
        nodeId: controlNode.id && controlNode.id(),
        regions: controlNode.getAttr('region'),
        regionType: typeof controlNode.getAttr('region')
      });
    }

    clonedGroup.find('Image').forEach(node => {
      // Ensure design image is always visible
      if (node.name() === 'design-image') {
        node.visible(true);
      }
      // Always export uploaded-image nodes.
      // In normal interaction mode, region-linked uploaded images may hide the control node via a custom sceneFunc
      // and render via derived visuals. During export we want the image pixels, so we force visibility and
      // restore default drawing.
      if (node.name() === 'uploaded-image') {
        try { node.visible(true); } catch (_) {}
        try {
          if (typeof node.sceneFunc === 'function') {
            node.sceneFunc(null);
          }
          delete node.sceneFunc;
        } catch (_) {}
      }
    });

    // Position and scale the design group to match the mask
    clonedGroup.position({
      x: -maskImage.x() * scaleX,
      y: -maskImage.y() * scaleY
    });

    clonedGroup.scale({ x: scaleX, y: scaleY });

    // Apply clipping to the design group
    clonedGroup.clip({
      x: 0,
      y: 0,
      width: exportWidth,
      height: exportHeight
    });

    // Apply ClipRegions region clipping (match preview behavior)
    try {
      const viewId = this.getViewId(this.selectedView);
      const views = this.configData?.SupportedModels?.CommonProperties?.Views;
      const viewCfg = Array.isArray(views) ? views.find(v => v.id === viewId) : null;
      const clipRegions = viewCfg?.ClipRegions;

      console.log('[exportDesignLayer] ClipRegions for view', viewId, clipRegions);

      // IMPORTANT: compute ratio/pos from current product image node to avoid stale values after view switch
      const computeStageTransform = () => {
        try {
          const imgEl = productImage && typeof productImage.image === 'function' ? productImage.image() : null;
          const naturalH = imgEl && imgEl.height ? imgEl.height : null;
          const renderedH = productImage && typeof productImage.height === 'function'
            ? (productImage.height() * (typeof productImage.scaleY === 'function' ? productImage.scaleY() : 1))
            : null;
          if (naturalH && renderedH) {
            return {
              ratio: renderedH / naturalH,
              pos: {
                x: (typeof productImage.x === 'function') ? productImage.x() : 0,
                y: (typeof productImage.y === 'function') ? productImage.y() : 0
              }
            };
          }
        } catch (_) {}
        return {
          ratio: this._productImageRatio || 1,
          pos: this._productImagePosition || { x: 0, y: 0 }
        };
      };

      const { ratio, pos } = computeStageTransform();

      console.log('[exportDesignLayer] ratio/pos/maskY/scaleY', { ratio, posY: pos.y, maskY: maskImage.y(), scaleY });

      // Remove control nodes (unclipped interaction nodes) from export
      // IMPORTANT: Do NOT remove uploaded-image nodes when exporting for 3D preview texture.
      // These are needed so user uploads appear in 3D.
      try {
        let removed = 0;
        clonedGroup.find('Image').forEach(n => {
          try {
            const nm = (typeof n.name === 'function') ? n.name() : null;
            // Only remove generic image-node; keep uploaded-image for 3D texture visibility.
            if (nm === 'image-node') {
              n.destroy();
              removed += 1;
            }
          } catch (_) {}
        });
        try {
          const childNames = clonedGroup.getChildren().map(n => {
            try { return (typeof n.name === 'function') ? n.name() : ''; } catch (_) { return ''; }
          });
          console.log('[afterRemove] removedControlImages:', removed);
          console.log('[afterRemove] clonedGroup children (after remove):', childNames);
        } catch (_) {}
      } catch (_) {}

      // Apply clipFunc to each region group in clonedGroup
      if (clipRegions) {
        clonedGroup.getChildren().forEach(child => {
          try {
            const childName = (typeof child.name === 'function') ? child.name() : null;
            if (!childName || !childName.startsWith('region-group-')) return;

            // Make region group visible
            child.visible(true);

            const regionKey = (Array.isArray(child.getAttr('region')) && child.getAttr('region').length > 0)
              ? child.getAttr('region')[0]
              : childName.replace('region-group-', '');

            const cfg = clipRegions[regionKey];
            const range = Array.isArray(cfg) ? cfg : cfg?.range;
            if (!range || range.length !== 2) return;
            const start = Number(range[0]);
            const end = Number(range[1]);
            if (!Number.isFinite(start) || !Number.isFinite(end)) return;

            // Convert product-space -> stage-space
            const stageStartY = (start * ratio) + pos.y;
            const stageEndY = (end * ratio) + pos.y;

            // Convert stage-space -> clonedGroup local coords (pre-scale)
            const localStartY = stageStartY - maskImage.y();
            const localHeight = stageEndY - stageStartY;

            const childY = (typeof child.y === 'function') ? child.y() : (child.attrs && child.attrs.y) || 0;
            const childLocalStartY = localStartY - childY;

            // Make all visual nodes in this region group visible and sync them
            child.getChildren().forEach(vn => {
              if (vn.name && vn.name() === 'sticker-visual') {
                vn.visible(true);
                
                // Find the control node for this visual node
                const controlNodeId = vn.id && vn.id().replace(/-visual-[^-]*$/, '');
                const controlNode = clonedGroup.findOne(`#${controlNodeId}`);
                
                if (controlNode) {
                  // Sync visual node with control node position
                  vn.setAttrs({
                    x: controlNode.x(),
                    y: controlNode.y(),
                    scaleX: controlNode.scaleX(),
                    scaleY: controlNode.scaleY(),
                    rotation: controlNode.rotation(),
                    opacity: controlNode.opacity(),
                    skewX: controlNode.skewX(),
                    skewY: controlNode.skewY()
                  });
                  
                  console.log('[exportDesignLayer] Synced visual node:', {
                    visualNodeId: vn.id && vn.id(),
                    controlNodeId: controlNodeId,
                    controlNodePos: { x: controlNode.x(), y: controlNode.y() },
                    visualNodePos: { x: vn.x(), y: vn.y() },
                    scale: { x: vn.scaleX(), y: vn.scaleY() },
                    region: regionKey
                  });
                }
              }
            });

            console.log('[exportDesignLayer] region-group clip', {
              childName,
              regionKey,
              productRange: [start, end],
              stageRange: [stageStartY, stageEndY],
              clonedGroupLocal: [localStartY, localStartY + localHeight],
              childY,
              childLocal: [childLocalStartY, childLocalStartY + localHeight],
              visualNodeCount: child.getChildren().filter(vn => vn.name && vn.name() === 'sticker-visual').length
            });

            child.clipFunc((ctx) => {
              ctx.rect(-10000, childLocalStartY, 20000, localHeight);
            });
          } catch (_) {}
        });
      } else {
      }
    } catch (_) {}

    tempLayer.add(clonedGroup);
    
    // Add the mask for final clipping if applyMask is true
    if (applyMask) {
      const maskClone = maskImage.clone({
        width: exportWidth,
        height: exportHeight,
        x: 0,
        y: 0
      });
      maskClone.globalCompositeOperation = 'destination-in';
      tempLayer.add(maskClone);
    }

    // Add case image on top of masked design (unmasked)
    if (caseImage && includeCaseInDesign) {
      const scaleXCase = scaleX;
      const scaleYCase = scaleY;
      const caseClone = caseImage.clone({ visible: true });
      caseClone.position({
        x: -maskImage.x() * scaleXCase + caseImage.x() * scaleXCase,
        y: -maskImage.y() * scaleYCase + caseImage.y() * scaleYCase
      });
      caseClone.scale({
        x: (typeof caseClone.scaleX === 'function' ? caseClone.scaleX() : (caseClone.attrs && caseClone.attrs.scaleX) || 1) * scaleXCase,
        y: (typeof caseClone.scaleY === 'function' ? caseClone.scaleY() : (caseClone.attrs && caseClone.attrs.scaleY) || 1) * scaleYCase
      });
      tempLayer.add(caseClone);
    }

    

    try {
      // Export with high quality settings
      const dataUrl = await new Promise((resolve) => {
        tempLayer.toDataURL({
          mimeType: 'image/png',
          quality: 1.0,
          width: exportWidth,
          height: exportHeight,
          callback: (url) => {
            try {
              // Use changedpi to set the DPI metadata
              const highDpiUrl = changeDpiDataUrl(url, 300);
              tempStage.destroy();
              resolve(highDpiUrl);
            } catch (error) {
              console.error('Error setting DPI:', error);
              // Fallback to original URL if DPI setting fails
              tempStage.destroy();
              resolve(url);
            }
          }
        });
      });
      return dataUrl;
    } finally {
      // Restore transformer / delete button visibility on the LIVE stage.
      // This is critical for keeping selection handles stable while 3D live-sync exports run.
      try {
        transformerStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
        deleteBtnStates.forEach(({ node, wasVisible }) => node.visible(wasVisible));
      } catch (_) {}

      try {
        if (selectedBefore && selectedBefore.getLayer && selectedBefore.getLayer()) {
          const hasTransformer = designLayer.find('Transformer').length > 0;
          if (!hasTransformer) {
            this.addTransform(selectedBefore, 'Design');
          }
        }
      } catch (_) {}

      try {
        if (this.stage && typeof this.stage.batchDraw === 'function') {
          this.stage.batchDraw();
        }
      } catch (_) {}

      // Always restore all product images visibility
      productNodes.forEach(item => {
        if (item.node) {
          item.node.visible(item.wasVisible);
        }
      });
      
    }
  }
  /**
   * Move the selected node up in the z-index (visually forward)
   */


  moveUp() {
    const selectedNode = this.getSelectedNode();
    if (selectedNode) {
      selectedNode.moveUp();
      this.stage.batchDraw();
    }
  }

  /**
   * Move the selected node down in the z-index (visually backward)
   */
  moveDown() {
    const selectedNode = this.getSelectedNode();
    if (selectedNode) {
      // Set global reference to the selected node
      window.selectedKonvaNode = selectedNode;
      selectedNode.moveDown();
      this.stage.batchDraw();
    }
  }



  /**
   * Legacy method for exporting a preview image (kept for backward compatibility)
   * @deprecated Use the enhanced exportPreview method at line ~1511 instead
   * @returns {Promise<string>} Promise that resolves with the data URL of the preview image
   */
  async legacyExportPreview() {
    

    // Hide any transformers before export
    const transformers = this.stage.find('Transformer');
    transformers.forEach(tr => tr.visible(false));

    // Make sure delete buttons are hidden for the export
    const deleteButtons = this.stage.find('.delete-button-group');
    deleteButtons.forEach(btn => btn.visible(false));

    try {
      // Get the data URL directly from the stage
      return this.stage.toDataURL({
        mimeType: 'image/png',
        quality: 0.8, // Lower quality is fine for preview
        pixelRatio: 1.5 // Reasonable resolution for preview
      });
    } finally {
      // Restore visibility of UI elements
      transformers.forEach(tr => tr.visible(true));
      deleteButtons.forEach(btn => btn.visible(true));

      // Redraw the stage to restore UI
      this.stage.batchDraw();
    }
  }


  /**
   * Save the current design to localStorage using view and global options as the key
   * @param {string|object} view - View name (front, back, etc.) or view object with id property
   */
  async saveDesign(view) {
    try {
      // Extract view ID from view object or string
      const viewId = this.getViewId(view);
      
      // Use global options instead of passed parameter
      const globalOptions = window.selectedOptions || {};
      console.log('💾 Saving design with global options:', globalOptions);
      
      // Process global options to ensure it's a proper string
      // Only include options tagged as isVisualOption === true in the config
      let options_str = '';
      if (globalOptions && Object.keys(globalOptions).length > 0) {
        const optionsCfg = (this.configData && this.configData.SupportedModels && this.configData.SupportedModels.Options) || {};
        const resolveMetaForKey = (k) => {
          // Direct key (e.g., Option6)
          let meta = optionsCfg[k];
          if (meta) return meta;
          // Match by name (e.g., P_packaging)
          const matchKey = Object.keys(optionsCfg).find(x => optionsCfg[x] && optionsCfg[x].name === k);
          return matchKey ? optionsCfg[matchKey] : null;
        };

        const optionsArray = [];
        for (const key in globalOptions) {
          const meta = resolveMetaForKey(key);
          const isVisual = meta && meta.isVisualOption === true; // strictly visual options only
          if (!isVisual) continue;
          const valObj = globalOptions[key];
          const value = (valObj && typeof valObj === 'object' && 'value' in valObj) ? valObj.value : valObj;
          if (value != null && value !== '') {
            optionsArray.push(String(value));
          }
        }

        options_str = optionsArray.join('_');
      }

      // Use current productId from controller
      const productId = this.productId;
      if (!productId) {
        console.warn('No product ID available for saving design');
        return;
      }
      
      // Store the current view for future reference
      this.selectedView = viewId;
      const designIndex = this.getDesignIndexForView(viewId);
      const key = `${viewId}_${designIndex}`;
      
      // Ensure design group exists
      if (!this.design_group) {
        console.warn('No design group found, nothing to save');
        return;
      }

      // Serialize design group and exclude derived nodes
      const designData = this.design_group.toObject();
      const childrenArr = Array.isArray(designData.children) ? designData.children : [];
      // Exclude base product-image and variant-design-image (derived)
      designData.children = childrenArr.filter((child) => {
        const attrs = child && child.attrs ? child.attrs : {};
        const id = attrs.id;
        const nm = attrs.name;
        if ((id === 'product-image' || nm === 'product-image') || nm === 'variant-design-image') return false;
        // Region derived visuals (rebuild on load)
        if (typeof nm === 'string' && nm.startsWith('region-group-')) return false;
        if (nm === 'sticker-visual') return false;
        return true;
      });

      // Prepare filtered live nodes aligned with serialized children (same exclusions)
      const rawNodes = this.design_group.getChildren();
      const nodesArr = typeof rawNodes.toArray === 'function' ? rawNodes.toArray() : Array.from(rawNodes || []);
      const filteredNodes = nodesArr.filter((node) => {
        try {
          const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
          const id = typeof node.id === 'function' ? node.id() : (node.attrs && node.attrs.id);
          if ((id === 'product-image' || nm === 'product-image') || nm === 'variant-design-image') return false;
          if (typeof nm === 'string' && nm.startsWith('region-group-')) return false;
          if (nm === 'sticker-visual') return false;
          return true;
        } catch (_) {
          return true;
        }
      });

      // Add extra attributes to serialized children
      if (Array.isArray(designData.children)) {
        designData.children.forEach((childObj, idx) => {
          const node = filteredNodes[idx];
          if (node) {
            childObj.attrs = childObj.attrs || {};
            // Persist zIndex (fallbacks for safety)
            try {
              childObj.attrs.zIndex = typeof node.zIndex === 'function' ? node.zIndex() : (typeof node.index === 'function' ? node.index() : idx);
            } catch (_) {
              childObj.attrs.zIndex = idx;
            }

            try {
              const c = this.getNodeVisualCenter(node);
              if (c) {
                const productPt = this.stagePointToProductPx(c.x, c.y);
                if (productPt) {
                  childObj.attrs.snapPx = productPt.x;
                  childObj.attrs.snapPy = productPt.y;
                }
              }
            } catch (_) {}

            // If image, persist a source URL hint
            if (node.className === 'Image') {
              try {
                const img = typeof node.image === 'function' ? node.image() : null;
                const nodeImageUrl = node && node.attrs
                  ? (node.attrs.imageUrl || node.attrs.src || '')
                  : '';
                const src = (img && img.src) ? String(img.src) : '';
                if (src && !src.startsWith('blob:')) {
                  childObj.attrs.imageUrl = src;
                } else if (nodeImageUrl && !String(nodeImageUrl).startsWith('blob:')) {
                  childObj.attrs.imageUrl = String(nodeImageUrl);
                } else if (src) {
                  // Best-effort fallback (may still be blob:)
                  childObj.attrs.imageUrl = src;
                }
              } catch (_) {}
            }

            // Persist region for region-linked uploaded images (control nodes)
            try {
              const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
              if (nm === 'uploaded-image') {
                const r = (node.getAttr && node.getAttr('region')) || (node.attrs && node.attrs.region);
                if (r != null) childObj.attrs.region = r;
              }
            } catch (_) {}
          }
        });
      }

      const designJson = JSON.stringify(designData);

      localStorage.setItem(key.toUpperCase(), designJson);
      console.log('💾 Design saved successfully (excluding derived variant layer)');
      
    } catch (error) {
      console.error('Error saving design:', error);
    }
  }
      
  /**
   * Load a saved design from localStorage using view and global options as the key
   * @param {string|object} view - View name (front, back, etc.) or view object with id property
   */
  async loadDesign(view) {
    // Always clear the design group before loading a new design
    // Check if stage exists
    if (!this.stage) {
      console.error('loadDesign: Stage not initialized');
      return;
    }
    
    // Get the design layer
    const designLayer = this.layers['Design'];
    if (!designLayer) {
      console.error('loadDesign: Design layer not found');
      return;
    }
    
    try {
      // No default values - must have actual values
      if (!view) {
        console.error('loadDesign: View parameter is required');
        return;
      }
      
      // Extract view ID from view object or string
      const viewId = this.getViewId(view);
      
      // Use current productId from controller
      const productId = this.productId;
      if (!productId) {
        console.warn('No product ID available for loading design');
        return;
      }
      
      // Store the current view for future reference
      this.selectedView = viewId;
      
      // Use global options instead of passed parameter
      const globalOptions = window.selectedOptions || {};
      console.log('📂 Loading design with global options:', globalOptions);

      // Ensure variant-specific DesignProduct image is updated for this view/index
      // Do this early so user elements loaded below will stack above it.
      await this.updateVariantDesignLayer(viewId);

      // IMPORTANT: Export All may call loadDesign multiple times.
      // To avoid duplicating user nodes / derived region visuals, clear existing user content
      // while keeping base assets.
      try {
        this.resetDesign({
          keepBase: true,
          baseNames: ['product-image', 'case-image', 'mask-image', 'background-color', 'background-image', 'hint-layer']
        });
      } catch (_) {}
      
      // Process global options to ensure it's a proper string
      // Only include options tagged as isVisualOption === true in the config
      if (globalOptions && Object.keys(globalOptions).length > 0) {
        const optionsCfg = (this.configData && this.configData.SupportedModels && this.configData.SupportedModels.Options) || {};
        const resolveMetaForKey = (k) => {
          // Direct key (e.g., Option6)
          let meta = optionsCfg[k];
          if (meta) return meta;
          // Match by name (e.g., P_packaging)
          const matchKey = Object.keys(optionsCfg).find(x => optionsCfg[x] && optionsCfg[x].name === k);
          return matchKey ? optionsCfg[matchKey] : null;
        };

        const optionsArray = [];
        for (const key in globalOptions) {
          const meta = resolveMetaForKey(key);
          const isVisual = meta && meta.isVisualOption === true; // strictly visual options only
          if (!isVisual) continue;
          const valObj = globalOptions[key];
          const value = (valObj && typeof valObj === 'object' && 'value' in valObj) ? valObj.value : valObj;
          if (value != null && value !== '') {
            optionsArray.push(String(value));
          }
        }
        // options_string was computed but not used, removed to fix ESLint warning
      }
      
      
      
      // Create keys for localStorage (index-aware and legacy without index)
      const designIndex = this.getDesignIndexForView(viewId);
      const keyWithIndex = `${viewId}_${designIndex}`;
      const key = `${viewId}_${designIndex}`;

      // Avoid duplicating user nodes: if the exact same design key was just loaded, skip restore
      // This commonly happens when toggling non-visual options (e.g., box yes/no) that don't change options_string
      if (!this._lastLoadedDesignKey) this._lastLoadedDesignKey = {};
      const viewCacheKey = String(viewId);
      // Do not skip while exporting; we need to rehydrate nodes after clearing layers
      const isExporting = typeof window !== 'undefined' && window.__isExporting === true;
      if (!isExporting && this._lastLoadedDesignKey[viewCacheKey] === keyWithIndex) {
        console.log('⏭️ loadDesign: same design key already loaded for view, skipping duplicate restore:', keyWithIndex);
        // Still ensure the layer is drawn in case variant/base assets changed above
        designLayer.draw();
        return;
      }
      
      let savedDesign = localStorage.getItem(key);      
      // Initialize designData variable outside the if block
      let designData = null;
      
      if (!savedDesign) {
        // No saved user design. Variant layer already updated above.
        return;
      } else {
        // Parse the saved design - handle both single and double JSON parsing cases
        try {
          // Try parsing twice (for backward compatibility)
          designData = JSON.parse(JSON.parse(savedDesign));
        } catch (e) {
          // If that fails, try parsing once
          try {
            designData = JSON.parse(savedDesign);
          } catch (e2) {
            console.error('Error parsing saved design:', e2);
            return;
          }
        }
      }
      
      
      
      // Debug: Check node properties after loading
      setTimeout(() => {
        this.design_group.getChildren().forEach(node => {
          if (node.name() === 'uploaded-image' || node.name().startsWith('img-')) {
          }
        });
      }, 100);
      this.ensureDesignGroup(designLayer);

      if (designData && designData.children) {
        // Create a promise array to track all image loads
        const loadPromises = [];
        
        // Iterate through children and add them to the design group
        // Track created nodes for zIndex restoration
        const createdNodes = [];
        designData.children.forEach(child => {
          let nodeInstance = null;
          if (child.className === 'Text') {
            const text = new Konva.Text(child.attrs);
            // Reset offset to center on current text size
            const w = text.width();
            const h = text.height();
            text.offsetX(w / 2);
            text.offsetY(h / 2);
            // Position using saved absolute product coordinates
            if (child.attrs.snapPx != null && child.attrs.snapPy != null) {
              const stagePt = this.productPxToStagePoint(child.attrs.snapPx, child.attrs.snapPy);
              if (stagePt) {
                text.position({ x: stagePt.x, y: stagePt.y });
              }
            }
            this.design_group.add(text);
            nodeInstance = text;
            // Add click handler to select the text and show transformer
            text.on('click tap', (e) => {
              e.cancelBubble = true; // Prevent event from reaching stage
              this.addTransform(text, 'Design');
            });
            // Track for zIndex restoration
            if (nodeInstance) {
              createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
            }
          } else if (child.className === 'Rect') {
            // Restore Rect nodes (e.g., background-color)
            const rect = new Konva.Rect(child.attrs || {});
            this.design_group.add(rect);
            nodeInstance = rect;

            // Special handling for background color rect
            if (child.attrs && child.attrs.name === 'background-color') {
              try {
                // Ensure exclusivity: remove any background image
                this.removeBackgroundImage();

                // Position just above product image (Option A behavior)
                const productImage = this.design_group.findOne(n => n.name && n.name() === 'product-image');
                if (productImage) {
                  productImage.moveToBottom();
                  rect.moveToBottom();
                  rect.moveUp();
                } else {
                  rect.moveToBottom();
                }

                // Keep size in sync on stage resize (same as setBackground)
                if (!this._onResizeBgHandler) {
                  this._onResizeBgHandler = () => {
                    const r = this.design_group && this.design_group.findOne(n => n.name && n.name() === 'background-color');
                    if (r) {
                      r.width(this.stage.width());
                      r.height(this.stage.height());
                    }
                  };
                  this.stage.on('resize.backgroundColor', this._onResizeBgHandler);
                }
              } catch (e) {
                console.warn('Failed to restore background-color rect properties:', e);
              }
            }

            // Track for zIndex restoration
            if (nodeInstance) {
              createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
            }
          } else if (child.className === 'Image') {
            // Check for image URL in our custom attribute first
            // Always prefer the exported imageUrl if present, fallback to src
            let imageUrl = child.attrs && child.attrs.imageUrl ? child.attrs.imageUrl : child.attrs.src;

            // blob: URLs are ephemeral to a single browser session and cannot be restored reliably.
            // Skip them to avoid noisy console errors and allow load/export to proceed.
            if (imageUrl && String(imageUrl).startsWith('blob:')) {
              return;
            }

            // Make sure the URL is absolute if it's not a data URL
            if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
              // Convert relative URL to absolute
              const base = window.location.origin;
              imageUrl = new URL(imageUrl, base).href;
            }
            
            if (imageUrl) {              
              const normalizedUrl = this._normalizeImageUrl(imageUrl);
              // Create a promise for this image load
              const loadPromise = new Promise((resolve) => {
                const img = new Image();
                let objectUrlToRevoke = '';
                img.crossOrigin = 'Anonymous'; // Handle CORS if needed
                
                img.onload = () => {
                  try {
                    if (objectUrlToRevoke) {
                      URL.revokeObjectURL(objectUrlToRevoke);
                      objectUrlToRevoke = '';
                    }
                  } catch (_) {}
                  // Avoid Konva warning: Node has no parent. zIndex parameter is ignored.
                  const { zIndex: _z, ...attrsNoZ } = (child.attrs || {});
                  const konvaImage = new Konva.Image({
                    ...attrsNoZ,
                    image: img
                  });
                  // Position using saved absolute product coordinates
                  if (child.attrs.snapPx != null && child.attrs.snapPy != null) {
                    const stagePt = this.productPxToStagePoint(child.attrs.snapPx, child.attrs.snapPy);
                    if (stagePt) {
                      // Position by visual center
                      const rect = konvaImage.getClientRect({ skipShadow: true, skipStroke: true });
                      const visualCenterX = rect.x + rect.width / 2;
                      const visualCenterY = rect.y + rect.height / 2;
                      const dx = stagePt.x - visualCenterX;
                      const dy = stagePt.y - visualCenterY;
                      const abs = konvaImage.getAbsolutePosition();
                      konvaImage.setAbsolutePosition({ x: abs.x + dx, y: abs.y + dy });
                    }
                  }
                  this.design_group.add(konvaImage);
                  nodeInstance = konvaImage;

                  // If this is a region-linked uploaded image, rebuild the derived visual node
                  try {
                    const nm = (typeof konvaImage.name === 'function') ? konvaImage.name() : (konvaImage.attrs && konvaImage.attrs.name);
                    const regionAttr = (konvaImage.getAttr && konvaImage.getAttr('region')) || (konvaImage.attrs && konvaImage.attrs.region);
                    const regionKeys = Array.isArray(regionAttr)
                      ? regionAttr.filter(r => typeof r === 'string')
                      : (typeof regionAttr === 'string' ? [regionAttr] : []);
                    if (nm === 'uploaded-image' && regionKeys && regionKeys.length > 0) {
                      const createdPairs = [];
                      regionKeys.forEach(key => {
                        const visualNode = konvaImage.clone({
                          listening: false,
                          draggable: false,
                          name: 'sticker-visual',
                          id: 'visual-' + ((typeof konvaImage.id === 'function' ? konvaImage.id() : (konvaImage.attrs && konvaImage.attrs.id)) || Date.now()) + '-' + key
                        });
                        const regionGroup = this._ensureRegionGroup(key);
                        if (regionGroup) {
                          regionGroup.add(visualNode);
                          regionGroup.visible(true);
                          createdPairs.push({ visual: visualNode, group: regionGroup });
                        }
                      });

                      // Make control node visually invisible but still interactive (match addImage)
                      konvaImage.sceneFunc((ctx, shape) => {
                        ctx.beginPath();
                        ctx.rect(0, 0, shape.width(), shape.height());
                        ctx.closePath();
                        ctx.strokeShape(shape);
                      });
                      konvaImage.hitFunc((ctx, shape) => {
                        ctx.beginPath();
                        ctx.rect(0, 0, shape.width(), shape.height());
                        ctx.closePath();
                        ctx.fillStrokeShape(shape);
                      });

                      konvaImage.visualGroups = createdPairs;
                      const syncVisual = () => {
                        if (Array.isArray(konvaImage.visualGroups)) {
                          konvaImage.visualGroups.forEach(({ visual }) => {
                            if (!visual || typeof visual.getLayer !== 'function' || !visual.getLayer()) return;
                            visual.setAttrs({
                              x: konvaImage.x(),
                              y: konvaImage.y(),
                              scaleX: konvaImage.scaleX(),
                              scaleY: konvaImage.scaleY(),
                              rotation: konvaImage.rotation(),
                              width: konvaImage.width(),
                              height: konvaImage.height(),
                              offsetX: konvaImage.offsetX(),
                              offsetY: konvaImage.offsetY(),
                              skewX: konvaImage.skewX(),
                              skewY: konvaImage.skewY()
                            });
                            const layer = visual.getLayer();
                            if (layer && typeof layer.batchDraw === 'function') layer.batchDraw();
                          });
                        }
                      };
                      konvaImage.syncVisual = syncVisual;
                      konvaImage.on('dragmove transform', syncVisual);

                      const originalOpacity = konvaImage.opacity.bind(konvaImage);
                      konvaImage.opacity = function(val) {
                        if (val !== undefined) {
                          if (Array.isArray(this.visualGroups)) {
                            this.visualGroups.forEach(({ visual }) => {
                              if (visual && typeof visual.opacity === 'function') visual.opacity(val);
                            });
                          }
                          return originalOpacity(val);
                        }
                        return originalOpacity();
                      };

                      syncVisual();
                    }
                  } catch (_) {}
                  // Add click handler to select the image and show transformer
                  konvaImage.on('click tap', (e) => {
                    e.cancelBubble = true; // Prevent event from bubbling to the stage
                    this.addTransform(konvaImage, 'Design');
                  });
                  // Track for zIndex restoration
                  if (nodeInstance) {
                    createdNodes.push({ node: nodeInstance, zIndex: (child.attrs && typeof child.attrs.zIndex !== 'undefined') ? child.attrs.zIndex : null });
                  }
                  resolve();
                };
                
                img.onerror = (error) => {
                  console.error(`Failed to load image: ${normalizedUrl}`, error);
                  
                  // Try with a different crossOrigin setting as fallback
                  
                  img.crossOrigin = 'use-credentials';
                  
                  // Set a new error handler for the retry
                  img.onerror = (retryError) => {
                    console.error(`Retry failed for image: ${normalizedUrl}`, retryError);
                    
                    // As a last resort, try without crossOrigin
                    
                    img.crossOrigin = '';
                    
                    img.onerror = (finalError) => {
                      console.error(`All attempts failed for image: ${normalizedUrl}`);

                      // Final fallback: fetch as blob and load via object URL.
                      // This can bypass some hotlink / header / redirect issues.
                      (async () => {
                        try {
                          const res = await fetch(normalizedUrl, { method: 'GET' });
                          if (!res || !res.ok) {
                            resolve();
                            return;
                          }
                          const blob = await res.blob();
                          objectUrlToRevoke = URL.createObjectURL(blob);
                          img.onerror = () => {
                            try {
                              if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
                            } catch (_) {}
                            resolve();
                          };
                          img.src = objectUrlToRevoke;
                        } catch (_) {
                          resolve();
                        }
                      })();
                    };
                    
                    // Retry loading
                    img.src = normalizedUrl;
                  };
                  
                  // Retry loading
                  img.src = normalizedUrl;
                };
                
                img.src = normalizedUrl;
              });
              
              loadPromises.push(loadPromise);
            }
          }
        });
        
        // Wait for all images to load, then update the layer
        Promise.all(loadPromises).then(() => {

          // Restore stacking order for all nodes using saved zIndex
          if (createdNodes.some(n => n.zIndex !== null && typeof n.zIndex !== 'undefined')) {
            createdNodes
              .filter(n => n.node && typeof n.zIndex === 'number')
              .sort((a, b) => a.zIndex - b.zIndex)
              .forEach((n, idx) => {
                try {
                  n.node.zIndex(n.zIndex);
                } catch (e) {
                  // fallback: move to correct position
                  try { n.node.moveTo({ index: n.zIndex }); } catch (err) {}
                }
              });
            // Redraw to ensure order
            this.design_group.getLayer().batchDraw();
          }
          // 1. First, make sure the product image is at the bottom
          const productImage = designLayer.findOne(node => node.name() === 'product-image');
          if (productImage) {
            productImage.moveToBottom();
          }
          
          // 2. Make sure design group is above product image
          if (this.design_group) {
            this.design_group.moveUp();
          }
          
          // 3. Make sure mask is at the top
          const maskImage = designLayer.findOne(node => node.name() === 'mask-image');
           if (maskImage) {
             maskImage.moveToTop();
             
           } else {
             console.warn('No mask image found after loading design');
           }
           
           // 4. Ensure background color rect is just above the product image and remove any background image
           const bgRect = this.design_group && this.design_group.findOne(n => n.name && n.name() === 'background-color');
           if (bgRect) {
             try {
               // Exclusivity: remove background image if present
               this.removeBackgroundImage();

               const prodImg = this.design_group.findOne(n => n.name && n.name() === 'product-image');
               if (prodImg) {
                 prodImg.moveToBottom();
                 bgRect.moveToBottom();
                 bgRect.moveUp();
               } else {
                 bgRect.moveToBottom();
               }
             } catch (e) {
               console.warn('Failed to position background-color rect after load:', e);
             }
           }

           
           // Background images are already included in the children array, so we don't need to load them again from backgroundImages
           // This prevents duplication of background images
           if (designData.backgroundImages && Array.isArray(designData.backgroundImages)) {
             console.log(`ℹ️ Background images already loaded from children array, skipping backgroundImages array`);
            
            // For debugging only - log what would have been loaded
            if (this.debug) {
              console.log(`🔍 Debug: Found ${designData.backgroundImages.length} background images in backgroundImages array (not loading)`);
              designData.backgroundImages.forEach(bgImageData => {
                if (bgImageData.url) {
                  console.log(`🔍 Debug: Background image URL in array: ${bgImageData.url}`);
                }
              });
            }
          }
          
          // Reapply clip function to design group
          if (this._productImagePosition && this._productImageDimensions) {
            const { x, y } = this._productImagePosition;
            const { width, height } = this._productImageDimensions;
            
            this.design_group.clipFunc((ctx) => {
              ctx.rect(x, y, width, height);
            });
            
          }
          
          designLayer.batchDraw();

          // Mark this view's last loaded design key to avoid duplicate restores on identical calls
          try {
            if (!this._lastLoadedDesignKey) this._lastLoadedDesignKey = {};
            const viewCacheKey = String(viewId);
            this._lastLoadedDesignKey[viewCacheKey] = keyWithIndex;
          } catch (_) {}

          }).catch(error => {
            console.error('Error loading design images:', error);
          });
      } else {
        console.warn('No children found in design data');
      }
    } catch (error) {
      console.error('Error loading design:', error);
    }
  }



  /**
   * Helper method to get the currently selected node from the transformer
   */
  getSelectedNode() {
    if (!this.stage) {
      console.error('No stage found');
      return null;
    }

    const transformer = this.stage.findOne('Transformer');
    if (!transformer) {
      
      return null;
    }

    const nodes = transformer.nodes();
    if (!nodes || nodes.length === 0) {
      
      return null;
    }

    return nodes[0];
  }

  /**
   * Get all currently selected nodes
   */
  getSelectedNodes() {
    if (!this.stage) {
      console.error('No stage found');
      return [];
    }

    const transformer = this.stage.findOne('Transformer');
    if (!transformer) {
      return [];
    }

    const nodes = transformer.nodes();
    return nodes || [];
  }

  updateSelectedNodes() {
    console.log('[KonvaController] updateSelectedNodes: START');
    if (!this.stage) {
      console.log('[KonvaController] updateSelectedNodes: No stage');
      return;
    }
    
    const transformer = this.stage.findOne('Transformer');
    if (!transformer) {
      console.log('[KonvaController] updateSelectedNodes: No transformer');
      return;
    }
    
    this._selectedNodes = transformer.nodes();
    console.log('[KonvaController] updateSelectedNodes: Found', this._selectedNodes.length, 'selected nodes');
  }

  setDefaultRegionsForNextAdd(regions) {
    const next = Array.isArray(regions) ? regions.slice() : [];
    this._defaultRegionsForNextAdd = next;
    console.log('[KonvaController] setDefaultRegionsForNextAdd:', next);
  }

  syncSelectedNodesToRegions(regions) {
    console.log('[KonvaController] syncSelectedNodesToRegions: START', { regions });
    let nodes = this._selectedNodes || [];
    
    if (nodes.length === 0) {
      this._defaultRegionsForNextAdd = Array.isArray(regions) ? regions.slice() : [];
      console.log('[KonvaController] syncSelectedNodesToRegions: No selected nodes, set default regions for next add:', this._defaultRegionsForNextAdd);
      return;
    }
    
    if (nodes.length === 0) {
      console.log('[KonvaController] syncSelectedNodesToRegions: No nodes to sync');
      return;
    }

    console.log('[syncSelectedNodesToRegions] Syncing', nodes.length, 'nodes to regions:', regions);

    nodes.forEach(node => {
      // Update region attribute on control nodes
      if (node.name && typeof node.name === 'function') {
        const nodeName = node.name();
        if (nodeName === 'uploaded-image' || nodeName === 'sticker-control') {
          // Store new region data
          node.setAttr('region', regions);
          console.log('[syncSelectedNodesToRegions] Set regions on node:', {
            nodeId: node.id && node.id(),
            newRegions: regions,
            actualRegions: node.getAttr('region')
          });
          
          // Destroy old visual node if it exists
          if (node.visualNode) {
            const oldVisualNode = node.visualNode;
            const oldRegionGroup = node.visualGroup;
            
            // Remove from old region group
            if (oldRegionGroup && oldVisualNode.getParent() === oldRegionGroup) {
              oldRegionGroup.remove(oldVisualNode);
            }
            
            // Destroy the old visual node
            oldVisualNode.destroy();
          }

          // Robustly clear any existing visual clones across all region groups
          if (Array.isArray(node.visualGroups)) {
            try {
              // Detach previous sync handlers to avoid duplicates
              if (node.syncVisual) {
                node.off('dragmove', node.syncVisual);
                node.off('transform', node.syncVisual);
              }
            } catch (_) {}
            try {
              node.visualGroups.forEach(({ visual, group }) => {
                try {
                  if (visual && typeof visual.destroy === 'function') visual.destroy();
                } catch (_) {}
                try {
                  if (group && typeof group.getChildren === 'function') {
                    // Ensure no orphan visuals remain in the group
                    group.getChildren(c => {
                      const isOrphan =
                        c &&
                        typeof c.name === 'function' &&
                        c.name() === 'sticker-visual' &&
                        typeof c.id === 'function' &&
                        typeof node.id === 'function' &&
                        c.id().includes(node.id());
                      return isOrphan;
                    }).forEach(c => {
                      try { c.destroy(); } catch (_) {}
                    });
                  }
                } catch (_) {}
              });
            } catch (_) {}
            node.visualGroups = [];
          }

          // Recreate visual node in new region(s)
          if (regions && regions.length > 0) {
            // Clear any residual visuals inside all region groups
            try {
              if (this.design_group && typeof this.design_group.getChildren === 'function') {
                this.design_group.getChildren(g => {
                  return g &&
                    typeof g.name === 'function' &&
                    typeof g.getChildren === 'function' &&
                    String(g.name()).startsWith('region-group-');
                }).forEach(g => {
                  try {
                    g.getChildren(c => {
                      const match =
                        c &&
                        typeof c.name === 'function' &&
                        c.name() === 'sticker-visual' &&
                        typeof c.id === 'function' &&
                        typeof node.id === 'function' &&
                        c.id().includes(node.id());
                      return match;
                    }).forEach(c => {
                      try { c.destroy(); } catch (_) {}
                    });
                  } catch (_) {}
                });
              }
            } catch (_) {}

            // Make control node invisible (but still interactive)
            // Keep it visible for interaction but use empty sceneFunc
            node.sceneFunc((ctx, shape) => {
              // Draw nothing - invisible for visual
            });
            
            node.hitFunc((ctx, shape) => {
              ctx.beginPath();
              ctx.rect(0, 0, shape.width(), shape.height());
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            });
            // Keep node visible for interaction but it's visually empty

            // For each selected region, create a visual node
            const createdPairs = [];
            regions.forEach(regionKey => {
              console.log('[syncSelectedNodesToRegions] Processing region:', regionKey);
              
              const regionGroup = this._ensureRegionGroup(regionKey);
              if (!regionGroup) {
                console.log('[syncSelectedNodesToRegions] ERROR: Failed to create region group for', regionKey);
                return;
              }
              
              const visualNode = node.clone({
                listening: false,
                draggable: false,
                name: 'sticker-visual',
                id: node.id && node.id() ? `${node.id()}-visual-${regionKey}` : `visual-${Date.now()}-${regionKey}`,
              });
              
              // Remove any sceneFunc from visual node to show the actual image
              visualNode.sceneFunc(null);
              delete visualNode.sceneFunc;
              visualNode.visible(true);

              if (regionGroup) {
                regionGroup.add(visualNode);
                regionGroup.visible(true);
                createdPairs.push({ visual: visualNode, group: regionGroup });
                console.log('[syncSelectedNodesToRegions] Added visual node to region group:', {
                  region: regionKey,
                  regionGroupName: regionGroup.name && regionGroup.name(),
                  regionGroupVisible: regionGroup.visible && regionGroup.visible(),
                  visualNodeId: visualNode.id && visualNode.id(),
                  visualNodeVisible: visualNode.visible && visualNode.visible()
                });
              } else {
                console.log('[syncSelectedNodesToRegions] ERROR: No region group for', regionKey);
              }
            });
            
            node.visualGroups = createdPairs;
            const syncVisual = () => {
              if (Array.isArray(node.visualGroups)) {
                node.visualGroups.forEach(({ visual }) => {
                  if (!visual.getLayer()) return;
                  const oldPos = { x: visual.x(), y: visual.y() };
                  visual.setAttrs({
                    x: node.x(),
                    y: node.y(),
                    scaleX: node.scaleX(),
                    scaleY: node.scaleY(),
                    rotation: node.rotation(),
                    opacity: node.opacity(),
                    skewX: node.skewX(),
                    skewY: node.skewY()
                  });
                  if (oldPos.x !== visual.x() || oldPos.y !== visual.y()) {
                    console.log('[syncVisual] Position updated:', {
                      nodeId: node.id && node.id(),
                      oldPos,
                      newPos: { x: visual.x(), y: visual.y() },
                      controlPos: { x: node.x(), y: node.y() }
                    });
                  }
                  visual.getLayer().batchDraw();
                });
              }
            };
            node.syncVisual = syncVisual;
            node.on('dragmove transform', syncVisual);
            syncVisual();

            const originalOpacity = node.opacity.bind(node);
            node.opacity = function(val) {
              if (val !== undefined) {
                if (Array.isArray(this.visualGroups)) {
                  this.visualGroups.forEach(({ visual }) => visual.opacity(val));
                }
                return originalOpacity(val);
              }
              return originalOpacity();
            };
          } else {
            // No regions selected - make control node visible (show whole image)
            if (typeof node.sceneFunc === 'function') {
              // Remove empty sceneFunc to make node visible
              node.sceneFunc(null);
              delete node.sceneFunc;
            }
            node.visible(true);
            // Ensure no stale visuals remain
            try {
              if (Array.isArray(node.visualGroups)) {
                node.visualGroups.forEach(({ visual }) => {
                  try { if (visual) visual.destroy(); } catch (_) {}
                });
                node.visualGroups = [];
              }
              if (this.design_group && typeof this.design_group.getChildren === 'function') {
                this.design_group.getChildren(g => {
                  return g &&
                    typeof g.name === 'function' &&
                    String(g.name()).startsWith('region-group-');
                }).forEach(g => {
                  try {
                    g.getChildren(c => {
                      const match =
                        c &&
                        typeof c.name === 'function' &&
                        c.name() === 'sticker-visual' &&
                        typeof c.id === 'function' &&
                        typeof node.id === 'function' &&
                        c.id().includes(node.id());
                      return match;
                    }).forEach(c => {
                      try { c.destroy(); } catch (_) {}
                    });
                  } catch (_) {}
                });
              }
              if (node.syncVisual) {
                node.off('dragmove', node.syncVisual);
                node.off('transform', node.syncVisual);
                delete node.syncVisual;
              }
            } catch (_) {}
          }

          console.log('[syncSelectedNodesToRegions] Updated node region:', {
            nodeId: node.id && node.id(),
            nodeName: nodeName,
            newRegions: regions
          });
        }
      }
    });

    // Debug: Check what's in region groups after sync
    console.log('[syncSelectedNodesToRegions] DEBUG: Checking region groups after sync');
    console.log('[syncSelectedNodesToRegions] DEBUG: All design_group children:');
    this.design_group.getChildren().forEach(child => {
      console.log('[syncSelectedNodesToRegions] Child:', {
        name: child.name && child.name(),
        type: child.className,
        visible: child.visible && child.visible(),
        id: child.id && child.id()
      });
    });
    
    Object.keys(this.layers).forEach(layerName => {
      const layer = this.layers[layerName];
      layer.getChildren(child => {
        if (child.name && child.name().startsWith('region-group-')) {
          const visualNodes = child.getChildren();
          console.log('[syncSelectedNodesToRegions] Region group:', {
            groupName: child.name && child.name(),
            groupVisible: child.visible && child.visible(),
            visualNodeCount: visualNodes.length,
            visualNodes: visualNodes.map(vn => ({
              id: vn.id && vn.id(),
              name: vn.name && vn.name(),
              visible: vn.visible && vn.visible()
            }))
          });
        }
      });
    });

    // Redraw to apply changes
    if (this.stage) {
      this.stage.batchDraw();
    }
  }

  /**
   * Clear user-added design elements while preserving base layers
   * Base layers include: product-image, background-image, background-color, case-image
   * @param {Object} options
   * @param {boolean} [options.keepBase=true] - Whether to preserve base elements
   * @param {string[]} [options.baseNames] - Additional base names to preserve
   */
  resetDesign(options = {}) {
    try {
      if (!this.stage) return;
      const designLayer = this.layers && this.layers['Design'];
      if (!designLayer) return;

      const {
        keepBase = true,
        // Align with JSDoc: preserve product-image, background-image, background-color, case-image, mask-image
        baseNames = ['product-image', 'case-image', 'mask-image']
      } = options;
      const baseSet = new Set(keepBase ? baseNames : []);

      // Remove any active transformers and delete buttons
      designLayer.find('Transformer').forEach(t => t.destroy());
      designLayer.find('.delete-button-group').forEach(g => g.destroy());

      // Ensure we operate on the design group if it exists
      const group = this.design_group || designLayer;

      // Destroy all non-base children
      // IMPORTANT: clone the children array before destroying to avoid skipping nodes
      const children = group.getChildren().slice();
      children.forEach((child) => {
        try {
          const nm = typeof child.name === 'function' ? child.name() : (child.attrs && child.attrs.name);
          if (!keepBase || !baseSet.has(nm)) {
            child.destroy();
          }
        } catch (e) {
          // continue
        }
      });

      // Clear selection reference
      window.selectedKonvaNode = null;

      // Redraw
      designLayer.batchDraw();
      this.stage.batchDraw();
    } catch (err) {
      console.error('resetDesign error:', err);
    }
  }

  async clearDesignLayer(options = {}) {
    const baseNames = Array.isArray(options.baseNames) && options.baseNames.length > 0
      ? options.baseNames
      : ['product-image', 'case-image', 'mask-image', 'hint-layer'];
    this.resetDesign({ keepBase: true, baseNames });
  }
}

export default KonvaController;
